import { DatePipe, Location, CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { HttpEventType } from '@angular/common/http';
import { BsDropdownModule, BsDropdownDirective } from 'ngx-bootstrap/dropdown';
import { UntypedFormControl, UntypedFormGroup, FormsModule } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ActivatedRoute, Router } from '@angular/router';
import { SearchPipe } from '../../pipe/SearchFilter/SearchPipe';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { MessageDetailsComponent } from '../message-details/message-details.component';
import { WidgetModule } from '../../widget/widget.module';
import { NgxPaginationModule } from 'ngx-pagination';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { NgxSummernoteDirective } from 'ngx-summernote';
import { debounceTime, distinctUntilChanged, of, Subject, Subscription, switchMap, takeUntil } from 'rxjs';
import { AppSettings } from 'src/app/services/AppSettings';
import { FormValidation } from 'src/app/services/FormValidation';
import { GigsumoConstants } from 'src/app/services/GigsumoConstants';
import { SocketServiceStream } from 'src/app/services/SocketServiceStream';
import { ApiService } from 'src/app/services/api.service';
import { JobService } from 'src/app/services/job.service';
import { UtilService } from 'src/app/services/util.service';
import { TimezoneDatePipe } from 'src/app/timezone-date.pipe';
import Swal from 'sweetalert2';
import { MessagescrollDirective } from './../../directives/messagescroll.directive';
import { SocketService } from './../../services/socket.service';
import { CommonSkeletonComponent } from '../../skeleton/common-skeleton/common-skeleton.component';
import { NgxSummernoteModule } from 'ngx-summernote';
import { PickerModule } from '@ctrl/ngx-emoji-mart';
import { MessageuniquePipe } from '../../pipe/messageunique.pipe';
import { MessageContactPipe } from '../../pipe/message-contact.pipe';
import { NG_ICON_DIRECTIVES } from '@ng-icons/core';


declare var $: any;
declare var SockJS: any;
declare var Stomp: any;

export enum MESSAGESTATUS {
  EMPTY, MESSAGE
}

/* ---------------------------------------------------------------------------
 * Paste normalization — turn a pasted bullet/numbered list into a CLEAN native
 * list (same shape Ctrl+Shift+7/8 produces), stripping the Word/Docs/webpage
 * markup (inline styles, classes, mso-* junk, stray spans) that otherwise
 * pastes in ugly or as flat text. Kept at module scope (pure functions) so the
 * Summernote onPaste callback can call them regardless of what `this` it runs
 * with. Only <li>/<ul>/<ol> content plus basic inline emphasis survives.
 * ------------------------------------------------------------------------- */

const PASTE_ALLOWED_TAGS = new Set(
  ['UL', 'OL', 'LI', 'P', 'BR', 'B', 'STRONG', 'I', 'EM', 'U', 'A']
);

function escapeHtmlText(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// True when the plain-text lines read as a list (majority start with "1." / "1)" /
// "-" / "*" / "•"), so a text-only paste (no HTML) can still become a real list.
function looksLikeTextList(text: string): boolean {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length);
  if (!lines.length) { return false; }
  const marker = /^(\d+[.)]|[-*•▪◦])\s+\S/;
  const hits = lines.filter((l) => marker.test(l)).length;
  return hits >= 1 && hits >= Math.ceil(lines.length / 2);
}

// Build clean <ol>/<ul>/<p> HTML from plain text: consecutive numbered lines →
// <ol>, consecutive bullet lines → <ul>, anything else → <p>.
function textListToHtml(text: string): string {
  const orderedRe = /^\s*\d+[.)]\s+(.*)$/;
  const bulletRe = /^\s*[-*•▪◦]\s+(.*)$/;
  let out = '';
  let mode: 'ol' | 'ul' | null = null;
  let buf: string[] = [];
  const flush = () => {
    if (mode && buf.length) {
      out += '<' + mode + '>' +
        buf.map((i) => '<li>' + escapeHtmlText(i) + '</li>').join('') +
        '</' + mode + '>';
    }
    buf = [];
  };
  text.split(/\r?\n/).forEach((line) => {
    let m = orderedRe.exec(line);
    if (m) { if (mode !== 'ol') { flush(); mode = 'ol'; } buf.push(m[1]); return; }
    m = bulletRe.exec(line);
    if (m) { if (mode !== 'ul') { flush(); mode = 'ul'; } buf.push(m[1]); return; }
    flush(); mode = null;
    const t = line.trim();
    if (t) { out += '<p>' + escapeHtmlText(t) + '</p>'; }
  });
  flush();
  return out;
}

// Strip a pasted HTML fragment down to the allow-listed tags: drop every
// attribute (keep only <a href>), delete style/script/meta, and unwrap anything
// else (spans, divs, o:p, …) so only clean list/emphasis markup remains.
function sanitizeListHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const body = doc.body;
  body.querySelectorAll('style, script, meta, link, title').forEach((n) => n.remove());

  const walk = (node: Element) => {
    Array.from(node.children).forEach((child) => walk(child as Element));
    Array.from(node.attributes).forEach((attr) => {
      if (!(node.tagName === 'A' && attr.name === 'href')) {
        node.removeAttribute(attr.name);
      }
    });
    if (!PASTE_ALLOWED_TAGS.has(node.tagName)) {
      const parent = node.parentNode;
      if (parent) {
        while (node.firstChild) { parent.insertBefore(node.firstChild, node); }
        parent.removeChild(node);
      }
    }
  };
  Array.from(body.children).forEach((c) => walk(c as Element));

  // Google Search / Gemini copies emit bare <li>s inside <div>/<span> wrappers with
  // NO <ul>/<ol> at all; after the unwrap above they sit orphaned. Adopt each run of
  // consecutive orphaned <li> siblings into a fresh <ul> so they read as a real list
  // (otherwise the caller's <ul|ol> gate rejects the paste and the browser's raw,
  // fully-styled default paste goes through instead).
  Array.from(body.querySelectorAll('li')).forEach((li) => {
    const parent = li.parentElement;
    if (!parent || parent.tagName === 'UL' || parent.tagName === 'OL') { return; }
    const list = doc.createElement('ul');
    parent.insertBefore(list, li);
    let n: any = list.nextSibling;
    while (n) {
      const next = n.nextSibling;
      if (n.nodeType === 1 && n.tagName === 'LI') { list.appendChild(n); }
      else if (n.nodeType === 3 && n.textContent.trim() === '') { n.remove(); }
      else { break; }
      n = next;
    }
  });

  /* Canonicalize to the exact shape TYPED lists produce (<ul><li>inline text</li></ul>).
   * The compose box renders children with white-space:pre-wrap, so the source's
   * formatting newlines/indentation would otherwise show as real blank space, and
   * <p> wrappers / empty trailing <li>s (ChatGPT/Docs markup) add stray height. */

  // <p> inside <li> → unwrap; multiple paragraphs join with a single <br>.
  body.querySelectorAll('li p').forEach((p) => {
    const li = p.parentNode as Element;
    if (p.previousElementSibling) { li.insertBefore(doc.createElement('br'), p); }
    while (p.firstChild) { li.insertBefore(p.firstChild, p); }
    p.remove();
  });

  // Collapse whitespace runs in every text node (pre-wrap shows raw newlines);
  // whitespace-only nodes between structural tags are dropped entirely.
  const walker = doc.createTreeWalker(body, NodeFilter.SHOW_TEXT);
  const blanks: Node[] = [];
  let tn: any;
  while ((tn = walker.nextNode())) {
    tn.textContent = tn.textContent.replace(/\s+/g, ' ');
    const parent = tn.parentElement;
    if (tn.textContent.trim() === '' && (!parent || /^(UL|OL|BODY)$/.test(parent.tagName))) {
      blanks.push(tn);
    }
  }
  blanks.forEach((n) => n.parentNode && n.parentNode.removeChild(n));

  // Trim item edges; drop items with no visible content, then lists left empty.
  body.querySelectorAll('li').forEach((li) => {
    const first = li.firstChild;
    if (first && first.nodeType === 3) { first.textContent = (first.textContent || '').replace(/^\s+/, ''); }
    const last = li.lastChild;
    if (last && last.nodeType === 3) { last.textContent = (last.textContent || '').replace(/\s+$/, ''); }
    if ((li.textContent || '').trim() === '') { li.remove(); }
  });
  body.querySelectorAll('ul, ol').forEach((l) => { if (!l.querySelector('li')) { l.remove(); } });

  return body.innerHTML.trim();
}

// Read a clipboard payload and return clean list HTML, or null when it isn't a
// list (so the caller lets the browser paste normally).
function clipboardToCleanListHtml(cb: any): string | null {
  if (!cb || typeof cb.getData !== 'function') { return null; }
  const html = cb.getData('text/html') || '';
  if (html && /<(ul|ol|li)\b/i.test(html)) {
    const clean = sanitizeListHtml(html);
    if (/<(ul|ol)\b/i.test(clean)) { return clean; }
  }
  const text = cb.getData('text/plain') || '';
  if (text && looksLikeTextList(text)) { return textListToHtml(text); }
  return null;
}

/* Trim only the LEADING and TRAILING empty space of a message — surrounding
 * spaces, &nbsp;, blank lines, empty <p>/<br> blocks — while leaving the actual
 * content and its formatting (lists, bold, links) untouched in the middle. A
 * whitespace-only message collapses to '' so it still counts as blank. */
function trimMessageHtml(html: string): string {
  if (typeof html !== 'string' || html === '') { return ''; }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const body = doc.body;

  const isBlankNode = (node: any): boolean => {
    if (!node) { return false; }
    if (node.nodeType === 3) { // text node
      return node.textContent.trim() === '';
    }
    if (node.nodeType === 1) { // element — an image is real content, never blank
      if (node.querySelector && node.querySelector('img')) { return false; }
      return (node.textContent || '').trim() === '';
    }
    return true; // comments / anything else → droppable
  };

  // Drop empty blocks/lines at the very start and end.
  while (body.firstChild && isBlankNode(body.firstChild)) { body.removeChild(body.firstChild); }
  while (body.lastChild && isBlankNode(body.lastChild)) { body.removeChild(body.lastChild); }

  // Then strip whitespace/&nbsp; hugging the first and last bits of real text.
  const texts: Text[] = [];
  const collect = (n: Node) => {
    n.childNodes.forEach((c) => {
      if (c.nodeType === 3) { texts.push(c as Text); } else { collect(c); }
    });
  };
  collect(body);
  if (texts.length) {
    texts[0].textContent = (texts[0].textContent || '').replace(/^[\s]+/, '');
    const last = texts[texts.length - 1];
    last.textContent = (last.textContent || '').replace(/[\s]+$/, '');
  }

  return body.innerHTML.trim();
}

@Component({
  selector: 'app-new-message-page',
  standalone: true,
  imports: [BsDropdownModule, CommonModule, FormsModule,
MatAutocompleteModule, TimezoneDatePipe, SearchPipe, InfiniteScrollModule, MessageDetailsComponent, WidgetModule, NgxPaginationModule, CommonSkeletonComponent, NgxSummernoteModule, PickerModule, MessageuniquePipe, MessageContactPipe,
    NG_ICON_DIRECTIVES,
  ],
  templateUrl: './new-message-page.component.html',
  styleUrls: ['./new-message-page.component.scss'],
})
export class NewMessagePageComponent extends FormValidation implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('videoPlayer', { static: false }) videoplayer: ElementRef;
  @ViewChild('chartDiscussion', { static: false }) chartDisc: ElementRef;
  @ViewChild('UploadFileInput', { static: false }) UploadFileInput: ElementRef;
  @ViewChild(MessagescrollDirective, { static: false }) scrollDir: MessagescrollDirective;
  @ViewChild(NgxSummernoteDirective, { static: false }) summernote: NgxSummernoteDirective;
  @ViewChild(MatMenuTrigger, { static: true }) matMenuTrigger: MatMenuTrigger;
  @ViewChild('scrollableDiv', { static: false }) scrollableDiv: ElementRef;
  // All per-message option dropdowns — closed on scroll (they render at body level
  // via container="body", so they'd otherwise float detached when the list scrolls).
  @ViewChildren(BsDropdownDirective) messageDropdowns: QueryList<BsDropdownDirective>;
  isSearchFocused: boolean = false;
  @ViewChild(MessageDetailsComponent) messageDetailComponent!: MessageDetailsComponent;
  menuTopLeftPosition = { x: '0', y: '0' }
  configlist = {
    itemsPerPage: 5,
    currentPage: 1
  }
  // Single onKeydown for both compose configs: auto-format first (Space), then the
  // Backspace list fix if the event wasn't consumed. Arrow property so `this` stays
  // the component even though Summernote invokes callbacks with its own `this`.
  // Declared ABOVE `config` — class fields initialize in order, and config's
  // initializer reads it.
  onComposeKeydown = (e: any) => {
    this.onComposeListAutoformat(e);
    if (!e.defaultPrevented) { this.onComposeListBackspace(e); }
  }

  /* ---- Paste/drop file handling: photo/video/file is STAGED in the composer's
   * preview strip (same uploadFiles flow as the paperclip) and only goes out when
   * the user hits Send — sendMessageWithAttachment() shows an optimistic bubble
   * with a live upload percentage. ---- */

  // Guard so the same paste/drop can't double-send when two hooks see one gesture.
  private recentComposerFiles: { [k: string]: number } = {};

  // Summernote hands pasted/dropped IMAGE files here (instead of inserting them
  // as raw base64, which made sends multi-MB slow). Declared above `config`.
  onComposeImageUpload = (files: any) => {
    this.handleComposerFiles(files);
  }

  // Paste: non-image files (video/docs copied from the file explorer) never reach
  // Summernote's onImageUpload — send them ourselves. Image-only pastes fall
  // through (Summernote routes them to onComposeImageUpload); other pastes go to
  // the list normalizer as before.
  onComposePaste = (e: any) => {
    const ev = e && e.originalEvent ? e.originalEvent : e;
    const cb = ev && (ev.clipboardData || (window as any).clipboardData);
    const files: any[] = cb && cb.files ? Array.from(cb.files) : [];
    if (files.length && files.some((f: any) => ((f && f.type) || '').indexOf('image') !== 0)) {
      e.preventDefault();
      this.handleComposerFiles(files);
      return;
    }
    this.onComposeListPaste(e);
  }

  // Drop (capture phase): ANY file dropped on the composer is ours — Summernote
  // must never see it (ngx-summernote overwrites onImageUpload with its own
  // base64-inline insert, so the callback route can't be trusted).
  private composerDropCapture = (e: DragEvent) => {
    const t: any = e.target;
    const inComposer = t && t.closest && t.closest('.chat-input-container-107');
    const files: any[] = e.dataTransfer && e.dataTransfer.files ? Array.from(e.dataTransfer.files) : [];
    if (!inComposer || !files.length) { return; }
    e.preventDefault();
    e.stopPropagation(); // we own this drop — also blocks the window:drop overlay reset, so:
    this.ngZone.run(() => {
      this.resetSummernoteDropzone();
      this.handleComposerFiles(files);
    });
  }

  // Paste (capture phase): same story — a clipboard holding FILES (screenshot,
  // copied photo/video/document) is sent as a message; Summernote never sees the
  // event, so it can't base64-inline the image. Plain text/html pastes pass
  // through untouched (list normalizer etc. keep working).
  private composerPasteCapture = (e: ClipboardEvent) => {
    const t: any = e.target;
    const inComposer = t && t.closest && t.closest('.chat-input-container-107');
    const cb: any = e.clipboardData;
    const files: any[] = cb && cb.files ? Array.from(cb.files) : [];
    if (!inComposer || !files.length) { return; }
    e.preventDefault();
    e.stopPropagation();
    this.ngZone.run(() => this.handleComposerFiles(files));
  }

  // Shared entry for every pasted/dropped file: 10 MB limit (same rule + popup
  // as the paperclip flow), then STAGE each file next to the composer — exactly
  // like the paperclip flow. Nothing uploads until the user hits Send.
  private handleComposerFiles(fileList: any): void {
    const files: any[] = Array.from(fileList || []);
    // Running TOTAL including what's already staged: the whole batch goes out as
    // ONE multipart request, and AWS API Gateway hard-rejects bodies over 10 MB
    // (413 before the backend ever sees it) — so the sum must stay under the cap.
    let total = this.stagedTotalSize();
    let rejectedType = false;
    files.forEach((file: any) => {
      if (!file || typeof file.size !== 'number') { return; }
      const key = file.name + '|' + file.size + '|' + file.lastModified;
      const now = Date.now();
      if (this.recentComposerFiles[key] && now - this.recentComposerFiles[key] < 1500) { return; }
      this.recentComposerFiles[key] = now;
      // Drop/paste used to take ANY file (pdf, doc, txt …) while the paperclip
      // offered media only, and the chat bubble has no branch for those types —
      // they rendered as an empty bubble. Same media-only rule enforced here so
      // no gesture bypasses it. Flagged, not popped, so one mixed drop shows one
      // dialog instead of stacking a Swal per rejected file.
      if (!this.isAllowedAttachment(file)) {
        rejectedType = true;
        return;
      }
      if (file.size > this.fileSize) {
        Swal.fire('', 'Please upload file lesser than 10 MB.', 'info');
        return;
      }
      if (total + file.size > this.fileSize) {
        Swal.fire('', 'Total attachments size should be lesser than 10 MB.', 'info');
        return;
      }
      total += file.size;
      this.stageComposerFile(file);
    });
    if (rejectedType) {
      Swal.fire('', 'Only image, audio and video files can be attached.', 'info');
    }
  }

  // Media-only whitelist shared by every attachment path. Anything else —
  // documents, archives, or a file the browser could not type at all — is
  // rejected before it ever reaches the staging strip.
  private isAllowedAttachment(file: any): boolean {
    const type = ((file && file.type) || '').toLowerCase();
    return type.indexOf('image/') === 0
      || type.indexOf('audio/') === 0
      || type.indexOf('video/') === 0;
  }

  // Combined size of everything already staged (edit-flow rows without a File
  // object count as 0 — they're already on the server, not re-uploaded).
  private stagedTotalSize(): number {
    return (this.uploadFiles || []).reduce(
      (sum: number, f: any) => sum + ((f && f.file && f.file.size) || 0), 0);
  }

  // Mirror of fileUpload()'s per-file staging (same {t,name,ft,src,file} shape,
  // same dedupe + attachment/edit flags) so a pasted/dropped file lands in the
  // SAME uploadFiles preview strip the paperclip uses, and goes out through
  // sendMessageWithAttachment() on Send.
  private stageComposerFile(file: any): void {
    if (this.uploadFiles.length == 0) {
      this.editValueEditImage['attachment'] = false;
    }
    const type = file.type || '';
    let type1 = 'others';
    if (type.includes('image')) {
      type1 = 'image';
    } else if (type.includes('video')) {
      type1 = 'video';
    } else if (type.includes('application')) {
      type1 = 'application';
    } else if (type.includes('audio')) {
      type1 = 'audio';
    }
    // Clipboard screenshots are ALL named "image.png"; duplicateimag() dedupes by
    // name, which would silently drop every paste after the first. Uniquify the
    // display name (the File itself is sent untouched).
    let name = file.name;
    const dot = name.lastIndexOf('.');
    const base = dot > 0 ? name.slice(0, dot) : name;
    const ext = dot > 0 ? name.slice(dot) : '';
    let n = 2;
    while (this.uploadFiles.some((f: any) => f.name === name)) {
      name = base + ' (' + (n++) + ')' + ext;
    }
    const reader = new FileReader();
    reader.onload = (event1: any) => {
      this.uploadFiles.push({
        t: type1,
        name: name,
        ft: type,
        src: event1.target.result,
        file: file
      });
      if (this.editMessage) {
        this.imageAttachmentEdit = true;
        this.editMessage = false;
        this.editValueEditImage['attachment'] = true;
      }
      this.uploadFiles = this.duplicateimag(this.uploadFiles);
      if (!this.attachment && !this.imageAttachmentEdit) {
        this.attachment = true;
      }
    };
    reader.readAsDataURL(file);
  }

  private removePendingMessage(ph: any): void {
    const i = this.messageList.indexOf(ph);
    if (i >= 0) { this.messageList.splice(i, 1); }
  }

  config = {
    focus: true,
    placeholder: 'Type a message...',
    toolbar: [],
    width: '100%',
    minHeight: 40,
    maxHeight: 120,
    callbacks: {
      onInit: function () {
        const $editor = $(this).next('.note-editor');
        $editor.find('.note-editable').css('overflow-y', 'auto');
      },
      // "- "/"1. " auto-lists + stranded-marker Backspace cleanup — see onComposeKeydown.
      onKeydown: this.onComposeKeydown,
      // Files → staged in the attachment strip; lists → normalizer. See onComposePaste.
      onPaste: this.onComposePaste,
      // Pasted/dropped images: staged as a real attachment (no inline base64) — see onComposeImageUpload.
      onImageUpload: this.onComposeImageUpload
    }
  };

  // Summernote can't cleanly remove a list marker on Backspace — the first bullet/number
  // in particular gets stuck. This intercepts Backspace when the caret is at the START of
  // a list item and lifts that one line out of the list into a plain paragraph, keeping
  // whatever text it holds (an empty item just becomes an empty line). The list is split
  // in place, so preceding/following items stay a correctly-numbered list. Mid-line
  // backspacing (caret not at the item start) is left to the browser, so deleting
  // characters works normally. Handles both <ol> and <ul>. Referenced from every compose
  // config (the field init above and the per-conversation reset in ngOnInit's socket
  // handler) so it stays a single source of truth; it reads no component state, so
  // Summernote invoking it with its own `this` is harmless.
  onComposeListBackspace(e: any) {
    if (e.key !== 'Backspace' && e.keyCode !== 8) { return; }

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) { return; }
    const range = sel.getRangeAt(0);
    if (!range.collapsed) { return; } // let a range-selection delete normally

    // Walk up to the enclosing <li>.
    let li: any = range.startContainer;
    li = li.nodeType === 3 ? li.parentElement : li;
    while (li && li.tagName !== 'LI') { li = li.parentElement; }
    if (!li) { return; }

    const list = li.parentElement;
    if (!list || (list.tagName !== 'OL' && list.tagName !== 'UL')) { return; }

    // Only act when the caret sits at the very start of the item — otherwise let the
    // browser delete the character before it. "Start" = nothing but whitespace/zero-width
    // between the item's beginning and the caret.
    const zwsp = String.fromCharCode(0x200B);
    const pre = range.cloneRange();
    pre.selectNodeContents(li);
    pre.setEnd(range.startContainer, range.startOffset);
    if (pre.toString().split(zwsp).join('').trim() !== '') { return; }

    e.preventDefault();

    // The lifted line: carry the item's own content across (empty item → blank line).
    const p = document.createElement('p');
    while (li.firstChild) { p.appendChild(li.firstChild); }
    if (!p.hasChildNodes()) { p.appendChild(document.createElement('br')); }

    // Items after this one resume as a fresh list below the paragraph; items before it
    // stay in the original list above. Inserting the paragraph right after the list keeps
    // everything in visual order (when the item was first, the now-empty list is removed
    // and the paragraph rises to the top).
    const following: Element[] = [];
    let sib = li.nextElementSibling;
    while (sib) { following.push(sib); sib = sib.nextElementSibling; }

    list.parentNode.insertBefore(p, list.nextSibling);
    if (following.length) {
      const newList = document.createElement(list.tagName);
      following.forEach((f) => newList.appendChild(f));
      p.parentNode.insertBefore(newList, p.nextSibling);
    }
    li.remove();
    if (list.children.length === 0) { list.remove(); }

    // Caret to the start of the lifted line, so it reads as "the bullet just vanished".
    const r = document.createRange();
    r.setStart(p, 0);
    r.collapse(true);
    sel.removeAllRanges();
    sel.addRange(r);

    // We mutated the DOM directly and prevented the default Backspace, so nudge
    // Summernote to re-sync its model (ngModel, empty-validator, send-button state).
    const editable = p.closest('.note-editable');
    if (editable) { editable.dispatchEvent(new Event('input', { bubbles: true })); }
  }

  // Auto-format on Space (like Docs/WhatsApp): a line holding only "-", "*" or "•"
  // becomes a bullet list, "1." / "1)" becomes a numbered list — the marker text is
  // removed and replaced with the same native <ul>/<ol> the paste normalizer makes.
  // Any other Space falls through untouched; already inside a list → untouched.
  // Reads no component state (safe under Summernote's own `this`).
  onComposeListAutoformat(e: any) {
    if (e.key !== ' ' && e.keyCode !== 32) { return; }

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) { return; }
    const range = sel.getRangeAt(0);
    if (!range.collapsed) { return; }

    let el: any = range.startContainer;
    el = el.nodeType === 3 ? el.parentElement : el;
    if (!el || !el.closest) { return; }
    const editable = el.closest('.note-editable');
    if (!editable || el.closest('li')) { return; }

    // The line's block: the ancestor sitting directly under .note-editable
    // (Summernote wraps each line in a <p>/<div>; a bare text line falls back to el).
    let block: any = el;
    while (block !== editable && block.parentElement && block.parentElement !== editable) {
      block = block.parentElement;
    }
    if (block === editable) { block = el; }

    // Text between the start of the line and the caret must be exactly a marker.
    const pre = range.cloneRange();
    pre.selectNodeContents(block);
    pre.setEnd(range.startContainer, range.startOffset);
    const t = pre.toString().replace(/\u200B/g, '').trim();
    const isBullet = t === '-' || t === '*' || t === '•';
    const isNumbered = /^1[.)]$/.test(t);
    if (!isBullet && !isNumbered) { return; }

    e.preventDefault();
    pre.deleteContents(); // remove the typed marker

    // Build the list manually rather than via execCommand insert*List: with the
    // block just emptied, Chrome resolves the caret ambiguously and can swallow
    // the PREVIOUS line into the new list. Replacing only this line's block with
    // a fresh <ul>/<ol> guarantees the list starts fresh.
    if (block !== editable && block.parentNode) {
      const listEl = document.createElement(isNumbered ? 'ol' : 'ul');
      const item = document.createElement('li');
      while (block.firstChild) { item.appendChild(block.firstChild); } // keep any text after the caret
      if (!item.hasChildNodes()) { item.appendChild(document.createElement('br')); }
      listEl.appendChild(item);
      block.parentNode.replaceChild(listEl, block);

      const r = document.createRange();
      r.setStart(item, 0);
      r.collapse(true);
      sel.removeAllRanges();
      sel.addRange(r);
    } else {
      // Bare text sitting directly under .note-editable (no line block) — rare;
      // let the browser do the conversion.
      document.execCommand(isNumbered ? 'insertOrderedList' : 'insertUnorderedList');
    }

    // Manual DOM change → nudge Summernote to re-sync its model.
    editable.dispatchEvent(new Event('input', { bubbles: true }));
  }

  // Paste handler: when the clipboard holds a bullet/numbered list (HTML or plain
  // text), insert it as a CLEAN native list instead of Word/Docs markup or flat text.
  // Non-list pastes fall through to the browser's normal paste, untouched. Referenced
  // from every compose config; uses only the module-scope helpers (no component state),
  // so Summernote invoking it with its own `this` is fine.
  onComposeListPaste(e: any) {
    const ev = e && e.originalEvent ? e.originalEvent : e;
    const cb = ev && (ev.clipboardData || (window as any).clipboardData);
    const clean = clipboardToCleanListHtml(cb);
    if (!clean) { return; } // not a list → let the browser paste as usual

    e.preventDefault();
    document.execCommand('insertHTML', false, clean);

    // Re-sync Summernote's model after the manual insert.
    const sel = window.getSelection();
    let node: any = sel && sel.anchorNode;
    node = node && (node.nodeType === 3 ? node.parentElement : node);
    const editable = node && node.closest ? node.closest('.note-editable') : null;
    if (editable) { editable.dispatchEvent(new Event('input', { bubbles: true })); }
  }

  // Single interpretation of the presence field for EVERY status binding. The
  // backend sends `online` as the string 'ONLINE'/'OFFLINE' (a truthy check reads
  // 'OFFLINE' as online, and === 'ONLINE' breaks if it's ever a boolean) — accept
  // both shapes here so all dots/labels agree.
  isOnline(v: any): boolean {
    return v === 'ONLINE' || v === true;
  }

  // SelectedContact comes from the contact/exist response, which carries no
  // `online` field (why the header was stuck on Offline). The presence data
  // lives on the contact-list rows — after selecting a chat, copy the matching
  // row's status across. No matching row / no field → leave as-is.
  // The open conversation must never carry an unread badge — its messages are marked
  // seen as soon as it loads. But the contact list, the mark-seen PUT and the
  // contact/exist call are three independent requests with no ordering between them,
  // so the list often lands holding a count the server has not cleared yet, and
  // nothing ever refetches it. That left a permanent badge on the chat being read,
  // which is why it looked random and why reading never cleared it. Zero the row
  // locally at BOTH points where the answer becomes known: when mark-seen succeeds,
  // and when a contact list arrives for an already-open chat.
  private clearBadgeForOpenChat(from?: string): void {
    const openId = this.SelectedContact && this.SelectedContact.groupId;
    if (!openId) {
      return;
    }
    (this.contactList || []).forEach((c: any) => {
      if (c && c.groupId === openId) {
        c['nonSeenCount'] = 0;
      }
    });
  }

  private syncSelectedOnlineFromList(): void {
    const row = (this.contactList || []).find((c) => c && this.SelectedContact && c.groupId === this.SelectedContact.groupId);
    if (row && row['online'] !== undefined) {
      this.SelectedContact.online = row['online'];
    }
  }

  modalRef: BsModalRef;
  modalRefAddgroup: BsModalRef;
  private lastAddcontactAt = 0; // debounce guard so a double-click opens only one panel
  private lastUserDataAt = 0; // debounce guard for the Edit button (rapid clicks)
  isMessagesLoading = false; // true while fetching a chat's messages (shows skeleton, not the "No messages yet" empty state)
  selectMessage = MESSAGESTATUS;
  messageStatus = MESSAGESTATUS.EMPTY;
  userId: any;
  msgPagnination = {
    page: 0,
    limit: 30,
    order: 'desc'
  };
  messageList: Array<any> = [];
  searchAfterKeys = null;
  DataLength: any = 0;
  sendMsgres = {
    recipientId: "",
    date: ""
  };
  emptyMsgOpt = {
    opt: 'empty'
  };
  contactList: Array<any> = [];
  tempContactList: Array<any> = [];
  newContactList: Array<any> = [];
  tempNewContactList: Array<any> = [];
  paginationFlag: Boolean = true;
  searchBusgroupList;
  searchConatct: any;
  stopScroll: boolean = false;
  groupData: any = {
    groupName: null,
    contacts: [],
    name: [],
    isAutoGeneratedGroupName: Boolean
  };
  // Group-chat creation wizard step (1 = pick members, 2 = name, 3 = confirm)
  groupStep: number = 1;
  // Drives the slide-out animation on the Private/Group slide-in panels before hide.
  panelClosing: boolean = false;
  isLoading: boolean = true; // Initially true to indicate loading state
  nodatafound: boolean = false
  groupDataBusiness: any = {
    groupName: null,
    contacts: [],
    status: [],
  };

  groupDataCommunity: any = {
    groupName: null,
    contacts: [],
    status: [],
  };

  imgUrl: any = AppSettings.photoUrl;
  SelectedContact: any = {};
  searchData: any = {};
  searchMessage: Array<any> = [];
  sendMessageData: any = {};
  // Emoji picker (compose bar) — powered by @ctrl/ngx-emoji-mart (native set = offline-safe)
  showEmojiPicker: boolean = false;
  // Chat-header "Search in conversation": icon that expands into the input.
  showConvoSearch: boolean = false;
  uploadFiles: Array<any> = [];
  EdituploadFiles: Array<any> = [];

  attachment = false;
  imageAttachmentEdit = false;
  FormData: any;
  fancyList: Array<any> = [];
  slideIndex = 1;
  fileSize = AppSettings.FILE_SIZE;
  intervalExistSeen: any = true;
  intervalNonSeenMessage: any = false;
  private destroy$ = new Subject<void>();
  private intervalIds: number[] = [];
  contactfil: any = '';
  remainingContact: Array<any> = [];
  public stompClient;
  public innerHeight;
  public chatHeight;
  public innerHeight1;
  public chatHeight1;
  page: number = 1;
  newpage: number = 1;
  pagecountAddModel: number = 1;
  groupNameshow;
  addusers = [];
  newuserGroup = [];
  tempNewuserGroup = [];
  groupAdduserSearch: any;
  groupUserSearch: any;
  groupUserList = []
  groupBusinessListMembers: any = [];
  groupCommunityListMembers: any = [];
  TempGroupUserList: any = [];
  tempBusinessList: any = [];
  tempCommunityList: any = [];
  data: any;
  pagination = 10;
  currentIndex = 0;
  redirect = true;
  modalOptions: ModalOptions;
  editorDisabled: boolean = false
  editMessage: boolean = false
  editValue: any;
  editValueEditImage: any = [];
  removeElement: any = [];
  userRedirect: any = {};
  searchTerm: string;
  tpoicData: any;
  searchTxt: any;
  countAPI: any;
  showbottom: boolean = true;
  p: number = 1;
  loginUserData: any;
  streamsubscription: any;
  socketTopicmessage !: Subscription;
  socketTopicmessage1!: Subscription;
  socketTopicmessage2!: Subscription;
  socketTopicmessage3!: Subscription;
  socketTopicmessage4!: Subscription;
  socketTopicmessage6!: Subscription;
  socketTopicmessage7!: Subscription;

  socketTopicmessage11: Subscription;
  socketTopicmessage13: Subscription;
  socketTopicmessage14: Subscription;

  socketTopicmessageMSG1: Subscription;
  socketTopicmessageMSG2: Subscription;
  socketTopicmessageMSG3: Subscription;
  socketTopicmessageMSG4: Subscription;
  socketTopicmessageMSG5: Subscription;
  socketTopicmessageMSG6: Subscription;
  socketTopicmessageMSG7: Subscription;
  socketTopicmessageMSG8: Subscription;
  socketTopicmessageMSG9: Subscription;
  socketTopicmessageMSG10: Subscription;
  socketTopicmessageMSG11: Subscription;
  socketTopicmessageMSG12: Subscription;
  socketTopicmessageMSG13: Subscription;
  socketTopicmessageMSG14: Subscription;
  socketTopicmessageMSG15: Subscription;
  onlinestatus: Subscription;
  servernonseencount: Subscription;
  selectedMenu: string = 'All';
  prevAfterkey = null;
  // Generation token: bumped on every fresh contact-list load so a late-arriving
  // scroll-pagination response (from before a menu re-click) is discarded instead
  // of corrupting pagination state.
  private loadToken = 0;
  menuItems = [
    { label: 'All', value: 'All' },
    { label: 'My Connections', value: 'USER' },
    { label: 'Group', value: 'GROUP' },
    { label: 'Job', value: 'JOB' },
    { label: 'Candidate', value: 'CANDIDATE' },
    // { label: 'Network', value: 'NETWORK' },
    { label: 'Workspace', value: 'TEAM' },
    { label: 'Business', value: 'BUSINESS' },
    { label: 'GigSumo Support', value: 'SUPPORT' }
  ];
  searchText = '';
  searchTextname = '';

  // "Search by name..." box under the tabs (bound above) — additive
  // message/getChatsByTab search. Does not touch findAllMessageContacts /
  // eventSelection / listAlltheMenuslist's existing behaviour when no search
  // text is present; it only adds a new branch alongside them.
  // menuItems' `value`s don't match the backend's tab enum 1:1 (MessageController
  // switch statement) — translate here rather than change menuItems/selectedMenu.
  private readonly chatsByTabMap: { [key: string]: string } = {
    All: 'ALL',
    USER: 'CONNECTIONS',
    GROUP: 'GROUP',
    JOB: 'JOB',
    CANDIDATE: 'CANDIDATE',
    TEAM: 'WORKSPACE',
    BUSINESS: 'BUSINESS',
    SUPPORT: 'SUPPORT',
    NETWORK: 'NETWORK',
    COMMUNITY: 'COMMUNITY'
  };
  private chatsByTabSearchSubject = new Subject<string>();
  private chatsByTabSearchSubscription: Subscription;
  private chatsByTabToken = 0;
  // Cursor pagination — mirrors this.prevAfterkey (used by listAlltheMenuslist)
  // but kept separate so the two lists can't cross-contaminate pagination state.
  private chatsByTabAfterKey: any = null;
  chatsByTabHasMore = true;
  // True while a "Search by name..." request (or the reload that follows
  // clearing it) is in flight — gates a loader and hides the empty-state
  // message so "No contacts to display" can't flash before results land.
  chatsByTabSearching = false;

  // "Search in conversation" header box (message/searchInConversation) —
  // additive, separate from both chatsByTab above and the legacy .third-panel
  // client-side message search (searchData/messageList | search pipe further
  // down this file) — neither of those is touched by this.
  convoSearchKeyword = '';
  convoSearchResults: any[] = [];
  convoSearchLoading = false;
  convoSearchLoadingMore = false;
  convoSearchHasMore = true;
  private convoSearchPage = 0;
  private convoSearchToken = 0;
  private convoSearchSubject = new Subject<string>();
  private convoSearchSubscription: Subscription;

  socketTopicmessage10: any;
  Jobandcandidatedetails = [];
  constructor(private _api: ApiService, private _util: UtilService, private _location: Location,
    private aroute: ActivatedRoute, private route: Router, private modalService: BsModalService,
    private _socket: SocketService, public datepipe: DatePipe, public elementRef: ElementRef,
    private JobServicecolor: JobService, private cdr: ChangeDetectorRef,
    private streamService: SocketServiceStream, private ngZone: NgZone) {
    super();
    this.userId = localStorage.getItem('userId');
    this.initializeWebSocketConnection();
    // this.getAllContacts();
    this.queryParamBasedMethod();
    this.selectedMenu = 'All';
    this.listAlltheMenuslist('click');
    this.initialLoad = true;
    $.extend($.summernote.plugins, {
      'brenter': function (context) {
        this.events = {
          'summernote.enter': function (we, e) {
            if ((e.which === 13 || e.keyCode === 13) && e.altKey) {
            } else if (e.which === 13 || e.keycode === 13) {
              $('#sendmsg').click();
              e.preventDefault();
            }
          }
        };
      }
    });
    this.modalOptions = {
      // msg-slide-panel makes every messaging modal (create/add/edit lists) slide
      // in from the right; animated:false lets our CSS keyframe drive the motion.
      class: 'group-msg-modals modal-md msg-slide-panel',
      backdrop: 'static',
      keyboard: false,
      animated: false
    };


  }

  applySearch() {
    this.chatsByTabSearchSubject.next(this.searchTextname);
  }

  // Clears the "Search by name..." box and restores the plain tab list.
  clearChatsByTabSearch() {
    this.searchTextname = '';
    this.applySearch();
  }

  // Debounced target for the subject above. Empty search text falls back to
  // the existing (untouched) tab list instead of calling the new endpoint.
  private runChatsByTabSearch() {
    this.chatsByTabAfterKey = null;
    this.chatsByTabHasMore = true;
    if (!this.searchTextname || this.searchTextname.trim() === '') {
      // Invalidate any in-flight getChatsByTab request (e.g. the "adam" call
      // still in flight when the box gets cleared right after) so its late
      // response can't land after listAlltheMenuslist() below and silently
      // overwrite the just-restored full list back to the stale search result.
      ++this.chatsByTabToken;
      // Restore contactfil the same way eventSelection would, so the
      // existing groupType pipe (html:157) keeps behaving exactly as before
      // this feature existed once search is cleared.
      this.contactfil = this.selectedMenu !== 'All' ? this.selectedMenu : null;
      // listAlltheMenuslist() APPENDS to contactList (correct for its normal
      // callers — initial load / infinite scroll continuation). Clear it
      // first here so the fresh full-list response replaces the leftover
      // search results instead of concatenating onto them.
      this.contactList = [];
      // Same reset eventSelection() does before a fresh (non-scroll) load —
      // without it, a stale cursor from scrolling before the search started
      // would resume mid-list instead of restoring page 1.
      this.prevAfterkey = null;
      this.stopScroll = false;
      this.params.searchAfterKey = null;
      // Gates the loader / hides the empty-state message (html:contacts-list-650)
      // until the restored list lands — listAlltheMenuslist() clears it below.
      this.chatsByTabSearching = true;
      this.listAlltheMenuslist();
      return;
    }
    this.fetchChatsByTab(false);
  }

  private fetchChatsByTab(loadMore: boolean) {
    const tab = this.chatsByTabMap[this.selectedMenu] || 'ALL';
    // getChatsByTab already scopes results to `tab` server-side, and its
    // response `groupType` may use the backend enum (e.g. WORKSPACE) rather
    // than the frontend value contactfil holds (e.g. TEAM) — re-filtering
    // client-side against a mismatched value would wrongly empty the list,
    // so disable that pipe (html:157) for search results.
    this.contactfil = null;
    // Mirror the invalidation above in the other direction: a
    // listAlltheMenuslist() response still in flight when a search starts
    // shouldn't be able to land after this and overwrite the search result.
    ++this.loadToken;
    // Cursor pagination — same shape/behaviour as this.params.searchAfterKey /
    // this.prevAfterkey in listAlltheMenuslist(), just kept separate so the
    // two lists' pagination state can't cross-contaminate.
    const payload = {
      userId: localStorage.getItem('userId'),
      tab,
      searchText: this.searchTextname,
      limit: 10,
      searchAfterKey: loadMore ? this.chatsByTabAfterKey : null
    };

    const token = ++this.chatsByTabToken;
    if (loadMore) {
      // Same bottom skeleton already used by the plain tab list's scroll
      // pagination (html:374, *ngIf="loadingMore650") — reusing it keeps
      // scroll-loading visually identical whether or not a search is active.
      this.loadingMore650 = true;
    } else {
      this._util.startLoader('msgspinner');
      // Gates the loader / hides the empty-state message (html:contacts-list-650)
      // while this request is in flight.
      this.chatsByTabSearching = true;
    }

    this._api.create('message/getChatsByTab', payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        if (token !== this.chatsByTabToken) { return; }
        this.loadingMore650 = false;
        this.chatsByTabSearching = false;
        this._util.stopLoader('msgspinner');

        if (res.code !== GigsumoConstants.SUCESSCODE) { return; }

        // The result list has come back under a different key per tab — `chats`
        // AND `contacts` (ALL, identical content), `contacts` only (JOB),
        // `groups` only (Workspace/Team) — with no consistent field name across
        // tabs. Rather than hardcode each one, take whichever array field in
        // `data` actually has items, skipping the known metadata fields.
        const chatsByTabMetaKeys = new Set(['total', 'searchAfterKey']);
        let chats: any[] = [];
        if (res.data) {
          for (const key of Object.keys(res.data)) {
            if (chatsByTabMetaKeys.has(key)) { continue; }
            const value = res.data[key];
            if (Array.isArray(value) && value.length > 0) {
              chats = value;
              break;
            }
          }
        }
        const afterKey = res.data && res.data.searchAfterKey;

        // The contact-list row (html:242) is named from item.groupName — a
        // SUPPORT-tab result came back with no groupName/title at all (only
        // searchContent), rendering a blank row. Backfill from whatever name
        // field the item actually has, same defensive approach as the array
        // key lookup above.
        chats.forEach((item: any) => {
          if (!item.groupName) {
            item.groupName = item.title || item.searchContent || item.lastMessagedUserName || '';
          }
          if (!item.title) {
            item.title = item.groupName;
          }
        });

        // Same "no more data" detection as listAlltheMenuslist(): an unchanged
        // afterKey or an empty page means we've reached the end.
        if (JSON.stringify(this.chatsByTabAfterKey) === JSON.stringify(afterKey) || chats.length === 0) {
          this.chatsByTabHasMore = false;
          if (!loadMore) {
            this.contactList = [];
            this.nodatafound = true;
            this.contactdatafound = true;
          }
          return;
        }

        this.chatsByTabAfterKey = afterKey;
        this.chatsByTabHasMore = true;
        this.contactList = loadMore ? [...this.contactList, ...chats] : chats;
        this.nodatafound = false;
        this.contactdatafound = false;
      }, () => {
        if (token !== this.chatsByTabToken) { return; }
        this.loadingMore650 = false;
        this.chatsByTabSearching = false;
        this._util.stopLoader('msgspinner');
      });
  }




  @HostListener('window:resize', ['$event'])
  onResize1(event) {
    this.innerHeight1 = window.innerHeight;
    this.chatHeight1 = this.innerHeight1 - 90;
  }

  // Summernote shows its "Drag image or text here" overlay by adding a `.dragover` class
  // to `.note-editor` on dragenter, tracked by an internal drag counter it only resets on
  // its OWN clean drop / dragleave-to-<body>. When a drag ends any other way (dropped
  // outside, Esc-cancelled, imbalanced enter/leave) that counter stays stuck non-empty, so
  // the overlay hangs AND the next drag won't re-show it or accept a drop. Just removing the
  // CSS class isn't enough — the counter is still stuck. We dispatch a dragleave on <body>
  // so Summernote's own handler resets the counter and drops the class, keeping drag-drop
  // working for subsequent attempts.
  private resettingDropzone = false;
  private dropzonePending = false;

  private resetSummernoteDropzone() {
    if (this.resettingDropzone) { return; } // guard: our synthetic event re-enters below
    this.resettingDropzone = true;
    // target === <body> is the condition Summernote's onDragleave uses to reset its counter.
    document.body.dispatchEvent(new Event('dragleave', { bubbles: true }));
    $('.note-editor.dragover').removeClass('dragover');
    this.resettingDropzone = false;
  }

  @HostListener('window:drop')
  @HostListener('window:dragend')
  clearSummernoteDropzone() {
    this.dropzonePending = false;
    this.resetSummernoteDropzone();
  }

  // A drag that leaves the browser window fires no drop; clear on window-exit dragleave.
  @HostListener('window:dragleave', ['$event'])
  onWindowDragLeave(event: DragEvent) {
    if (this.resettingDropzone) { return; } // ignore our own synthetic dragleave
    if (!event.relatedTarget && (event.clientX <= 0 || event.clientY <= 0 ||
      event.clientX >= window.innerWidth || event.clientY >= window.innerHeight)) {
      this.dropzonePending = false;
      this.resetSummernoteDropzone();
    }
  }

  // Catch-all for drags that end WITHOUT firing drop/dragend/window-exit dragleave — e.g.
  // dragging text/an image from OUTSIDE the page and releasing it on a non-drop-target
  // (no drop fires, no in-page source for dragend). HTML5 drags suppress mouse events, so
  // the first mousemove after a drag means it's over — clear then. Registered OUTSIDE the
  // Angular zone so the frequent mousemove/dragenter never trigger change detection.
  private onDocDragEnter = () => { this.dropzonePending = true; };
  private onDocMouseMove = () => {
    if (this.dropzonePending) {
      this.dropzonePending = false;
      this.resetSummernoteDropzone();
    }
  };

  private registerDropzoneCatchAll() {
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('dragenter', this.onDocDragEnter);
      document.addEventListener('mousemove', this.onDocMouseMove);
      // Capture-phase so composer file drops/pastes are ours before Summernote sees them.
      document.addEventListener('drop', this.composerDropCapture, true);
      document.addEventListener('paste', this.composerPasteCapture, true);
    });
  }

  private unregisterDropzoneCatchAll() {
    window.removeEventListener('dragenter', this.onDocDragEnter);
    document.removeEventListener('mousemove', this.onDocMouseMove);
    document.removeEventListener('drop', this.composerDropCapture, true);
    document.removeEventListener('paste', this.composerPasteCapture, true);
  }

  initializeWebSocketConnection() {

  }
  params: any = {
    userId: localStorage.getItem('userId'),
    groupType: null,
    searchAfterKey: null,
    limit: 20
  }
  selectedMenu1: string;
  isLoading1: boolean = false
  loadingMore650: boolean = false // left contact-list scroll pagination — bottom skeleton, not the util spinner
  // Sub-header "Close" (wireframe) — leave the messaging page for Home/landing.
  closeMessages() {
    this.route.navigate(['/landingPage']);
  }

  // Display labels for every userType the connection lists can surface. The
  // short forms for the first three are the wireframe wording and are kept as-is.
  // RECRUITER / JOB_SEEKER were previously unmapped and fell through to the raw
  // value, so a user with no title rendered as shouty "RECRUITER" / "JOB SEEKER".
  // Keep this table in sync with the copy in MessageDetailsComponent.
  private readonly userTypeLabelMap: { [key: string]: string } = {
    MANAGEMENT_TALENT_ACQUISITION: 'Manager',
    BENCH_RECRUITER: 'Bench Sales',
    FREELANCE_RECRUITER: 'Freelancer',
    RECRUITER: 'Recruiter',
    JOB_SEEKER: 'Job Seeker',
  };

  // Convert a raw userType to a display label. An unmapped type (admin roles, or
  // anything added server-side later) is title-cased rather than shown in caps.
  private getUserTypeLabel(userType: string): string {
    if (!userType) { return ''; }
    const key = String(userType).trim();
    if (!key) { return ''; }
    return this.userTypeLabelMap[key] || key.replace(/_/g, ' ').toLowerCase()
      .replace(/\b\w/g, (c: string) => c.toUpperCase());
  }

  // New Private Chat subtitle (wireframe): "Role · Organization".
  // Role falls back to the converted userType when the user has no title;
  // the organization is dropped when absent (and vice-versa).
  getConnectionSubtitle(item: any): string {
    if (!item) { return ''; }
    const role = (item.title && String(item.title).trim()) || this.getUserTypeLabel(item.userType);
    const org = item.organisation && String(item.organisation).trim();
    return [role, org].filter(Boolean).join(' · ');
  }

  eventSelection(event: string) {

    // Store the current selected menu without changing anything else
    this.selectedMenu = event;
    // Update URL without reloading the page
    this.route.navigate([], {
      relativeTo: this.aroute,
      queryParams: { type: event !== 'All' ? event : null },
      queryParamsHandling: 'merge'
    });

    // A search is active ("Search by name..." box) — re-query the new
    // tab-scoped search endpoint for the newly-selected tab instead of the
    // plain tab list below, which stays untouched for the no-search case.
    if (this.searchTextname && this.searchTextname.trim() !== '') {
      this.chatsByTabAfterKey = null;
      this.chatsByTabHasMore = true;
      this.fetchChatsByTab(false);
      return;
    }

    // Only update the left contact list with new filter
    const params = {
      ...this.params,
      groupType: event,
      searchAfterKey: null // Reset pagination for the new filter
    };

    this.isLoading1 = true
    // New generation — invalidates any in-flight scroll-pagination response, and
    // reset pagination state up front so a re-click can't be corrupted by a race.
    const token = ++this.loadToken;
    this.prevAfterkey = null;
    this.stopScroll = false;
    this.params.searchAfterKey = null;
    this._api.create('message/findAllMessageContacts', params)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
      // A submenu (re)filter finishing means the page is no longer "initial
      // loading", so clear BOTH the left-list flag and the chat-area flag together
      // — otherwise the left shows data while middle/right stay skeleton.
      if (token !== this.loadToken) { this.isLoading1 = false; this.isLoading = false; return; }
      if (res) {
        this.isLoading1 = false
        this.isLoading = false
      }
      if (res.data && event != 'All') {
        this.contactfil = event;
      } else if (res.data && event == 'All') {
        this.contactfil = null;
      }
      // Process the response and update only the contact list
      if (res.data?.setContactsData?.length > 0) {
        this.contactList = res.data.setContactsData;
        this.nodatafound = false;
        this.contactdatafound = false;

        // Sort and process the list
        this.contactList = this.sortBy(this.contactList, 'lastMessageOn');
        let datas = this.duplicate(this.contactList);
        this.contactList = datas;
        // This branch REPLACES the whole list, so every local correction is gone:
        // the "0" a row click wrote, and the clear that ran when mark-seen landed.
        // Without this the open chat's badge came back on each tab switch, showing
        // whatever the server happened to report at that instant.
        this.clearBadgeForOpenChat('filter-switch');

        // Update pagination state
        this.prevAfterkey = res.data?.searchAfterKey;
        this.stopScroll = false;
      } else {
        this.contactList = [];
        // this.nodatafound = true;
        this.contactdatafound = true;
        this.stopScroll = true;
      }

      // Save the new params for future pagination
      this.params.groupType = event;
      this.params.searchAfterKey = null;

      // A filter (re)selection always loads page 1 — smoothly scroll the list back
      // to the top instead of restoring the old (scrolled-down) position.
      setTimeout(() => {
        const container = document.getElementById('entity-container');
        if (container) {
          container.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 0);

    }, error => {
      this.isLoading1 = false;
      this.isLoading = false;
      console.error('Error fetching filtered contacts:', error);
    });
  }

  contactdatafound: boolean = false;
  listAlltheMenuslist(from?: string) {
    // Scroll pagination shows a bottom skeleton; other loads keep the util spinner.
    if (from === 'scroll') {
      this.loadingMore650 = true;
    } else {
      this._util.startLoader('msgspinner');
    }
    // Scroll pagination continues the current generation; any other load starts a
    // new one. A stale scroll response (arriving after a menu re-click) is discarded.
    const token = from === 'scroll' ? this.loadToken : ++this.loadToken;

    this.params.groupType = this.selectedMenu ? this.selectedMenu : 'All';
    if (from === 'click') {
      this.selectedMenu1 = this.selectedMenu ? this.selectedMenu : 'All';
    }
    this._api.create('message/findAllMessageContacts', this.params)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
      this._util.stopLoader('msgspinner');
      this.loadingMore650 = false;
      // Clear the chat-area loading flag whenever ANY response lands — even a
      // superseded one (stale token). Otherwise clicking a submenu mid-load bumps
      // loadToken, this initial response is discarded below, and isLoading stays
      // true forever → middle/right + nested-left skeletons stick while the left
      // list already shows the newly-filtered data.
      this.isLoading = false;
      this.chatsByTabSearching = false;
      if (token !== this.loadToken) { return; }
      const afterKey = res.data?.searchAfterKey;
      if (this.initialLoad && res.data.setContactsData.length == 0) {
        // Only clear contactList if queryParamBasedMethod hasn't added a contact
        // Check if there are query params that would have added a contact
        let queryGroupId = this.aroute.snapshot.queryParamMap.get('groupId');
        let queryType = this.aroute.snapshot.queryParamMap.get('type');
        let queryRefererId = this.aroute.snapshot.queryParamMap.get('refererId');
        if (!queryGroupId && !queryType && !queryRefererId) {
          this.contactList = [];
        }
        if (this.contactList.length == 0) {
          this.nodatafound = true;
          this.contactdatafound = true;
        }
      }
      if ((JSON.stringify(this.prevAfterkey) === JSON.stringify(afterKey) || res.data.setContactsData.length == 0)
      ) {
        this.stopScroll = true;
        return;
      }
      this.prevAfterkey = afterKey;
      if (res.data?.setContactsData?.length > 0) {
        this.contactList = [...this.contactList, ...res.data.setContactsData];
        this.nodatafound = false;
        this.contactdatafound = false;
        this.intervalExistSeen = true;
        this.intervalNonSeenMessage = true;
        this.contactList = this.sortBy(this.contactList, 'lastMessageOn');
        let datas = this.duplicate(this.contactList);
        this.contactList = datas;
        // A redirect-opened chat (?groupId entry) selects its contact BEFORE this
        // list arrives — pick up the row's presence for the header now.
        this.syncSelectedOnlineFromList();
        // …and drop the unread badge on that row: this response was computed before
        // the chat's mark-seen landed, so its count is already out of date.
        this.clearBadgeForOpenChat('contact-list');

        // Only select first contact if URL has query parameters
        if (this.initialLoad) {
          // Check if there are URL parameters that require a specific chat to be loaded
          let groupId = this.aroute.snapshot.queryParamMap.get('groupId');
          let type = this.aroute.snapshot.queryParamMap.get('type');
          let refererId = this.aroute.snapshot.queryParamMap.get('refererId');

          // Only auto-select if URL parameters are present
          // Check for groupId OR refererId (BUSINESS contacts may not have groupId yet)
          if ((groupId || refererId) && type) {
            // URL parameters exist, selection will happen in queryParamBasedMethod()
            // Do nothing here as queryParamBasedMethod() will handle the selection
          } else {
            // No URL parameters, show empty state instead of selecting first contact
            this.messageStatus = this.selectMessage.EMPTY;
            this.SelectedContact = {};
            this.messageList = [];
          }
          this.initialLoad = false;
        }
      } else {
        this.contactdatafound = true;
      }
    }, error => {
      this._util.stopLoader('msgspinner');
      this.isLoading = false;
      this.loadingMore650 = false;
      this.chatsByTabSearching = false;
      console.error('Error fetching contacts:', error);
    });
  }




  UesrsScrollDown() {
    // A search is active — paginate the new tab-scoped search endpoint
    // instead, leaving the branch below untouched for the no-search case.
    if (this.searchTextname && this.searchTextname.trim() !== '') {
      if (this.chatsByTabHasMore && !this.loadingMore650) {
        this.fetchChatsByTab(true);
      }
      return;
    }
    if (JSON.stringify(this.params.searchAfterKey) !== JSON.stringify(this.prevAfterkey)) {
      this.params.searchAfterKey = (this.prevAfterkey && this.prevAfterkey.length === 0) ? null : this.prevAfterkey;
      this.listAlltheMenuslist('scroll');
    }
  }
  datechange(date) {
    const dateSendingToServer = new DatePipe('en-US').transform(date, 'MM/dd/yyyy HH:mm');
    return dateSendingToServer;
  }

  getInitials(firstname: string, lastname: string): string {
    return this.JobServicecolor.getInitialsparam(firstname, lastname);
  }

  getColor(firstname: string, lastname: string): string {
    return this.JobServicecolor.getColorparam(firstname, lastname);
  }
  getColorfullname(fullname: string): string {
    return this.JobServicecolor.getColor(fullname);
  }
  getFullNameTextColor(fullname: string): string {
    return this.JobServicecolor.getFullnameTextColor(fullname);
  }
  getInitialsfullname(fullname: string): string {
    return this.JobServicecolor.getInitials(fullname);
  }

  deleteitem(value) {
    //////value)

  }

  summernoteForm: UntypedFormGroup;
  initialLoad: boolean = false
  ngOnInit() {

    // Zone-safe catch-all so a failed/abandoned drag never leaves the dropzone overlay stuck.
    this.registerDropzoneCatchAll();

    // Set up the debounced search
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      this.performSearch();
    });

    // "Search by name..." box (getChatsByTab) — debounced independently of the
    // two subjects above, which drive unrelated searches (group-member modal /
    // business admin list).
    this.chatsByTabSearchSubscription = this.chatsByTabSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      this.runChatsByTabSearch();
    });

    // "Search in conversation" header box — debounced independently of every
    // other search subject in this file.
    this.convoSearchSubscription = this.convoSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      this.runConvoSearch();
    });

    this.searchBusSubscription = this.searchSubjectBusiness.pipe(
      debounceTime(500), // Wait 500ms after the user stops typing
      distinctUntilChanged() // Only emit if the value changed
    ).subscribe(searchTerm => {
      this.searchContent = searchTerm;
      this.onsearchbusinessadminlist();
    });

    this.contact.searchAfterKey = null
    this.innerHeight = window.innerHeight;

    let type = this.aroute.snapshot.queryParamMap.get('type');

    // this.selectedMenu = type;
    if (type && type != 'All') {
      this.contactfil = type;
    } else if (type && type == 'All') {
      this.contactfil = null;
    }

        const screenType = this.aroute.snapshot.queryParamMap.get('type');

    if (screenType) {
      // Check if the type parameter exists in our menuItems
      const exists = this.menuItems.some(item => item.value === screenType);
      if (exists) {
         this.selectedMenu = screenType;
      } else {
         this.selectedMenu = 'All'; // Default to 'All' if type doesn't match any menu item
      }
    } else {
       this.selectedMenu = 'All'; // Default to 'All' if type parameter doesn't exist
    }

    this.loginuser();

    this.summernoteForm = new UntypedFormGroup({
      summernoteEditor: new UntypedFormControl('', [this.summernoteValidator]),
    });
    this.onlinestatus = this.streamService.connect().pipe(
      switchMap(client => {
        if (!client.connected) {
          console.log('WebSocket is not connected');
          return of(null);
        } else {
          return this.streamService.onMessage('/topic/contacts/' + localStorage.getItem('userId'));
        }
      })
    ).subscribe(contact => {
      if (contact) {
        if (this.contactList.length > 0) {
          const updatedContactList = this.contactList.map((element) => {
            if (element.groupId === contact.groupId) {
              return {
                ...element,
                online: contact.online,
              };
            }
            return element;
          });
          this.contactList = updatedContactList;
        }
        // Same presence event, applied to the open chat's header too — it was
        // only ever mapped onto the list rows, so the header went stale.
        if (this.SelectedContact && this.SelectedContact.groupId === contact.groupId) {
          this.SelectedContact.online = contact.online;
        }
      }
    });
  }



  handleKeyUp(event: KeyboardEvent) {
    // Ignore modifier keys and special keys
    const ignoredKeys = [
      'Alt', 'AltGraph', 'CapsLock', 'Control', 'Fn', 'FnLock',
      'Hyper', 'Meta', 'NumLock', 'ScrollLock', 'Shift', 'Super',
      'Symbol', 'SymbolLock', 'Tab', 'ArrowDown', 'ArrowLeft',
      'ArrowRight', 'ArrowUp', 'End', 'Home', 'PageDown', 'PageUp',
      'Escape', 'PrintScreen', 'Insert', 'Delete', 'ContextMenu',
      'Help', 'Pause', 'Play', 'ZoomToggle', 'AudioVolumeUp', 'AudioVolumeDown',
      'AudioVolumeMute', 'LaunchApplication1', 'LaunchApplication2', 'BrowserHome'
    ];

    // Check if key is a function key (F1-F12) by key name or key code (112-123)
    const isFunctionKey = /^F\d+$/.test(event.key) || (event.keyCode >= 112 && event.keyCode <= 123);

    // Check for media keys and other special keys
    const isMediaOrSpecialKey = event.key.startsWith('Media') ||
      event.key.startsWith('Volume') ||
      event.key.startsWith('Audio') ||
      event.key.startsWith('Launch') ||
      event.key.startsWith('Browser') ||
      event.key.startsWith('Brightness') ||
      event.key.startsWith('KBD') ||
      event.key.startsWith('Light') ||
      event.key.indexOf('Brightness') !== -1 ||
      event.key.indexOf('Light') !== -1;

    // Check if any modifier key is pressed
    const hasModifier = event.ctrlKey || event.altKey || event.metaKey || event.shiftKey;

    // Check if this is a printable character (likely what we want for search)
    // This helps filter out many special keys not covered above
    const isPrintableKey = event.key.length === 1 || event.key === 'Backspace' || event.key === 'Space';

    // For debugging (remove in production)
    // console.log(`Key: ${event.key}, KeyCode: ${event.keyCode}, isPrintable: ${isPrintableKey}`);

    // Only trigger search if:
    // 1. It's a printable character (or backspace/space)
    // 2. No modifier keys are pressed
    // 3. It's not a function key or special key
    if (isPrintableKey && !hasModifier && !isFunctionKey && !isMediaOrSpecialKey &&
      !ignoredKeys.includes(event.key)) {
      this.onsearchgroupmembers();
    }
  }

  closeChat() {
    // Set the message status to EMPTY to display the empty state
    this.messageStatus = this.selectMessage.EMPTY;

    // Clear the selected contact
    this.SelectedContact = {};
    this.selectedMenu1 = null
    this.nodatafound = true
    // Clear message list
    this.messageList = [];

    // Update URL to remove any chat ID
    this.route.navigate(['/message'], {
      queryParams: { type: this.selectedMenu }
    });
  }

  handleKeyUp1(event: KeyboardEvent) {
    // Ignore modifier keys and special keys
    const ignoredKeys = [
      'Alt', 'AltGraph', 'CapsLock', 'Control', 'Fn', 'FnLock',
      'Hyper', 'Meta', 'NumLock', 'ScrollLock', 'Shift', 'Super',
      'Symbol', 'SymbolLock', 'Tab', 'ArrowDown', 'ArrowLeft',
      'ArrowRight', 'ArrowUp', 'End', 'Home', 'PageDown', 'PageUp',
      'Escape', 'PrintScreen', 'Insert', 'Delete', 'ContextMenu',
      'Help', 'Pause', 'Play', 'ZoomToggle', 'AudioVolumeUp', 'AudioVolumeDown',
      'AudioVolumeMute', 'LaunchApplication1', 'LaunchApplication2', 'BrowserHome'
    ];

    // Check if key is a function key (F1-F12) by key name or key code (112-123)
    const isFunctionKey = /^F\d+$/.test(event.key) || (event.keyCode >= 112 && event.keyCode <= 123);

    // Check for media keys and other special keys
    const isMediaOrSpecialKey = event.key.startsWith('Media') ||
      event.key.startsWith('Volume') ||
      event.key.startsWith('Audio') ||
      event.key.startsWith('Launch') ||
      event.key.startsWith('Browser') ||
      event.key.startsWith('Brightness') ||
      event.key.startsWith('KBD') ||
      event.key.startsWith('Light') ||
      event.key.indexOf('Brightness') !== -1 ||
      event.key.indexOf('Light') !== -1;

    // Check if any modifier key is pressed
    const hasModifier = event.ctrlKey || event.altKey || event.metaKey || event.shiftKey;

    // Check if this is a printable character (likely what we want for search)
    // This helps filter out many special keys not covered above
    const isPrintableKey = event.key.length === 1 || event.key === 'Backspace' || event.key === 'Space';

    // For debugging (remove in production)
    // console.log(`Key: ${event.key}, KeyCode: ${event.keyCode}, isPrintable: ${isPrintableKey}`);

    // Only trigger search if:
    // 1. It's a printable character (or backspace/space)
    // 2. No modifier keys are pressed
    // 3. It's not a function key or special key
    if (isPrintableKey && !hasModifier && !isFunctionKey && !isMediaOrSpecialKey &&
      !ignoredKeys.includes(event.key)) {
      this.searchgetMembersForGroup();
    }
  }


  onSearchInputChange(event: any) {
    this.searchSubjectBusiness.next(event.target.value);
  }

  summernoteValidator(control: UntypedFormControl) {
    const content = control.value;
    // Check if the content is empty or just contains HTML tags
    if (!content || content.trim() === '' || content.replace(/<[^>]*>/g, '').trim() === '') {
      return { required: true };
    }
    return null; // return null if there is no error
  }


  onSubmitsummernote() {
    if (this.summernoteForm.invalid) {
      // Handle the invalid form.
      console.log('Form is invalid due to empty content');
    } else {
      // Handle the valid form.
      console.log('Form is valid and ready to be submitted');
    }
  }

  loginuser() {
    this._api.query("user/" + localStorage.getItem('userId'))
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
      if (res.code == "00000") {
        this.loginUserData = res;
      }
    });
  }

  ngOnDestroy() {
    this.unregisterDropzoneCatchAll();
    this.intervalExistSeen = false;
    this.intervalNonSeenMessage = false;

    // Close any open body-level slide-in panel (Private/Group) so it doesn't
    // linger over the next page when this route is torn down — e.g. the session
    // expires and the app routes to /login. BsModalService (ngx-bootstrap) modals
    // are NOT dismissed by the interceptor's ngbModal.dismissAll() (that's NgbModal).
    if (this.modalRef) {
      this.modalRef.hide();
    }
    if (this.modalRefAddgroup) {
      this.modalRefAddgroup.hide();
    }

    // Clear all managed intervals
    this.intervalIds.forEach(id => clearInterval(id));
    this.intervalIds = [];

    // Complete all subscriptions using destroy$ subject
    this.destroy$.next();
    this.destroy$.complete();

    //this.route.routeReuseStrategy.shouldReuseRoute = () => true;

    $.extend($.summernote.plugins, {
      'brenter': function (context) {
        this.events = {
          'summernote.enter': function (we, e) {
            if (e.which === 13 || e.keycode === 13) {
            }
          }
        };
      }
    });
    if (this.onlinestatus) {
      this.onlinestatus.unsubscribe();
    }
    if (this.servernonseencount) {
      this.servernonseencount.unsubscribe();
    }
    if (this.socketTopicmessage) {
      this.socketTopicmessage.unsubscribe();
    }
    if (this.socketTopicmessage1) {
      this.socketTopicmessage1.unsubscribe();
    }
    if (this.socketTopicmessage2) {
      this.socketTopicmessage2.unsubscribe();
    }
    if (this.socketTopicmessage3) {
      this.socketTopicmessage3.unsubscribe();
    }

    if (this.socketTopicmessage6) {
      this.socketTopicmessage6.unsubscribe();
    }
    if (this.socketTopicmessage7) {
      this.socketTopicmessage7.unsubscribe();
    }

    if (this.socketTopicmessage10) {
      this.socketTopicmessage10.unsubscribe();
    }
    if (this.socketTopicmessage11) {
      this.socketTopicmessage11.unsubscribe();
    }
    if (this.socketTopicmessageMSG1) {
      this.socketTopicmessageMSG1.unsubscribe();
    }
    if (this.socketTopicmessageMSG2) {
      this.socketTopicmessageMSG2.unsubscribe();
    }
    if (this.socketTopicmessageMSG3) {
      this.socketTopicmessageMSG3.unsubscribe();
    }
    if (this.socketTopicmessageMSG4) {
      this.socketTopicmessageMSG4.unsubscribe();
    }
    if (this.socketTopicmessageMSG5) {
      this.socketTopicmessageMSG5.unsubscribe();
    }
    if (this.socketTopicmessageMSG6) {
      this.socketTopicmessageMSG6.unsubscribe();
    }
    if (this.socketTopicmessageMSG7) {
      this.socketTopicmessageMSG7.unsubscribe();
    }

    if (this.socketTopicmessageMSG8) {
      this.socketTopicmessageMSG8.unsubscribe();

    }
    if (this.socketTopicmessageMSG9) {
      this.socketTopicmessageMSG9.unsubscribe();
    }
    if (this.socketTopicmessageMSG10) {
      this.socketTopicmessageMSG10.unsubscribe();
    }


    if (this.socketTopicmessageMSG11) {
      this.socketTopicmessageMSG11.unsubscribe();
    }

    if (this.socketTopicmessageMSG12) {
      this.socketTopicmessageMSG12.unsubscribe();
    }

    if (this.socketTopicmessageMSG12) {
      this.socketTopicmessageMSG12.unsubscribe();
    }
    if (this.socketTopicmessageMSG13) {
      this.socketTopicmessageMSG13.unsubscribe();
    }
    if (this.socketTopicmessageMSG14) {
      this.socketTopicmessageMSG14.unsubscribe();
    }

    if (this.socketTopicmessage14) {
      this.socketTopicmessage14.unsubscribe();
    }

    if (this.socketTopicmessageMSG15) {
      this.socketTopicmessageMSG15.unsubscribe();
    }

    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }

    if (this.chatsByTabSearchSubscription) {
      this.chatsByTabSearchSubscription.unsubscribe();
    }

    if (this.convoSearchSubscription) {
      this.convoSearchSubscription.unsubscribe();
    }

    if (this.searchSubjectBusiness) {
      this.searchSubjectBusiness.unsubscribe();
    }


  }


  @ViewChild('summernoteInstance') summernoteInstance: ElementRef;

  ngAfterViewInit() {
    if (this.summernoteInstance && this.summernoteInstance.nativeElement) {
      const editor = $(this.summernoteInstance.nativeElement).next('.note-editor.note-frame');
      editor.css('max-height', '120px');
      editor.css('overflow-y', 'auto');
    }

    let userId = localStorage.getItem('userId');

    // Managed interval for intervalExistSeen check
    const intervalId1 = setInterval(() => {
      if (this.intervalExistSeen) {
        // getMessageSeenAndExistStatus implementation can be added here if needed
      }
    }, 5 * 1000) as any;
    this.intervalIds.push(intervalId1);

    // Managed interval for intervalNonSeenMessage check
    const intervalId2 = setInterval(() => {
      if (this.intervalNonSeenMessage) {
        //this.getNonSeenMessage();
      }
    }, 2 * 1000) as any;
    this.intervalIds.push(intervalId2);
  }

  routeProfie(item) {


    let data: any = {}

    // A personal profile can always be viewed — even after the connection is
    // removed (status INACTIVE) — so "View Full Profile", the name and the avatar
    // keep working for a no-longer-connected user.
    if (item.groupType == 'USER') {
      data.userId = item.groupId;
      this.route.navigate(["personalProfile"], { queryParams: data });
      return;
    }

    if (item.status != "INACTIVE") {
      if (item.groupType == 'TEAM') {
        this._api.query("teams/get/" + item.groupId).subscribe((res) => {
          if (res != undefined && res != null) {
            // res.data.Network.menu = "home"
            this.route.navigate(["teamPage/home"], {
              queryParams: res.data.teams,
            });
          }
        }, err => {
          this._util.stopLoader();
        });

      } else if (item.groupType == 'NETWORK') {
        this._util.startLoader();
        this._api.query("network/get/" + item.groupId).subscribe((res) => {
          this._util.stopLoader();
          if (res != undefined && res != null) {
            // res.data.Network.menu = "home"
            this.route.navigate(["networkPage/home"], {
              queryParams: res.data.Network,
            });
          }
        }, err => {
          this._util.stopLoader();
        });
      }
      else if (item.groupType == 'BUSINESS') {
        // if (item.refererId == null && item.refererId == 'null' && item.refererId == undefined) {
        //   data.businessId = item.groupId;
        // } else {
        //   data.businessId = item.refererId;
        // }
        data.businessId = item.refererId;
        data.userId = localStorage.getItem("userId");
        //   this._api.create("business/check/admin", data).subscribe((res) => {
        //     //  this.util.stopLoader()
        //     localStorage.setItem("businessId", data.businessId);
        //     localStorage.setItem("isAdmin", res.isAdmin);
        //     localStorage.setItem("isSuperAdmin", res.isSuperAdmin);
        //     localStorage.setItem("screen", "business");
        //     localStorage.setItem("adminviewflag", "false");
        //    this.route.navigate(["business/post"], { queryParams: data });
        //  });
        this.route.navigate(["business/post"], { queryParams: data });
      }
      else if (item.groupType == 'COMMUNITY') {

        // if (item.refererId == null && item.refererId == 'null' && item.refererId == undefined) {
        //   data.communityId = item.groupId;
        // } else {
        //   data.communityId = item.refererId;
        // }
        data.communityId = item.refererId;
        this.route.navigate(["community"], { queryParams: data });
      }
    }
  }


  // openModelChat(emptyMsgOpt, template?: TemplateRef<any>, ){
  //   if(emptyMsgOpt=="empty"){
  //     this.modalRef = this.modalService.show(template, Object.assign({}, { class: 'group-msg-modal modal-md' }));
  //   }
  // }



  private getNonSeenMessage() {
    let groupId = this.aroute.snapshot.queryParamMap.get('groupId');
    let type = this.aroute.snapshot.queryParamMap.get('type');
    if (groupId && groupId != null && groupId != "") {
      let url = "message/seen";
      let msg = {
        seen: false,
        authorId: groupId,
        recipientId: localStorage.getItem('userId'),
        messageType: type
      };
      this._api.messagePageService('POST', url, msg).subscribe(res => {
        if (res) {
          let msgList = res;
          if (msgList && msgList != null && msgList.length > 0) {
            msgList.forEach(element => {
              element.justSendmsg = false;
              this.messageList.push(element);
              this.messageList = this.sortByMsg(this.messageList, 'lastMessageOn');
            });

            setTimeout(() => {
              if (this.chartDisc && this.chartDisc.nativeElement) {
                this.chartDisc.nativeElement.scroll({
                  top: this.chartDisc.nativeElement.scrollHeight,
                  left: 0
                });
              }
            }, 200);
            this.updateNonSeenMessage(msgList);
          }
        }
      });
    }
  }

  private updateNonSeenMessage(message: any) {
    let id = localStorage.getItem('userId');
    let url = "message/seen/" + id;
    //this._socket.send(url, message);
    this.socketTopicmessage14 = this._api.messagePageService('PUT', url, message).subscribe(res => {
      // The list may already have rendered a pre-read count for this chat.
      this.clearBadgeForOpenChat('mark-seen');
      if (res) { }
    }, (err: any) => { });
  }
  updateSearch(event) {
    this.searchTerm = event.target.value;
  }
  getAllContacts() {
    let url = "message/contact/" + this.userId;
    this._util.startLoader('msgspinner');
    this._api.messagePageService('GET', url).subscribe(res => {
      if (res) {

        let newdatas: any = res.filter(element => element.groupName != undefined);
        console.log(newdatas);

        // let  newres = res.filter(element => element.groupName !== undefined || element.groupName !== null);
        newdatas.forEach(element => {
          if (element.lastMessageOn == null) {
            element.lastMessageOn = element.createdOn
          }
          this.contactList = newdatas;
        });



        ////// "contactListcontactList", this.contactList)
        // this.contactList = res;
        this.queryParamBasedMethod();
        if (this.contactList && this.contactList != null && this.contactList.length > 0) {
          this.intervalExistSeen = true;
          this.intervalNonSeenMessage = true;
          this.contactList = this.sortBy(this.contactList, 'lastMessageOn');
          let datas = this.duplicate(this.contactList);
          this.contactList = datas;
        }
      } else {
        this.contactList = [];
        this._util.stopLoader('msgspinner');
      }
    }, err => {
      this._util.stopLoader();
    });
  }


  private queryParamBasedMethod() {
    console.log(this.aroute.url)
    let groupId = this.aroute.snapshot.queryParamMap.get('groupId');
    let type = this.aroute.snapshot.queryParamMap.get('type');
    let refererId = this.aroute.snapshot.queryParamMap.get('refererId');
    var newRefererId;
    if (groupId != null) {
      this.userRedirect.groupId = groupId
    } if (type != null) {
      this.userRedirect.type = type
    } if (refererId != null) {
      this.userRedirect.refererId = refererId

    }

    if (refererId == null) {
      newRefererId = groupId;
    } else if (refererId != null) {
      newRefererId = refererId
    }
    if (type && type != null && type != '' && (groupId != null || refererId != null || type.toUpperCase() === 'SUPPORT')) {

      let contact = {
        userId: localStorage.getItem('userId'),
        groupType: type.toUpperCase(),
        groupId: groupId,
        messageType: type.toUpperCase(),
        refererId: refererId
      };
      let url = "message/contact/exist";
      this.currentIndex = 0;
      this._util.startLoader('msgspinner');
      this._api.messagePageService('POST', url, contact).subscribe(res => {
        /*setTimeout(() => {
        this._util.stopLoader('msgspinner');
        }, 800); */
        this.sendMessageData.message = ""
        this.editMessage = false;
        // Feed the right-side <app-message-details> widget — the in-page
        // contact-click path (existsapicall) stores this same response, but
        // this redirect entry (?groupId&type from job/candidate Chat) never
        // did, leaving the widget's info rows empty until a manual click.
        this.Jobandcandidatedetails = res;
        this.sendMsgres.date = res['date'];
        this.sendMsgres.recipientId = res['groupId'];
        this.searchAfterKeys = null;
        if (res && res['code'] == '00000') {
          let d = res;
          if (d['pageAdmin']) {
            this.route.navigate(['/message']);
            return;
          }
          if (d['exist'] || d['groupType'] == "SUPPORT" && res.groupId != undefined && res.groupId != null) {
            if (res.groupStatus == "INACTIVE") {
              Swal.fire('', 'The ' + res.groupType.toLowerCase() + ' has been deleted', 'error');
            }
            if ((type === 'COMMUNITY' || type === 'BUSINESS' || type === 'SUPPORT') && (groupId == undefined || groupId == null || groupId == '')) {
              let l = {
                groupId: d['groupId'],
                type: type,
                refererId: refererId
              };
              //this.route.routeReuseStrategy.shouldReuseRoute = () => true;

              this.route.navigate(['/message'], { queryParams: l });

            }
            let index = this.contactList.findIndex((item) => item.groupId === d.groupId);
            if (index < 0) {
              // The exist response carries the unread count as it stood BEFORE this
              // chat was opened, and opening it marks those messages seen a moment
              // later. Pushing the snapshot as-is froze that number on the row: the
              // fresh server row (count 0) loses the groupId dedupe in
              // listAlltheMenuslist, which keeps whichever copy is already in the
              // list. Zero it here — we are opening this chat, so nothing is unread.
              d['nonSeenCount'] = 0;
              this.contactList.push(d);
            }
            // Reset no-data flags and loading since we have a contact
            this.nodatafound = false;
            this.contactdatafound = false;
            this.isLoading = false;

            this.messageList = []
            this.SelectedContact = res;
            this.syncSelectedOnlineFromList();

            if (res.status == "GROUP_INACTIVE") {
              this.SelectedContact.status = "INACTIVE";
              // console.log("got printed here")
            }

            if (type === "BUSINESS" || type === "COMMUNITY" || type === "COMMUNITY") {
              this.SelectedContact.newstatus = this.SelectedContact.status;
              if (this.SelectedContact.status == "ACTIVE" && res.chatStatus == "ENABLED" || this.SelectedContact.status == "ACTIVE" && res.chatStatus == "NEW") {
                this.SelectedContact.status = "ACTIVE";
              } else if (this.SelectedContact.chatStatus == "DISABLED") {
                this.SelectedContact.status = "INACTIVE";
                // console.log("got printed here")
              }
            }
            this.sendMsgres.recipientId = d['groupId'];
            this.startFreshMessageLoad(d['groupId']);

          } else {
            // New contact - exist=false, no groupId yet
            this._util.stopLoader('msgspinner');
            this.isLoading = false;
            this.contactList.push(res);
            this.SelectedContact = res;
            this.syncSelectedOnlineFromList();
            this.messageList = [];
            let datas = this.duplicate(this.contactList);
            this.contactList = datas;
            // Reset no-data flags since we now have a contact
            this.nodatafound = false;
            this.contactdatafound = false;
            // Set up recipientId using refererId for new contacts
            this.sendMsgres.recipientId = res['refererId'];
            // Call messageListscroll to initialize the chat (will return empty messages for new contact)
            this.startFreshMessageLoad(res['refererId']);
          }
          this.messageStatus = MESSAGESTATUS.MESSAGE;
          if (type === 'USER') {

            this.socketTopicmessageMSG7 = this._socket.onMessage('/topic/message/' + groupId + '-' + localStorage.getItem('userId')).subscribe(res => {
              //////"USER", res)
              //////" this.SelectedContact", this.SelectedContact)
              if (this.SelectedContact.groupId == res.authorId && type === 'USER') {
                //" queryParam USER ", res)
                this.constructMsg(this, res);
              }

            });
            this.socketTopicmessageMSG8 = this._socket.onMessage('/topic/message/' + localStorage.getItem('userId') + '-' + groupId).subscribe(res => {
              //////"USER2", res)
              //////" this.SelectedContact", this.SelectedContact)
              if (this.SelectedContact.groupId == res.authorId && type === 'USER') {
                //" queryParam USER1 ", res)

                this.constructMsg(this, res);
              }
            });

            this.socketTopicmessageMSG13 = this._socket.onMessage('/topic/event/' + localStorage.getItem('userId')).subscribe(res_evn => {
              // console.log("res_evn userr " + JSON.stringify(res_evn))
              // console.log("printed 1")
              if (type === 'USER') {

                this.SelectedContact.status = res_evn.status;

                if (res_evn.status == 'GROUP_INACTIVE' && res_evn.groupId === this.SelectedContact.groupId) {
                  this.SelectedContact.status = "INACTIVE"
                  // console.log("got printed here")
                }


              }
              // this.SelectedContact = {};

              // this.messageStatus = MESSAGESTATUS.EMPTY;
              // const index = this.contactList.findIndex(x => x.groupId === res_evn.groupId);
              // if (index !== undefined) this.contactList.splice(index, 1);
            });

          } else if (type === 'GROUP' || type === 'NETWORK' || type === 'TEAM' || type === 'JOB' || type === 'CANDIDATE') {

            this.socketTopicmessageMSG9 = this._socket.onMessage('/topic/message/' + groupId).subscribe(res => {

              if (type === 'GROUP' || type === 'NETWORK' || type === 'TEAM' || type === 'JOB' || type === 'CANDIDATE') {
                if (res.eventType == "ACTION") {
                  const timezoneDatePipe = new TimezoneDatePipe();
                  const dates = res.sentDate;
                  const latest_date = timezoneDatePipe.transform(dates, 'custom');
                  var tempMsg: any = [];
                  var secondTemMsg: any = [];
                  if (res.actionType == "ADDED_TO_NETWORK") {
                    if (res.authorId == this.userId) {
                      tempMsg = " You "
                    } else {
                      tempMsg = res.authorName;
                    }
                    if (res.recipientUserId == this.userId) {
                      secondTemMsg = " You "
                    } else {
                      secondTemMsg = res.recipientName;
                    }
                    res.oldMessage = tempMsg + " joined " + " on " + latest_date;
                  }
                  else if (res.actionType == "REMOVED_FROM_NETWORK") {
                    if (res.authorId == this.userId) {
                      tempMsg = " You "
                    } else {
                      tempMsg = res.authorName;
                    }
                    if (res.recipientUserId == this.userId) {
                      secondTemMsg = " You "
                    } else {
                      secondTemMsg = res.recipientName;
                    }
                    res.oldMessage = tempMsg + " removed " + secondTemMsg + " on " + latest_date;
                  } else if (res.actionType == "LEFT_NETWORK") {
                    if (res.authorId == this.userId) {
                      tempMsg = " You "
                    } else {
                      tempMsg = res.authorName;
                    }
                    if (res.recipientUserId == this.userId) {
                      secondTemMsg = " You "
                    } else {
                      secondTemMsg = res.recipientName;
                    }
                    res.oldMessage = tempMsg + " left " + " on " + latest_date;
                  } else if (res.actionType == "ADDED_TO_TEAM") {
                    if (res.authorId == this.userId) {
                      tempMsg = " You "
                    } else {
                      tempMsg = res.authorName;
                    }
                    if (res.recipientUserId == this.userId) {
                      secondTemMsg = " You "
                    } else {
                      secondTemMsg = res.recipientName;
                    }
                    res.oldMessage = tempMsg + " joined " + " on " + latest_date;
                  } else if (res.actionType == "REMOVED_FROM_TEAM") {
                    if (res.authorId == this.userId) {
                      tempMsg = " You "
                    } else {
                      tempMsg = res.authorName;
                    }
                    if (res.recipientUserId == this.userId) {
                      secondTemMsg = " You "
                    } else {
                      secondTemMsg = res.recipientName;
                    }
                    res.oldMessage = tempMsg + " removed " + secondTemMsg + " on " + latest_date;
                  } else if (res.actionType == "LEFT_TEAM") {
                    if (res.authorId == this.userId) {
                      tempMsg = " You "
                    } else {
                      tempMsg = res.authorName;
                    }
                    if (res.recipientUserId == this.userId) {
                      secondTemMsg = " You "
                    } else {
                      secondTemMsg = res.recipientName;
                    }
                    res.oldMessage = tempMsg + " left " + " on " + latest_date;
                  } else if (res.actionType == "ADDED_TO_MSG_GRP") {
                    if (res.authorId == this.userId) {
                      tempMsg = " You "
                    } else {
                      tempMsg = res.authorName;
                    }
                    if (res.recipientUserId == this.userId) {
                      secondTemMsg = " You "
                    } else {
                      secondTemMsg = res.recipientName;
                    }
                    res.oldMessage = tempMsg + " added " + secondTemMsg + " on " + latest_date;
                  } else if (res.actionType == "REMOVED_FROM_MSG_GRP") {
                    if (res.authorId == this.userId) {
                      tempMsg = " You "
                    } else {
                      tempMsg = res.authorName;
                    }
                    if (res.recipientUserId == this.userId) {
                      secondTemMsg = " You "
                    } else {
                      secondTemMsg = res.recipientName;
                    }
                    res.oldMessage = tempMsg + " removed " + secondTemMsg + " on " + latest_date;
                  } else if (res.actionType == "LEFT_FROM_MSG_GRP") {
                    if (res.authorId == this.userId) {
                      tempMsg = " You "
                    } else {
                      tempMsg = res.authorName;
                    }
                    if (res.recipientUserId == this.userId) {
                      secondTemMsg = " You "
                    } else {
                      secondTemMsg = res.recipientName;
                    }
                    res.oldMessage = tempMsg + " left " + " on " + latest_date;
                  } else if (res.actionType == "ENABLED_RIGHTS") {
                    if (res.authorId == this.userId) {
                      tempMsg = " You "
                    } else {
                      tempMsg = res.authorName;
                    }
                    if (res.recipientUserId == this.userId) {
                      secondTemMsg = " You "
                    } else {
                      secondTemMsg = res.recipientName;
                    }
                    res.oldMessage = tempMsg + " enabled chat rights for " + secondTemMsg + " on " + latest_date;
                  } else if (res.actionType == "DISABLED_RIGHTS") {
                    if (res.authorId == this.userId) {
                      tempMsg = " You "
                    } else {
                      tempMsg = res.authorName;
                    }
                    if (res.recipientUserId == this.userId) {
                      secondTemMsg = " You "
                    } else {
                      secondTemMsg = res.recipientName;
                    }
                    res.oldMessage = tempMsg + " disabled  chat rights for " + secondTemMsg + " on " + latest_date;
                  }

                  else if (this.SelectedContact.groupType == "NETWORK" && res.actionType == "DEACTIVATED") {
                    res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                  }

                  else if (this.SelectedContact.groupType == "TEAM" && res.actionType == "DEACTIVATED") {
                    res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                  }

                  else if (this.SelectedContact.groupType == "BUSINESS" && res.actionType == "DEACTIVATED") {
                    res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                  }

                  else if (this.SelectedContact.groupType == "COMMUNITY" && res.actionType == "DEACTIVATED") {
                    res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                  }


                  else {
                    res.oldMessage = res.message + " on " + latest_date;

                  }
                }



                if (this.SelectedContact.status == 'ACTIVE' && res.recipientId == this.sendMsgres.recipientId) {
                  //" queryParam GROUP ", res)
                  this.constructMsg(this, res);
                }
              }

            });




            this.socketTopicmessage7 = this._socket.onMessage('/topic/event/' + localStorage.getItem('userId')).subscribe(res_evn => {
              //////"GROUP2", res)
              // console.log(res_evn)
              // console.log(this.userRedirect.groupId)
              // console.log("printed 2")
              // console.log("res_evn GROUP2 "+JSON.stringify(res_evn))

              if (type === 'GROUP' || type === 'NETWORK' || type === 'TEAM' || type === 'JOB' || type === 'CANDIDATE') {
                console.log(this.userRedirect.groupId)
                console.log(res_evn.groupId)
                // this.SelectedContact.status = res_evn.status;
                if ((res_evn.status == 'GROUP_INACTIVE' || res_evn.status == 'INACTIVE') && this.userRedirect.groupId === res_evn.groupId) {
                  this.SelectedContact.status = "INACTIVE"
                  // console.log("got printed here")
                }
                //////'topic/event', res_evn);

                this.tpoicData = res_evn;
                // const index = this.contactList.findIndex(x => x.groupId === res_evn.groupId);
                // if (index !== undefined) this.contactList.splice(index, 1);
                //
                // this.messageStatus = MESSAGESTATUS.EMPTY;
                if (this.SelectedContact.groupId == res_evn.groupId) {
                  if (res_evn.groupStatus == 'REVOKED') {
                    // Swal.fire(res_evn.groupOwner + ' removed you from ' + res_evn.groupName);
                  } else if (res_evn.groupStatus == 'INACTIVE') {
                    Swal.fire(res_evn.groupName + ' group has been deleted');
                  }
                }
              }
            });

          } else if (type === 'BUSINESS' || type === 'COMMUNITY' || type === 'SUPPORT') {

            if (groupId != undefined && groupId != null && groupId != "") {

              this.socketTopicmessageMSG10 = this._socket.onMessage('/topic/message/' + groupId).subscribe(res => {

                if (res.recipientId == this.SelectedContact.groupId && type === 'BUSINESS' || res.recipientId == this.SelectedContact.groupId && type === 'COMMUNITY') {

                  if (res.eventType == "ACTION") {
                    const timezoneDatePipe = new TimezoneDatePipe();
                    const dates = res.sentDate;
                    const latest_date = timezoneDatePipe.transform(dates, 'custom');

                    var tempMsg: any = [];
                    var secondTemMsg: any = [];
                    if (res.actionType == "ADDED_TO_NETWORK") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " joined " + " on " + latest_date;
                    }
                    else if (res.actionType == "REMOVED_FROM_NETWORK") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " removed " + secondTemMsg + " on " + latest_date;
                    } else if (res.actionType == "LEFT_NETWORK") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " left " + " on " + latest_date;
                    } else if (res.actionType == "ADDED_TO_TEAM") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " joined  " + " on " + latest_date;
                    } else if (res.actionType == "REMOVED_FROM_TEAM") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " removed " + secondTemMsg + " on " + latest_date;
                    } else if (res.actionType == "LEFT_TEAM") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " left " + " on " + latest_date;
                    } else if (res.actionType == "ADDED_TO_MSG_GRP") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " added " + secondTemMsg + " on " + latest_date;
                    } else if (res.actionType == "REMOVED_FROM_MSG_GRP") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " removed " + secondTemMsg + " on " + latest_date;
                    } else if (res.actionType == "LEFT_FROM_MSG_GRP") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " left " + " on " + latest_date;
                    } else if (res.actionType == "ENABLED_RIGHTS") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " enabled chat rights for " + secondTemMsg + " on " + latest_date;
                    } else if (res.actionType == "DISABLED_RIGHTS") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " disabled  chat rights for " + secondTemMsg + " on " + latest_date;
                    }

                    else if (this.SelectedContact.groupType == "NETWORK" && res.actionType == "DEACTIVATED") {
                      res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                    }

                    else if (this.SelectedContact.groupType == "TEAM" && res.actionType == "DEACTIVATED") {
                      res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                    }

                    else if (this.SelectedContact.groupType == "BUSINESS" && res.actionType == "DEACTIVATED") {
                      res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                    }

                    else if (this.SelectedContact.groupType == "COMMUNITY" && res.actionType == "DEACTIVATED") {
                      res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                    }

                    else {
                      res.oldMessage = res.message + " on " + latest_date;

                    }
                  }


                  //" queryParam BUSINESS ", res)
                  this.constructMsg(this, res);
                }
              });
            } else {

              this.socketTopicmessageMSG15 = this._socket.onMessage('/topic/message/' + refererId).subscribe(res => {
                ////"USER BUSINESS Message2", res)

                ////this.SelectedContact.status)

                if (type === 'BUSINESS' || type === 'COMMUNITY') {


                  if (res.recipientId == this.SelectedContact.groupId) {
                    if (res.eventType == "ACTION") {
                      const timezoneDatePipe = new TimezoneDatePipe();
                      const dates = res.sentDate;
                      const latest_date = timezoneDatePipe.transform(dates, 'custom');

                      var tempMsg: any = [];
                      var secondTemMsg: any = [];
                      if (res.actionType == "ADDED_TO_NETWORK") {
                        if (res.authorId == this.userId) {
                          tempMsg = " You "
                        } else {
                          tempMsg = res.authorName;
                        }
                        if (res.recipientUserId == this.userId) {
                          secondTemMsg = " You "
                        } else {
                          secondTemMsg = res.recipientName;
                        }
                        res.oldMessage = tempMsg + " joined " + " on " + latest_date;
                      }
                      else if (res.actionType == "REMOVED_FROM_NETWORK") {
                        if (res.authorId == this.userId) {
                          tempMsg = " You "
                        } else {
                          tempMsg = res.authorName;
                        }
                        if (res.recipientUserId == this.userId) {
                          secondTemMsg = " You "
                        } else {
                          secondTemMsg = res.recipientName;
                        }
                        res.oldMessage = tempMsg + " removed " + secondTemMsg + " on " + latest_date;
                      } else if (res.actionType == "LEFT_NETWORK") {
                        if (res.authorId == this.userId) {
                          tempMsg = " You "
                        } else {
                          tempMsg = res.authorName;
                        }
                        if (res.recipientUserId == this.userId) {
                          secondTemMsg = " You "
                        } else {
                          secondTemMsg = res.recipientName;
                        }
                        res.oldMessage = tempMsg + " left " + " on " + latest_date;
                      } else if (res.actionType == "ADDED_TO_TEAM") {
                        if (res.authorId == this.userId) {
                          tempMsg = " You "
                        } else {
                          tempMsg = res.authorName;
                        }
                        if (res.recipientUserId == this.userId) {
                          secondTemMsg = " You "
                        } else {
                          secondTemMsg = res.recipientName;
                        }
                        res.oldMessage = tempMsg + " joined " + " on " + latest_date;
                      } else if (res.actionType == "REMOVED_FROM_TEAM") {
                        if (res.authorId == this.userId) {
                          tempMsg = " You "
                        } else {
                          tempMsg = res.authorName;
                        }
                        if (res.recipientUserId == this.userId) {
                          secondTemMsg = " You "
                        } else {
                          secondTemMsg = res.recipientName;
                        }
                        res.oldMessage = tempMsg + " removed " + secondTemMsg + " on " + latest_date;
                      } else if (res.actionType == "LEFT_TEAM") {
                        if (res.authorId == this.userId) {
                          tempMsg = " You "
                        } else {
                          tempMsg = res.authorName;
                        }
                        if (res.recipientUserId == this.userId) {
                          secondTemMsg = " You "
                        } else {
                          secondTemMsg = res.recipientName;
                        }
                        res.oldMessage = tempMsg + " left " + " on " + latest_date;
                      } else if (res.actionType == "ADDED_TO_MSG_GRP") {
                        if (res.authorId == this.userId) {
                          tempMsg = " You "
                        } else {
                          tempMsg = res.authorName;
                        }
                        if (res.recipientUserId == this.userId) {
                          secondTemMsg = " You "
                        } else {
                          secondTemMsg = res.recipientName;
                        }
                        res.oldMessage = tempMsg + " added " + secondTemMsg + " on " + latest_date;
                      } else if (res.actionType == "REMOVED_FROM_MSG_GRP") {
                        if (res.authorId == this.userId) {
                          tempMsg = " You "
                        } else {
                          tempMsg = res.authorName;
                        }
                        if (res.recipientUserId == this.userId) {
                          secondTemMsg = " You "
                        } else {
                          secondTemMsg = res.recipientName;
                        }
                        res.oldMessage = tempMsg + " removed " + secondTemMsg + " on " + latest_date;
                      } else if (res.actionType == "LEFT_FROM_MSG_GRP") {
                        if (res.authorId == this.userId) {
                          tempMsg = " You "
                        } else {
                          tempMsg = res.authorName;
                        }
                        if (res.recipientUserId == this.userId) {
                          secondTemMsg = " You "
                        } else {
                          secondTemMsg = res.recipientName;
                        }
                        res.oldMessage = tempMsg + " left " + " on " + latest_date;
                      } else if (res.actionType == "ENABLED_RIGHTS") {
                        if (res.authorId == this.userId) {
                          tempMsg = " You "
                        } else {
                          tempMsg = res.authorName;
                        }
                        if (res.recipientUserId == this.userId) {
                          secondTemMsg = " You "
                        } else {
                          secondTemMsg = res.recipientName;
                        }
                        res.oldMessage = tempMsg + " enabled chat rights for " + secondTemMsg + " on " + latest_date;
                      } else if (res.actionType == "DISABLED_RIGHTS") {
                        if (res.authorId == this.userId) {
                          tempMsg = " You "
                        } else {
                          tempMsg = res.authorName;
                        }
                        if (res.recipientUserId == this.userId) {
                          secondTemMsg = " You "
                        } else {
                          secondTemMsg = res.recipientName;
                        }
                        res.oldMessage = tempMsg + " disabled  chat rights for " + secondTemMsg + " on " + latest_date;
                      }
                      else if (this.SelectedContact.groupType == "NETWORK" && res.actionType == "DEACTIVATED") {
                        res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                      }

                      else if (this.SelectedContact.groupType == "TEAM" && res.actionType == "DEACTIVATED") {
                        res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                      }

                      else if (this.SelectedContact.groupType == "BUSINESS" && res.actionType == "DEACTIVATED") {
                        res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                      }

                      else if (this.SelectedContact.groupType == "COMMUNITY" && res.actionType == "DEACTIVATED") {
                        res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                      }

                      else {
                        res.oldMessage = res.message + " on " + latest_date;

                      }
                    }

                    //" queryParam BUSINESS refererId", res)

                    this.constructMsg(this, res);
                  }
                }
              });
            }

            this.socketTopicmessage3 = this._socket.onMessage('/topic/event/' + localStorage.getItem('userId')).subscribe(res_evn => {
              //"res_business one event " + JSON.stringify(res_evn))
              // console.log("printed 11")

              //console.log("res_business oneevent " +JSON.stringify(res_evn))

              if (type === 'BUSINESS' || type === 'COMMUNITY') {
                if (res_evn.groupId === this.SelectedContact.groupId) {
                  if (res_evn.status == "ACTIVE") {
                    if (res_evn.chatStatus == "ENABLED") {
                      this.SelectedContact.status = "ACTIVE";
                    } else if (res_evn.chatStatus == "DISABLED") {
                      this.SelectedContact.status = "INACTIVE";
                      // console.log("got printed here")
                    }
                  } else if (res_evn.status == "INACTIVE" || res_evn.status == "GROUP_INACTIVE") {
                    this.SelectedContact.status = "INACTIVE";
                    // console.log("got printed here")
                  }
                }


              }
              // const index = this.contactList.findIndex(x => x.groupId === res_evn.groupId);
              // if (index !== undefined) this.contactList.splice(index, 1);
            });

          }


          this._util.stopLoader('msgspinner');
        } else {
          this._util.stopLoader('msgspinner');
          if (this.redirect == true) {
            Swal.fire('', res['message'], 'error');
          } else if (this.redirect == false) {
            this.redirect = true;
          }
          this._location.replaceState('/message');

          // this.route.navigate(['/message']);
          //this._location.back();
          // this.route.navigate(['/message']);
        }
      }, err => {
        this._util.stopLoader();
      });
    } else {
      this._util.stopLoader('msgspinner');
    }
    this.socketTopicmessage2 = this._socket.onMessage('/topic/contacts/all/' + localStorage.getItem('userId')).subscribe((contact) => {
      //////'123 ', contact);
      let contactList = contact;
      contactList = this.sortBy(contact, 'lastMessageOn');
      if (this.contactList != null && this.contactList != undefined) {
        this.contactList.forEach(element => {
          if (element.lastMessageOn == null) {
            element.lastMessageOn = element.createdOn
          }
          contactList = this.sortBy(contact, 'lastMessageOn');
          let index = this.contactList.findIndex((item) => (item.userId === element.userId && item.groupId === element.groupId));
          if (index < 0) {
            this.contactList.push(element);
            var data: any = []
            data = this.duplicate(this.contactList);
            this.contactList = data;
          } else {
            Object.assign(this.contactList[index], element);
          }
        });
      }
    });

    this.socketTopicmessage1 = this._socket.onMessage('/topic/contacts/all/remain/' + localStorage.getItem('userId')).subscribe((contact) => {
      let contactList = contact;

      if (this.remainingContact != null && this.remainingContact != undefined) {
        contactList.forEach(element => {
          if (element.lastMessageOn == null) {
            element.lastMessageOn = element.createdOn
          }
          contactList = this.sortBy(contact, 'lastMessageOn');
          let index = this.remainingContact.findIndex((item) => (item.userId === element.userId && item.groupId === element.groupId));
          if (index < 0) {
            this.remainingContact.push(element);
            let datas: any;
            datas = this.duplicate(this.remainingContact);
            this.remainingContact = datas

          } else {
            Object.assign(this.remainingContact[index], element);
          }
        });
      }

    });
    this.servernonseencount = this.socketTopicmessage = this._socket.connect().pipe(
      switchMap(client => {
        if (!client.connected) {
          console.log('WebSocket is not connected');
          return of(null);
        } else {
          return this._socket.onMessage('/topic/contacts/' + localStorage.getItem('userId'));
        }
      })
    ).subscribe(contact => {
      if (contact) {
        if (this.contactList != null && this.contactList != undefined) {
          this.contactList.forEach((e, index) => {
            if (contact.groupId === e.groupId) {

              e['lastMessagedUserName'] = contact.lastMessagedUserName;
              e['title'] = contact.title;
              e['lastMessageOn'] = contact['lastMessageOn'];
              // Left-list preview; skip when the payload omits it so it isn't wiped.
              if (contact.lastMessage != null) { e['lastMessage'] = contact.lastMessage; }

              // One increment per incoming message from someone else. (There used
              // to be a second, unguarded +1 block for groupType USER right after
              // this one — it made private chats count +2 per message and also
              // counted the user's OWN sent messages. Removed; this block's guards
              // already cover USER chats correctly.)
              if (this.userId != contact.userId && contact.status != 'INACTIVE') {
                e['nonSeenCount'] = (parseInt(e['nonSeenCount']) || 0) + 1;

                if (this.SelectedContact.groupId == contact.groupId) {
                  e['nonSeenCount'] = 0;
                }
              }
              this.contactList = this.sortBy(this.contactList, 'lastMessageOn');
            }
          });
          let index = this.contactList.findIndex((item) => (item.userId === contact.userId && item.groupId === contact.groupId));
          if (index < 0) {
          } else {
            Object.assign(this.contactList[index], this.contactList[index]);
          }
        }
        this.sortBy(this.contactList, 'lastMessageOn');
        let data: any = {};
        data = this.duplicate(this.contactList);
        this.contactList = data;
      }
    });


    this.socketTopicmessage11 = this._socket.onMessage('/topic/newcontacts/' + localStorage.getItem('userId')).subscribe((contact) => {

      var dateOne = new Date(contact.lastMessageOn); //Year, Month, Date
      var dateTwo = new Date(contact.lastModifiedOn); //Year, Month, Date
      if (dateOne > dateTwo) {
      } else { contact.lastMessageOn = contact.lastModifiedOn; }


      this.contactList.push(contact);
      this.contactList = this.sortBy(this.contactList, 'lastMessageOn');
      let data: any = {};
      data = this.duplicate(this.contactList);
      this.contactList = data;

    });

  }




  /* Fresh-open message load: every conversation open MUST start from a clean
   * cursor. Rapid clicks interleave so that the PREVIOUS open's messages
   * response writes searchAfterKeys back AFTER the new click reset it — then
   * messageListscroll's end-of-history skip (sak != null && DataLength < 10)
   * fires on the freshly wiped list and no request goes out at all → blank
   * conversation. Resetting here, at load time, closes that window; the
   * generation bump makes any still-in-flight older response a no-op. */
  private msgLoadGen = 0;

  private startFreshMessageLoad(group: any) {
    this.msgLoadGen++;
    this.searchAfterKeys = null;
    this.messageListscroll(group, this.currentIndex, this.pagination);
  }

  messageListscroll(group, currentIndex, pagination) {
    // Discard this load's response if a NEWER conversation open happens meanwhile.
    const gen = this.msgLoadGen;
    // Show the message skeleton only for the INITIAL load (empty list); during
    // scroll-up pagination the list already has items so the skeleton is gated out.
    this.isMessagesLoading = true;
    // let groupId = this.aroute.snapshot.queryParamMap.get('groupId');
    // let type = this.aroute.snapshot.queryParamMap.get('type');
    // let refererId = this.aroute.snapshot.queryParamMap.get('refererId');

    let groupId = this.userRedirect.groupId
    let type = this.userRedirect.type
    let refererId = this.userRedirect.refererId

    if (groupId == null && groupId == undefined) {
      groupId = refererId;
    }

    // Unread count at conversation open — captured BEFORE the wipe below discards
    // small counts. Drives the initial scroll position: >0 anchors the view at the
    // first unread message, 0 pins to the newest.
    const unseenAtOpen = this.currentIndex === 0 ? (Number(this.countAPI) || 0) : 0;
    let anchoredAtUnread = false;

    if (this.countAPI < 9) {
      this.countAPI = 0;
    }

    let url = "messages";
    let contact: any = {
      authorId: localStorage.getItem('userId'),
      recipientId: groupId,
      page: this.currentIndex,
      limit: this.pagination,
      date: this.sendMsgres.date,
      searchAfterKey: this.searchAfterKeys,
      messageType: type,

    };


    if (this.countAPI > 9 && this.searchAfterKeys == null) {
      contact.count = this.countAPI
    }


    if (type == "COMMUNITY" || type == "BUSINESS") {
      // Prefer the group this load was CALLED with (every fresh open passes the
      // real groupId). The shared sendMsgres.recipientId is written from many
      // flows and can be stale/wrong on the FIRST open of a business chat — the
      // messages query then hits the wrong id and the chat looks empty until a
      // re-click (which inherits the id the first open wrote). Scroll-up
      // pagination passes group=null and falls back to the shared field, which
      // by then is set by this conversation's own open.
      contact.recipientId = group || this.sendMsgres.recipientId;
    }

    if (this.searchAfterKeys != null && this.DataLength < 10) {
      this.isMessagesLoading = false;
    } else {
      // this._util.startLoader();
      this._api.messagePageService('POST', url, contact).subscribe(res => {
        // this._util.stopLoader();
        this.isMessagesLoading = false;
        // A newer conversation was opened while this request was in flight —
        // its messages and cursor belong to the OLD chat; drop them.
        if (gen !== this.msgLoadGen) { return; }
        let index = this.messageList.length - 1;
        if (res.data != null && this.searchAfterKeys != res.data.searchAfterKey) {
          this.searchAfterKeys = res.data.searchAfterKey;
          this.DataLength = res.data.messagesList.length;
          if (this.scrollDir) {
            this.scrollDir.prepareFor('up');
          }
          let messageListscrolldata = (res.data.messagesList == undefined || res.data.messagesList) == null ? [] : res.data.messagesList;
          if (messageListscrolldata.length != 0) {
            res.data.messagesList.forEach(res => {


              if (res.eventType == "ACTION") {

                const timezoneDatePipe = new TimezoneDatePipe();
                const dates = res.sentDate;
                const latest_date = timezoneDatePipe.transform(dates, 'custom');

                var tempMsg: any = [];
                var secondTemMsg: any = [];
                if (res.actionType == "ADDED_TO_NETWORK") {
                  if (res.authorId == this.userId) {
                    tempMsg = " You "
                  } else {
                    tempMsg = res.authorName;
                  }
                  if (res.recipientUserId == this.userId) {
                    secondTemMsg = " You "
                  } else {
                    secondTemMsg = res.recipientName;
                  }
                  res.oldMessage = tempMsg + " joined " + " on " + latest_date;
                }
                else if (res.actionType == "REMOVED_FROM_NETWORK") {
                  if (res.authorId == this.userId) {
                    tempMsg = " You "
                  } else {
                    tempMsg = res.authorName;
                  }
                  if (res.recipientUserId == this.userId) {
                    secondTemMsg = " You "
                  } else {
                    secondTemMsg = res.recipientName;
                  }
                  res.oldMessage = tempMsg + " removed " + secondTemMsg + " on " + latest_date;
                } else if (res.actionType == "LEFT_NETWORK") {
                  if (res.authorId == this.userId) {
                    tempMsg = " You "
                  } else {
                    tempMsg = res.authorName;
                  }
                  if (res.recipientUserId == this.userId) {
                    secondTemMsg = " You "
                  } else {
                    secondTemMsg = res.recipientName;
                  }
                  res.oldMessage = tempMsg + " left " + " on " + latest_date;
                } else if (res.actionType == "ADDED_TO_TEAM") {
                  if (res.authorId == this.userId) {
                    tempMsg = " You "
                  } else {
                    tempMsg = res.authorName;
                  }
                  if (res.recipientUserId == this.userId) {
                    secondTemMsg = " You "
                  } else {
                    secondTemMsg = res.recipientName;
                  }
                  res.oldMessage = tempMsg + " joined " + " on " + latest_date;
                } else if (res.actionType == "REMOVED_FROM_TEAM") {
                  if (res.authorId == this.userId) {
                    tempMsg = " You "
                  } else {
                    tempMsg = res.authorName;
                  }
                  if (res.recipientUserId == this.userId) {
                    secondTemMsg = " You "
                  } else {
                    secondTemMsg = res.recipientName;
                  }
                  res.oldMessage = tempMsg + " removed " + secondTemMsg + " on " + latest_date;
                } else if (res.actionType == "LEFT_TEAM") {
                  if (res.authorId == this.userId) {
                    tempMsg = " You "
                  } else {
                    tempMsg = res.authorName;
                  }
                  if (res.recipientUserId == this.userId) {
                    secondTemMsg = " You "
                  } else {
                    secondTemMsg = res.recipientName;
                  }
                  res.oldMessage = tempMsg + " left " + " on " + latest_date;
                } else if (res.actionType == "ADDED_TO_MSG_GRP") {
                  if (res.authorId == this.userId) {
                    tempMsg = " You "
                  } else {
                    tempMsg = res.authorName;
                  }
                  if (res.recipientUserId == this.userId) {
                    secondTemMsg = " You "
                  } else {
                    secondTemMsg = res.recipientName;
                  }
                  res.oldMessage = tempMsg + " added  " + secondTemMsg + " on " + latest_date;
                } else if (res.actionType == "REMOVED_FROM_MSG_GRP") {
                  if (res.authorId == this.userId) {
                    tempMsg = " You "
                  } else {
                    tempMsg = res.authorName;
                  }
                  if (res.recipientUserId == this.userId) {
                    secondTemMsg = " You "
                  } else {
                    secondTemMsg = res.recipientName;
                  }
                  res.oldMessage = tempMsg + " removed " + secondTemMsg + " on " + latest_date;
                } else if (res.actionType == "LEFT_FROM_MSG_GRP") {

                  if (res.authorId == this.userId) {
                    tempMsg = " You "
                  } else {
                    tempMsg = res.authorName;
                  }
                  if (res.recipientUserId == this.userId) {
                    secondTemMsg = " You "
                  } else {
                    secondTemMsg = res.recipientName;
                  }


                  res.oldMessage = tempMsg + " left " + " on " + latest_date;
                } else if (res.actionType == "ENABLED_RIGHTS") {
                  if (res.authorId == this.userId) {
                    tempMsg = " You "
                  } else {
                    tempMsg = res.authorName;
                  }
                  if (res.recipientUserId == this.userId) {
                    secondTemMsg = " You "
                  } else {
                    secondTemMsg = res.recipientName;
                  }
                  res.oldMessage = tempMsg + " enabled chat rights for " + secondTemMsg + " on " + latest_date;
                } else if (res.actionType == "DISABLED_RIGHTS") {
                  if (res.authorId == this.userId) {
                    tempMsg = " You "
                  } else {
                    tempMsg = res.authorName;
                  }
                  if (res.recipientUserId == this.userId) {
                    secondTemMsg = " You "
                  } else {
                    secondTemMsg = res.recipientName;
                  }
                  res.oldMessage = tempMsg + " disabled  chat rights for " + secondTemMsg + " on " + latest_date;
                }

                else if (this.SelectedContact.groupType == "NETWORK" && res.actionType == "DEACTIVATED") {
                  res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                }

                else if (this.SelectedContact.groupType == "TEAM" && res.actionType == "DEACTIVATED") {
                  res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                }

                else if (this.SelectedContact.groupType == "BUSINESS" && res.actionType == "DEACTIVATED") {
                  res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                }

                else if (this.SelectedContact.groupType == "COMMUNITY" && res.actionType == "DEACTIVATED") {
                  res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                }

                else {
                  res.oldMessage = res.message + " on " + latest_date;

                }
              }

              if (res.deleted) {
                res.message = "This message was deleted";
                res.attachments = null;
              }


              this.messageList.unshift(res);
            })

            // Conversation open (not up-scroll pagination): render the new messages
            // and position IN THE SAME TASK, so the browser's first paint is already
            // at the right place (painting top-anchored and jumping 400ms later was
            // a visible glitch). With unread messages the view anchors at the FIRST
            // unread one (clamped by the browser, so a couple of unread near the
            // bottom just land at the bottom); otherwise it pins to the newest.
            if (this.currentIndex === 0) {
              this.cdr.detectChanges();
              if (this.chartDisc && this.chartDisc.nativeElement) {
                const el = this.chartDisc.nativeElement;
                let anchor: any = null;
                if (unseenAtOpen > 0 && unseenAtOpen <= this.messageList.length) {
                  const firstUnread = this.messageList[this.messageList.length - unseenAtOpen];
                  anchor = firstUnread && firstUnread.messageId ? document.getElementById(firstUnread.messageId) : null;
                }
                if (anchor) {
                  el.scrollTop = Math.max(0, anchor.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop - 8);
                  anchoredAtUnread = true;
                } else {
                  el.scrollTop = el.scrollHeight;
                }
              }
            }

            // Up-scroll pagination: put the view back BEFORE the browser paints.
            // The browser's own scroll anchoring holds the position on a slow
            // scroll (scrollTop still > 0), but a fast scroll lands at the very
            // top where anchoring is suppressed — and the only correction, the
            // restore() in the 400ms timeout below, arrives far too late to hide
            // the trip to the oldest message. rAF runs after Angular has rendered
            // the prepended messages and before the first paint; restore() itself
            // is unchanged (it then no-ops below, having cleared toReset).
            if (this.currentIndex !== 0 && this.scrollDir) {
              requestAnimationFrame(() => this.scrollDir.restore());
            }

            setTimeout(() => {



              if (this.showbottom == false) {

                setTimeout(() => {

                  // element.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
                  this.showbottom = true;
                  ////"inside",this.showbottom);
                  ////" Id data New Values : ",id);
                  // this.scrollDir.restore();
                  if (this.countAPI > 9 && this.chartDisc && this.chartDisc.nativeElement) {
                    this.chartDisc.nativeElement.scroll({
                      behavior: "smooth", block: "top", inline: "nearest"
                    });
                    const id = res.data.messagesList[0].messageId;
                    const element = document.getElementById(id);
                    // element.style.scrollPaddingBottom ="100px";

                    //setTimeout(() => {
                    //  const id = res.data.messagesList[3].messageId;
                    // const elementnew = document.getElementById(id);
                    // elementnew.style.scrollPaddingTop = "100px";

                    // },300);
                  }
                }, 400);

              } else {
                if (this.currentIndex === 0 && !anchoredAtUnread) {
                  // Entering / switching a conversation → land on the newest message.
                  this.updateScoll();
                } else if (this.scrollDir) {
                  // Up-scroll pagination → preserve the current position.
                  this.scrollDir.restore();
                }
              }

            }, 400);
          }
        }

        setTimeout(() => {
          // if( this.countAPI==0){
          // if (this.currentIndex == 0) {
          // this.chartDisc.nativeElement.scroll({
          //   behavior: "smooth", block: "end", inline: "nearest"
          // });
          //  }
          // }
        }, 200);


        if (this.messageList && this.messageList.length > 0) {
          this.updateNonSeenMessage(this.messageList);
        }


      }, err => {
        this._util.stopLoader();
        this.isMessagesLoading = false;
      });
    }


  }
  ngAfterViewChecked() {
    ////this.showbottom )
    // if(this.showbottom){
    // this.scrollToBottom();
    // }
  }
  scrollToBottom(): void {
    try {
      if (this.scrollDir) {
        this.scrollDir.restore();
      }
    } catch (err) { }
  }

  // Reliably pin the conversation to the newest message (same approach as the
  // bottom chat widget's updateScoll). Deferred a tick so the just-rendered
  // messages are in the DOM and scrollHeight is the final height.
  private updateScoll(): void {
    if (!this.chartDisc) return;
    setTimeout(() => {
      if (this.chartDisc && this.chartDisc.nativeElement) {
        const container: HTMLElement = this.chartDisc.nativeElement;
        container.scrollTop = container.scrollHeight;
      }
    }, 0);
  }

  duplicate(data) {
    var temp = [];
    var arr = data.filter(function (el) {
      // If it is not a duplicate, return true
      if (temp.indexOf(el.groupId) == -1) {
        temp.push(el.groupId);
        return true;
      }
      return false;
    });
    return arr;
  }




  // thiru changes

  // to get group Data
  contact = {
    createdBy: null,
    groupId: null,
    groupType: null,
    searchAfterKey: null,
    limit: 10
  };
  previousSearchAfterKey: any = null;

  groupMembersList = [];
  getGroupMembers(data, template, openModal, payload = this.contact) {
    const groupType = this.aroute.snapshot.queryParamMap.get('type') || this.SelectedContact.groupType;
    Object.assign(this.contact, {
      groupId: this.SelectedContact.groupId,
      createdBy: this.SelectedContact.createdBy,
      groupType: groupType,
    });
    this.groupUserList = [];
    this._util.startLoader('msgspinner');
    this._api.messagePageService('POST', "message/group/getGroupMembers", payload).subscribe(res => {
      this._util.stopLoader('msgspinner');

      if (res.code === GigsumoConstants.SUCESSCODE) {
        const { data } = res;
        const key = res.data.searchAfterKey && res.data.searchAfterKey[0];
        const newMembers = data.groupMembers.filter((newMember) =>
          !this.groupUserList.some((existingMember) => existingMember.userId === newMember.userId)
        );

        if (this.previousSearchAfterKey === key || (data.groupMembers && data.groupMembers.length === 0)) {
          this.stopScroll1 = true;
        }

        if (this.previousSearchAfterKey !== key || (data.groupMembers && data.groupMembers.length > 0)) {
          this.groupUserList = [...this.groupUserList, ...newMembers];
          this.TempGroupUserList = data;
        }
        this.previousSearchAfterKey = key;
        if (this.modalRef) {
          this.modalRef.hide();
        }
        if (this.modalRefAddgroup) {
          this.modalRefAddgroup.hide();
        }
        if (openModal != undefined && openModal != null && openModal != '' && openModal) {
          this.modalRef = this.modalService.show(template, Object.assign({}, this.modalOptions));
          document.body.classList.add('body-no-scroll');
        }
      }
    }, err => this._util.stopLoader());
  }



  handleKeyUp2(event: KeyboardEvent | any) {
    // Ignore modifier keys and special keys
    const ignoredKeys = [
      'Alt', 'AltGraph', 'CapsLock', 'Control', 'Fn', 'FnLock',
      'Hyper', 'Meta', 'NumLock', 'ScrollLock', 'Shift', 'Super',
      'Symbol', 'SymbolLock', 'Tab', 'ArrowDown', 'ArrowLeft',
      'ArrowRight', 'ArrowUp', 'End', 'Home', 'PageDown', 'PageUp',
      'Escape', 'PrintScreen', 'Insert', 'Delete'
    ];

    // Check if the key is not in the ignored list
    if (!ignoredKeys.includes(event.key) && !event.ctrlKey && !event.altKey) {
      // this.searchgetMembersForGroup();
      this.onSearchInputChange(event);
    }
  }


  stopScroll1: boolean = false
  LoadmoreGroupMembers() {

    const groupType = this.aroute.snapshot.queryParamMap.get('type') || this.SelectedContact.groupType;
    Object.assign(this.contact, {
      groupId: this.SelectedContact.groupId,
      createdBy: this.SelectedContact.createdBy,
      groupType: groupType,
      limit: 10,
      searchContent: this.searchContent === '' ? null : this.searchContent,
    });
    this._util.startLoader('msgspinner');
    this._api.messagePageService('POST', "message/group/getGroupMembers", this.contact).subscribe(res => {
      this._util.stopLoader('msgspinner');
      if (res.code === GigsumoConstants.SUCESSCODE) {
        const { data } = res;
        const key = res.data.searchAfterKey && res.data.searchAfterKey[0];
        if (this.previousSearchAfterKey === key || (data.groupMembers && data.groupMembers.length == 0)) {
          this.stopScroll1 = true
        }

        if (this.previousSearchAfterKey !== key) {
          if (this.searchContent != '') {
            this.groupUserList = data.groupMembers;
          } else {
            this.groupUserList = [
              ...this.groupUserList,
              ...data.groupMembers.filter((newMember) => !this.groupUserList.some((existingMember) => existingMember.userId === newMember.userId))];
            this.TempGroupUserList = data;
          }
        }
        this.previousSearchAfterKey = key;


      }
    }, err => this._util.stopLoader());
  }


  changeBusiness(status) {

    var temp = [];
    if (status == "COMMUNITY") {
      this.groupCommunityListMembers.forEach(element => {
        var status;
        if (element.checked == true) {
          status = "ENABLED"
        } else if (element.checked == false) {
          status = "DISABLED"
        }
        temp.push({
          "userId": element.userId,
          "status": status
        });

      });

    } else if (status == "BUSINESS") {
      this.groupBusinessListMembers.forEach(element => {
        var status;
        if (element.checked == true) {
          status = "ENABLED"
        } else if (element.checked == false) {
          status = "DISABLED"
        }
        temp.push({
          "userId": element.userId,
          "status": status
        });

      });
    }


    let url = "message/changeChatStatus";
    let contact = {
      messageContacts: temp,
      groupId: this.SelectedContact.groupId,
    };
    this._util.startLoader('msgspinner');
    this._api.messagePageService('POST', url, contact).subscribe(res => {
      this._util.stopLoader('msgspinner');
      if (res.code == "00000") {
        this.modalRef.hide();
        Swal.fire({
          position: 'center',
          icon: 'success',
          title: 'Updated Successfully',
          showConfirmButton: false,
          timer: 1500
        })
      }
    }, error => {
      this._util.stopLoader();
    });


  }




  userData(data, template?: TemplateRef<any>, businessGrouptemplate?: TemplateRef<any>, communityGrouptemplate?: TemplateRef<any>, network?: TemplateRef<any>) {

    // Debounce rapid double-clicks on the Edit button so we don't fire two
    // getGroupMembers calls / open two edit panels.
    const nowTs = Date.now();
    if (this.lastUserDataAt && (nowTs - this.lastUserDataAt) < 500) {
      return;
    }
    this.lastUserDataAt = nowTs;

    if (this.SelectedContact.groupType == "GROUP" && this.SelectedContact.status == "ACTIVE") {
      this.groupNameshow = data.groupName;
      const groupType = this.aroute.snapshot.queryParamMap.get('type') || this.SelectedContact.groupType;
      Object.assign(this.contact, {
        groupId: this.SelectedContact.groupId,
        createdBy: this.SelectedContact.createdBy,
        groupType: groupType,
        searchContent: null,
        searchAfterKey: null
      });
      this.getGroupMembers(data, template, true, this.contact)
    }
    else if (this.SelectedContact.groupType == "BUSINESS" && this.SelectedContact.status == "ACTIVE") {
      this.searchBusgroupList = ""
      if (this.SelectedContact.adminList[0] == localStorage.getItem('userId')) {
        this.BusinessGrouplist(data, businessGrouptemplate, true);
      } else if (this.SelectedContact.adminList.includes(localStorage.getItem('userId'))) {
        this.BusinessGrouplist(data, businessGrouptemplate, true);
      }
    } else if (this.SelectedContact.groupType == "COMMUNITY" && this.SelectedContact.status == "ACTIVE") {

      this.searchBusgroupList = ""
      if (this.SelectedContact.adminList[0] == localStorage.getItem('userId')) {
        this.communityGroupList(data, businessGrouptemplate, true);
      } else if (this.SelectedContact.adminList.includes(localStorage.getItem('userId'))) {
        this.communityGroupList(data, businessGrouptemplate, true);
      }
    } else if (this.SelectedContact.groupType == "NETWORK" && this.SelectedContact.status == "ACTIVE" || this.SelectedContact.groupType == "TEAM" && this.SelectedContact.status == "ACTIVE") {
      this.groupNameshow = data.groupName;
      // this.getTeamMembers(data, network, true);
      this.getGroupMembers(data, template, true)



    }
  }


  addUserToBusiness(event: any, userId: any, item) {

    if (event.target.checked) {
      item.checked = true
      this.groupDataBusiness.contacts.push(item.userId);
    } else {
      item.checked = false
      for (var i = 0; i < this.groupDataBusiness.contacts.length; i++) {
        if (!item.checked && this.groupDataBusiness.contacts[i] == item.userId) {
          if (this.groupDataBusiness.contacts.length != 0) {
            this.groupDataBusiness.contacts.splice(i, 1);
          }
        }
      }
    }
  }

  addUserTocommunity(event: any, userId: any, item) {
    if (event.target.checked) {
      item.checked = true
      this.groupDataCommunity.contacts.push(item.userId);
    } else {
      item.checked = false;
      for (var i = 0; i < this.groupDataCommunity.contacts.length; i++) {
        if (!item.checked && this.groupDataCommunity.contacts[i] == item.userId) {
          if (this.groupDataCommunity.contacts.length != 0) {
            this.groupDataCommunity.contacts.splice(i, 1);
          }
        }
      }
    }
  }

  BusinessGrouplist(data, template, openModal) {
    let url = "message/group/getGroupMembers";
    let contact = {
      createdBy: this.SelectedContact.createdBy,
      groupType: this.SelectedContact.groupType,
      groupId: this.SelectedContact.groupId,
      limit: 10,
      searchAfterKey: null
    };
    this.groupDataBusiness.contacts = []
    this._util.startLoader('msgspinner');
    this._api.messagePageService('POST', url, contact).subscribe(res => {
      this._util.stopLoader('msgspinner');
      if (res) {
        res.data.groupMembers.forEach(element => {
          element.userName = element.firstName + " " + element.lastName;
          if (element.profileStatus == "ENABLED" || element.profileStatus == "NEW") {
            element.checked = true;
            this.groupDataBusiness.contacts.push(element.userId);
            if (element.superAdmin == true) {
              element.disable = true;
            } else if (element.superAdmin == false) {
              element.disable = false;
            }
            if (this.SelectedContact.adminList[0] != localStorage.getItem('userId') && element.superAdmin == false) {
              element.disable = true;
            }

          }

          else if (element.profileStatus == "DISABLED") {
            element.checked = false;
            element.disable = true;
            if (this.SelectedContact.adminList[0] == localStorage.getItem('userId')) {
              element.disable = false;
            }


          }

        });
        const key = res.data.searchAfterKey && res.data.searchAfterKey[0];
        if (this.previousSearchAfterKey === key || (data.groupMembers && data.groupMembers.length == 0)) {
          this.stopScroll1 = true
        }
        if (this.previousSearchAfterKey !== key) {
          this.groupBusinessListMembers = res.data.groupMembers;
          this.tempBusinessList = res.data.groupMembers;
        }
        this.previousSearchAfterKey = key;
        if (openModal != undefined && openModal != null && openModal != '' && openModal) {
          this.modalRef = this.modalService.show(template, Object.assign({}, this.modalOptions));
        }
      }
    }, err => {
      this._util.stopLoader();
    });
  }





  communityGroupList(data, template, openModal) {
    let url = "message/group/getGroupMembers";
    let contact = {
      createdBy: this.SelectedContact.createdBy,
      groupType: this.SelectedContact.groupType,
      groupId: this.SelectedContact.groupId,
    };
    this.groupDataCommunity.contacts = [];
    this._util.startLoader('msgspinner');
    this._api.messagePageService('POST', url, contact).subscribe(res => {
      this._util.stopLoader('msgspinner');
      if (res) {
        res.groupMembers.forEach(element => {
          element.userName = element.firstName + " " + element.lastName;
          if (element.profileStatus == "ENABLED" || element.profileStatus == "NEW") {
            element.checked = true;
            this.groupDataCommunity.contacts.push(element.userId);
            if (element.superAdmin == true) {
              element.disable = true;
            } else if (element.superAdmin == false) {
              element.disable = false;
            }
            if (this.SelectedContact.adminList[0] != localStorage.getItem('userId') && element.superAdmin == false) {
              element.disable = true;
            }

          }
          else if (element.profileStatus == "DISABLED") {
            element.checked = false;
            element.disable = true;
            if (this.SelectedContact.adminList[0] == localStorage.getItem('userId')) {
              element.disable = false;
            }
          }

        });

        this.groupCommunityListMembers = res.groupMembers;
        this.tempCommunityList = res.groupMembers;
        if (openModal != undefined && openModal != null && openModal != '' && openModal) {
          this.modalRef = this.modalService.show(template, Object.assign({}, this.modalOptions));
        }
      }
    }, err => {
      this._util.stopLoader();
    });
  }





  deletechatConfirm(SelectedContact) {
    let datas: any = {};
    datas.userId = localStorage.getItem('userId')
    datas.groupType = this.SelectedContact.groupType;
    datas.groupId = this.SelectedContact.groupId;
    datas.refererId = this.SelectedContact.refererId;

    Swal.fire({
      title: "Are you sure want to delete chat history?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#DC2626', // --red (destructive action)
      cancelButtonColor: '#6E6880',  // --gs-gray-500 (neutral)
      reverseButtons: true,
      allowOutsideClick: false
    }).then((result) => {
      if (result.isConfirmed) {
        this.deleteAPIfunction()
      }
    })
  }

  deleteAPIfunction() {
    let datas: any = {};
    datas.userId = localStorage.getItem('userId')
    datas.groupType = this.SelectedContact.groupType;
    datas.groupId = this.SelectedContact.groupId;
    datas.refererId = this.SelectedContact.refererId;

    this._util.startLoader('msgspinner');
    this._api.create("message/deleteChat", datas).subscribe((res) => {
      this._util.stopLoader('msgspinner');
      if (res.code == "00000") {
        // this.route.navigate(['/message']);
        // this.SelectedContact = {};

        // this.reloadcontact()
        this.ngOnInit()

      }
    }, err => {
      this._util.stopLoader();
      if (err.status == 500) {
        this._util.stopLoader();
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: 'Something went wrong while deleting chats. Please, try again later.',
          showDenyButton: false,
          confirmButtonText: `ok`,
        })
      }
    })
  }


  deleteChat(SelectedContact) {
    let datas: any = {};
    datas.userId = localStorage.getItem('userId')
    datas.groupType = this.SelectedContact.groupType;
    datas.groupId = this.SelectedContact.groupId;

    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: "btn btn-success",
        cancelButton: "btn btn-danger",
      },
      buttonsStyling: false,
    });
    swalWithBootstrapButtons.fire({
      // title: 'Are you sure?',
      title: "Do you want to delete Chat history?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      reverseButtons: true,
      allowOutsideClick: false,
    }).then((result) => {
      if (result.isConfirmed) {
        // this._util.startLoader('msgspinner');
        // this._api.create("message/deleteChat", datas).subscribe((res) => {
        //   if (res.code == '00000') {
        //     this.route.navigate(['/message']);
        //   }
        // })
        this.deletechatConfirm(this.SelectedContact)
      }
    })
  }
  onsearch(searchtext) {

    if (searchtext != '') {
      searchtext = searchtext.trim().toLowerCase();
      this.newContactList = this.tempNewContactList;
      this.newuserGroup = this.tempNewuserGroup;
      this.newContactList = this.filterByString(this.newContactList, searchtext);
      this.page = 1;
      if (this.newContactList.length == 0) {
        this.paginationFlag = false;
      } else if (this.newContactList.length != 0) {
        this.paginationFlag = true;
      }
    } else {
      this.newContactList = this.tempNewContactList;
      this.paginationFlag = true;

    }
  }
  searchContent: string = '';
  performSearch() {
    if (this.skipNextSearch) {
      return;
    }

    if (this.searchContent.trim() !== '') {
      this.contact.searchAfterKey = null;
      this.previousSearchAfterKey = null;
      this.searchandscrollconatcts();
    } else {
      this.contact.searchAfterKey = null;
      this.previousSearchAfterKey = null;
      this.searchandscrollconatcts();
    }
    // this.stopScroll = false;
    this.stopScroll1 = false;
  }
  onScrollcontacts() {

    if (!this.stopScroll1) {
      this.contact.searchAfterKey = this.previousSearchAfterKey === null ? null
        : [this.previousSearchAfterKey];
      this.searchandscrollconatcts();
    }
  }

  private searchSubject = new Subject<string>();
  private searchSubscription: Subscription;
  private searchBusSubscription: Subscription;
  searchgetMembersForGroup() {
    this.searchSubject.next(this.searchContent);
  }

  handlePaste2(event: ClipboardEvent) {
    // Short delay to ensure the pasted content is in the input field
    setTimeout(() => {
      // Access the input value after the paste event is processed
      const inputElement = event.target as HTMLInputElement;
      this.searchSubjectBusiness.next(inputElement.value);
    }, 10);
  }


  onsearchGroupmember(searchtext) {

    if (searchtext != '') {
      searchtext = searchtext.trim().toLowerCase();
      this.newuserGroup = this.tempNewuserGroup;
      this.newuserGroup = this.filterByStringGroupName(this.newuserGroup, searchtext);
      this.page = 1;
      if (this.newuserGroup.length == 0) {
        this.paginationFlag = false;
      } else if (this.newuserGroup.length != 0) {
        this.paginationFlag = true;
      }
    } else {
      this.newuserGroup = this.tempNewuserGroup;
      this.paginationFlag = true;
    }
  }

  onsearchgroupmembers() {
    if (this.searchContent.trim() !== '') {
      this.contact.searchAfterKey = null
      this.previousSearchAfterKey = null
      this.LoadmoreGroupMembers();
    } else {
      this.groupUserList = [];
      this.contact.searchAfterKey = null
      this.previousSearchAfterKey = null
      this.LoadmoreGroupMembers();
    }
    // this.stopScroll = false;
    this.stopScroll1 = false;
  }

  onsearchnetworkmember(searchtext) {

    if (searchtext != '') {
      searchtext = searchtext.trim().toLowerCase();
      this.groupUserList = this.TempGroupUserList.groupMembers;
      this.groupUserList = this.filterByStringbybusiness(this.groupUserList, searchtext);

      this.page = 1;

      if (this.groupUserList.length == 0) {
        this.paginationFlag = false;
      } else if (this.groupUserList.length != 0) {
        this.paginationFlag = true;
      }

    } else {
      this.groupUserList = this.TempGroupUserList.groupMembers;
      this.paginationFlag = true;
    }
  }


  onsearchMsg(searchtext) {
    if (searchtext != '') {
      searchtext = searchtext.trim().toLowerCase();
      this.groupBusinessListMembers = this.tempBusinessList;
      this.groupBusinessListMembers = this.filterByStringbybusiness(this.groupBusinessListMembers, searchtext);
      this.page = 1;
      if (this.groupBusinessListMembers.length == 0) {
        this.paginationFlag = false;
      } else if (this.groupBusinessListMembers.length != 0) {
        this.paginationFlag = true;

      }
    } else {
      this.groupBusinessListMembers = this.tempBusinessList;
      this.paginationFlag = true;

    }

  }
  getInitialstwo(firstname: string, lastname: string): string {
    return this.JobServicecolor.getInitialsparam(firstname, lastname);
  }

  getColortwo(firstname: string, lastname: string): string {
    return this.JobServicecolor.getColorparam(firstname, lastname);
  }
  getTextColor(firstname: string, lastname: string): string {
    return this.JobServicecolor.getTextColor(firstname, lastname);
  }

  getFullnameTextColor(fullname: string): string {
    return this.JobServicecolor.getFullnameTextColor(fullname);
  }


  onsearchMsgCommunity(searchtext) {
    if (searchtext != '') {
      searchtext = searchtext.trim().toLowerCase();
      this.groupCommunityListMembers = this.tempCommunityList;
      this.groupCommunityListMembers = this.filterByStringbybusiness(this.groupCommunityListMembers, searchtext);
      this.page = 1;

      if (this.groupCommunityListMembers.length == 0) {
        this.paginationFlag = false;
      } else if (this.groupCommunityListMembers.length != 0) {
        this.paginationFlag = true;
      }
    } else {
      this.groupCommunityListMembers = this.tempCommunityList;
      this.paginationFlag = true;
    }
  }
  filterByStringbybusiness(data: any[], s: string) {
    if (!data || !Array.isArray(data)) {
      console.error("Invalid data array:", data);
      return [];
    }

    data.forEach(element => {
      element.userName = element.firstName + " " + element.lastName;
    });

    return data.filter(e => e.userName.toLowerCase().includes(s))
      .sort((a, b) => a.userName.toLowerCase().includes(s) && !b.userName.toLowerCase().includes(s) ? -1 : b.userName.toLowerCase().includes(s) && !a.userName.toLowerCase().includes(s) ? 1 : 0);
  }


  filterByString(data, s) {

    return data.filter(e => e.groupName.toLowerCase().includes(s))
      .sort((a, b) => a.groupName.toLowerCase().includes(s) && !b.groupName.toLowerCase().includes(s) ? -1 : b.groupName.toLowerCase().includes(s) && !a.groupName.toLowerCase().includes(s) ? 1 : 0);
  }

  filterByStringGroupName(data, s) {

    data.forEach(element => {
      element.userName = "";
      element.userName = element.firstName + " " + element.lastName;
    });

    return data.filter(e => e.userName.toLowerCase().includes(s))
      .sort((a, b) => a.userName.toLowerCase().includes(s) && !b.userName.toLowerCase().includes(s) ? -1 : b.userName.toLowerCase().includes(s) && !a.userName.toLowerCase().includes(s) ? 1 : 0);
  }


  filterByStringNetwork(data, s) {

    data.forEach(element => {
      element.userName = "";
      element.userName = element.firstName + " " + element.lastName;
    });

    return data.filter(e => e.userName.toLowerCase().includes(s))
      .sort((a, b) => a.userName.toLowerCase().includes(s) && !b.userName.toLowerCase().includes(s) ? -1 : b.userName.toLowerCase().includes(s) && !a.userName.toLowerCase().includes(s) ? 1 : 0);
  }

  handlePaste(event: ClipboardEvent) {
    // Short delay to ensure the pasted content is in the input field before search
    setTimeout(() => {
      this.onsearchgroupmembers();
    }, 10);
  }

  handlePaste1(event: ClipboardEvent) {
    // Short delay to ensure the pasted content is in the input field before search
    setTimeout(() => {
      this.searchgetMembersForGroup();
    }, 10);
  }



  searchandscrollconatcts() {
    let url = "message/group/getMembersForGroup";
    Object.assign(this.contact, {
      userId: localStorage.getItem('userId'),
      groupId: this.SelectedContact.groupId,
      limit: 10,
      searchContent: this.searchContent === '' ? null : this.searchContent,
    });

    this._util.startLoader('msgspinner');
    this._api.messagePageService('POST', url, this.contact).subscribe(res => {
      this._util.stopLoader('msgspinner');
      if (res) {
        // Process new connections and preserve checked state
        res.data.connections.forEach(element => {
          element.groupName = element.firstName + " " + element.lastName;
          element.groupId = element.userId;

          // Check if this contact is already selected in groupData OR in this.addusers
          element.checked = this.groupData.contacts.includes(element.groupId) ||
            this.addusers.includes(element.userId);
        });

        const key = res.data.searchAfterKey && res.data.searchAfterKey[0];
        if (this.previousSearchAfterKey === key || (res.data.connections && res.data.connections.length == 0)) {
          this.stopScroll1 = true;
        }

        if (this.previousSearchAfterKey !== key) {
          if (this.searchContent != '') {
            // For searches, completely replace the list
            this.newContactList = res.data.connections;
            this.tempNewContactList = res.data.connections;
            this.newuserGroup = res.data.connections;
            this.tempNewuserGroup = res.data.connections;
          } else {
            // When the search is cleared, start fresh to avoid duplicates
            if (this.contact.searchAfterKey === null) {
              // First page of results
              this.newContactList = res.data.connections;
              this.newuserGroup = res.data.connections;
              this.tempNewuserGroup = res.data.connections;
              this.tempNewContactList = res.data.connections;
            } else {
              // For subsequent pages when scrolling with empty search
              // Create a map of existing users by ID to prevent duplicates
              const existingUsers = {};
              this.newuserGroup.forEach(user => {
                existingUsers[user.userId] = true;
              });

              // Only add non-duplicate users
              const newConnections = res.data.connections.filter(user => !existingUsers[user.userId]);
              this.newContactList = this.newContactList.concat(newConnections);
              this.newuserGroup = this.newuserGroup.concat(newConnections);
              this.tempNewuserGroup = this.tempNewuserGroup.concat(newConnections);
              this.tempNewContactList = this.tempNewContactList.concat(newConnections);
            }
          }
        }
        this.previousSearchAfterKey = key;
      }
    });
  }

  addcontact(openModal: any, templates?: TemplateRef<any>, businesstemplates?: TemplateRef<any>, communityMembertemplate?: TemplateRef<any>) {
    // Debounce rapid double-clicks (e.g. the Add-members button) so we don't fire
    // two async opens and stack two panels (whose leftover backdrop then blocks
    // the Edit panel underneath). Sequential open→close→open is always >400ms apart.
    const nowTs = Date.now();
    if (this.lastAddcontactAt && (nowTs - this.lastAddcontactAt) < 500) {
      return;
    }
    this.lastAddcontactAt = nowTs;

    // Private/Group picker: never open a second slider while one is already up.
    // The debounce alone can't guarantee this — the panel opens inside the API
    // response, so two slow clicks fire two fetches whose responses each showed
    // a modal. Count-based (no flag that could stick and dead-click the button);
    // scoped to the create flows so the edit→Add-members nesting still works.
    if (openModal === true && this.modalService.getModalsCount() > 0) {
      return;
    }

    if (this.SelectedContact.groupType == 'GROUP' || openModal == true) {
      let url = "message/group/getMembersForGroup";
      let contact = {
        userId: localStorage.getItem('userId'),
        groupId: this.SelectedContact.groupId,
        limit: 10,
        searchAfterKey: null,

      };
      this.newContactList = [];
      this._util.startLoader('msgspinner');
      this._api.messagePageService('POST', url, contact).subscribe(res => {
        this._util.stopLoader('msgspinner');
        if (res && res.data && res.data.connections) {
          this.newContactList = res.data.connections;
          res.data.connections.forEach(element => {
            element.checked = false;
            element.groupName = element.firstName + " " + element.lastName;
            element.groupId = element.userId;
          });
          const key = res.data.searchAfterKey && res.data.searchAfterKey[0];
          if (this.previousSearchAfterKey === key || (res.data.connections && res.data.connections.length === 0)) {
            this.stopScroll1 = true;
          }

          if (this.previousSearchAfterKey !== key) {
            this.newuserGroup = res.data.connections;
            this.tempNewuserGroup = res.data.connections;
            this.newContactList = res.data.connections;
            this.tempNewContactList = res.data.connections;
          }

          this.previousSearchAfterKey = key;
          // Open the modal whenever there's a template to show. The create flows
          // (openModal === true) keep the original "only if the list has items"
          // guard; the Add-members flow (a TemplateRef) always opens — its template
          // has its own search + "No data" state so the user can look people up
          // even when the initial connections list comes back empty.
          // A group chat needs 2 picked members, so at least 2 connections;
          // a private chat needs just 1.
          const minConnections = this.pickerMode === 'group' ? 2 : 1;
          if (openModal != undefined && openModal != null && openModal != '' && openModal
            && (openModal === true ? this.newContactList.length >= minConnections : true)
            // In-flight double-open guard: an earlier click's response may have
            // opened the picker while THIS fetch was still on the wire.
            && !(openModal === true && this.modalService.getModalsCount() > 0)) {
            // All messaging modals slide in (the class is on the shared modalOptions).
            this.modalRef = this.modalService.show(templates, Object.assign({}, this.modalOptions));

          }
          // Create flows without enough connections: the panel intentionally
          // doesn't open, but the user must be told why (a silent no-op made the
          // Private/Group Chat buttons look dead).
          if (openModal === true && this.newContactList.length < minConnections) {
            if (this.pickerMode === 'group') {
              Swal.fire({
                position: 'center',
                icon: 'info',
                title: 'Add more connections',
                text: 'Add at least 2 connections to create a group chat.',
                showConfirmButton: false,
                timer: 3000
              });
            } else {
              Swal.fire({
                position: 'center',
                icon: 'info',
                title: 'No connections yet',
                text: 'Connect with a user first to start a conversation.',
                showConfirmButton: false,
                timer: 3000
              });
            }
          }
          if (res.data.connections.length != 0 && !openModal) {
            this.modalRefAddgroup = this.modalService.show(templates, Object.assign({}, this.modalOptions));
          }
          // else {
          //   Swal.fire({
          //     position: 'center',
          //     icon: 'info',
          //     title: "New user unavailable",
          //     text: 'Please connect with the user first and then try again.',
          //     showConfirmButton: false,
          //     timer: 5000
          //   })
          // }
        }
      })
    } else if (this.SelectedContact.groupType == 'BUSINESS') {
      this.BusinessGrouplist("data", businesstemplates, true);
    } else if (this.SelectedContact.groupType == 'COMMUNITY') {
      this.communityGroupList("data", communityMembertemplate, true);
    }
  }

  closeAdduserModel() {
    this.searchContent = '';
    // this.stopScroll = false;
    this.stopScroll1 = false;
    this.contact.searchAfterKey = null;
    this.previousSearchAfterKey = null;
    this.skipNextSearch = true
    this.searchSubject.next('');
    this.searchSubjectBusiness.next('');
    setTimeout(() => {
      this.skipNextSearch = false
    }, 2000);

    this.newuserGroup = [];
    // Close ONLY the add-user panel (with slide-out). When opened from inside Edit
    // it's modalRefAddgroup (the Edit panel underneath is a separate ref and must
    // stay open); when opened standalone from the header it's modalRef.
    // (The old `modalService.hide(1)` closed whatever modal had id=1, which was
    // the Edit panel in the nested case — closing both.)
    this.animateSlideClose(() => {
      if (this.modalRefAddgroup) {
        this.modalRefAddgroup.hide();
        this.modalRefAddgroup = null;
      } else if (this.modalRef) {
        this.modalRef.hide();
      }
    });
    this.page = 1;
    this.paginationFlag = true;
  }

  onCheckboxChange(option, event, data) {
    if (event.target.checked) {
      option.checked = true
      this.addusers.push(option.userId);
    } else {
      option.checked = false
      for (var i = 0; i < this.addusers.length; i++) {
        if (!option.checked && this.addusers[i] == option.userId) {
          this.addusers.splice(i, 1);
        }
      }
    }
  }


  exitGroup(name) {
    var user = localStorage.getItem('userId')
    if (name == 'user') {
      this.leftUser(user)
    } else if (name = 'owner') {
      this.deleteGroup()
    }
  }

  // Delete group by owner

  deleteGroup() {
    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: "btn btn-success",
        cancelButton: "btn btn-danger",
      },
      buttonsStyling: false,
    });
    swalWithBootstrapButtons.fire({
      title: "Are you sure you want to delete this group?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {

        let url = "message/group/delete/" + this.SelectedContact.groupId;
        this._util.startLoader('msgspinner');
        this._api.messagePageService('DELETE', url).subscribe(res => {
          this._util.stopLoader('msgspinner');
          if (res.code == "00000") {

            const idToRemove = this.SelectedContact.groupId;
            const newTempContactList = [...this.tempContactList.filter(item => item.groupId !== idToRemove)];
            this.tempContactList = newTempContactList

            const newTempContactList1 = [...this.contactList.filter(item => item.groupId !== idToRemove)];
            this.contactList = newTempContactList1

            const newTempContactList2 = [...this.remainingContact.filter(item => item.groupId !== idToRemove)];
            this.remainingContact = newTempContactList2

            this.messageStatus = this.selectMessage.EMPTY
            Swal.fire({
              position: 'center',
              icon: 'success',
              title: "Group deleted successfully",
              showConfirmButton: false,
              timer: 3000
            });

            this.nodatafound = true;
            // const queryParams: Params = { groupId: '', groupType: '', refererId: '' };
            this.deleteAPIfunction();
            // this.SelectedContact = {};
            this.closeNewContactModal();
            // this._location.replaceState('/message');
            // this.route.routeReuseStrategy.shouldReuseRoute = () => false;
            // this.redirect = false;
            this.route.navigate(['/message'], {
              queryParams: { type: this.selectedMenu }
            });
          }
          else if (res.code == "99999") {
            Swal.fire({
              position: 'center',
              icon: 'error',
              title: "Oops...",
              text: 'Group could not be deleted now. Please try again later later.',
              showConfirmButton: false,
              timer: 4000
            })
          }
        })



      }
    })



  }



  // Add new user
  addUser() {
    var temp = [...new Set(this.addusers)];
    this.addusers = temp;
    let url = "message/group/contact/add";
    let contact = {
      contacts: this.addusers,
      groupId: this.SelectedContact.groupId,
    };
    if (temp.length != 0) {
      this._util.startLoader('msgspinner');
      this._api.messagePageService('POST', url, contact).subscribe(res => {
        this._util.stopLoader('msgspinner');
        if (res && res != null && res != undefined) {
          if (res.code == "00000") {
            if (this.modalRefAddgroup) {
              this.modalRefAddgroup.hide();
            }
            this.groupUserList = [...this.groupUserList, ...res.data.addedUsers]
            this.searchContent = ''
            this.modalRef.hide();
            this.addusers = []
            this.queryParamBasedMethod()
            Swal.fire({
              position: 'center',
              icon: 'success',
              title: 'Add user',
              text: "User added successfully",
              showConfirmButton: false,
              timer: 3000
            })
          }
        }
      }, err => {
        this._util.stopLoader();

      })
    } else {
      Swal.fire({
        position: 'center',
        icon: 'info',
        title: "Select user",
        text: 'Please select an user to add',
        showConfirmButton: false,
        timer: 3000
      })
    }

  }

  // Remove user from  group
  removeGroupMember(item) {
    var temp = []
    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: "btn btn-success",
        cancelButton: "btn btn-danger",
      },
      buttonsStyling: false,
    });
    swalWithBootstrapButtons.fire({
      title: 'Are you sure you want to remove this user?',
      // text: "You won't be able to remove this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        temp.push(item.userId)
        let url = "message/group/contact/remove";
        let contact = {
          contacts: temp,
          status: "REVOKED",
          groupId: this.SelectedContact.groupId,
        };
        this._util.startLoader('msgspinner');
        this._api.messagePageService('POST', url, contact).subscribe(res => {
          this._util.stopLoader('msgspinner');
          if (res && res != null && res != undefined) {
            if (res.code == "00000") {
              Swal.fire({
                position: 'center',
                icon: 'success',
                title: 'User removed successfully',
                showConfirmButton: false,
                timer: 1500
              })

              this.SelectedContact.nonSeenCount = res.status;

              this.closeNewContactModal()

              let groupId = this.aroute.snapshot.queryParamMap.get('groupId');
              let type = this.aroute.snapshot.queryParamMap.get('type');
              let refererId = this.aroute.snapshot.queryParamMap.get('refererId');
              let d = {
                groupId: this.SelectedContact.groupId,
                type: 'GROUP'
              };
              this.route.navigate(['/message'], { queryParams: d });
              //  this.closeAdduserModel()
            }

          }
        }, err => {
          this._util.stopLoader();

        })

      }
    })
  }

  leftUser(item) {
    var temp = []
    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: "btn btn-success",
        cancelButton: "btn btn-danger",
      },
      buttonsStyling: false,
    });
    swalWithBootstrapButtons.fire({
      title: 'Are you sure you want to exit?',
      // text: "You won't be able to remove this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        temp.push(item)
        let url = "message/group/contact/remove";
        let contact = {
          contacts: temp,
          status: "LEFT",
          groupId: this.SelectedContact.groupId,
        };
        this._util.startLoader('msgspinner');
        this._api.messagePageService('POST', url, contact).subscribe(res => {
          this._util.stopLoader('msgspinner');
          if (res && res != null && res != undefined) {
            if (res.code == "00000") {
              this.deleteAPIfunction();
              Swal.fire({
                position: 'center',
                icon: 'success',
                title: 'User left Successfully',
                showConfirmButton: false,
                timer: 1500
              })

              this.SelectedContact.status = "INACTIVE"
              // console.log("got printed here")
              // this.closeOpenedMsg();
              // this.route.navigate(['/message']);
              // this.SelectedContact = {};

              // const index = this.contactList.findIndex(x => x.groupId === this.SelectedContact.groupId);
              // if (index !== undefined) this.contactList.splice(index, 1);
              this.closeNewContactModal()
              // this.getAllContacts();
              // let groupId = this.aroute.snapshot.queryParamMap.get('groupId');
              // let type = this.aroute.snapshot.queryParamMap.get('type');
              // let refererId = this.aroute.snapshot.queryParamMap.get('refererId');
              // let d = {
              //   groupId: groupId,
              //   type: 'GROUP'
              // };
              // this.route.navigate(['/message'], { queryParams: d });
              // this.route.navigate(['/message']);
              // this.closeAdduserModel()
            }

          }
        }, err => {
          this._util.stopLoader();

        })

      }
    })
  }



  getSocketMessage(message: any) {
    ////"changes test" ,message)
    let _this = this;
    _this.constructMsg(_this, JSON.parse(message.body));
  }

  private constructMsg(that: any, msg: any) {
    this.duplicate(this.messageList);

    if (msg.enableDelete == true && msg.authorId == this.userId) {
      msg.enableDelete == true;
      msg.authorPhoto = this.loginUserData.photo;
      msg.authorName = this.loginUserData.firstName + " " + this.loginUserData.lastName;
    } else if (msg.enableDelete == true && msg.authorId != this.userId) {
      msg.enableDelete == false;
    }

    if (msg.action == "UPDATE") {
      this.messageList.forEach(element => {
        if (element.messageId == msg.messageId) {
          element.message = msg.message;
          if (msg.deleted) {
            element.message = "This message was deleted";
            element.attachments = null;
          }
          if (msg.edited) {
            msg.enableDelete == true;
            element.attachments = msg.attachments;
          }
        }
      });
    }





    this.messageList.push(msg);
    this.messageList = this.sortByMsg(this.messageList, 'lastMessageOn');


    if (this.chartDisc) {
      setTimeout(() => {
        if (this.chartDisc.nativeElement) {
          this.chartDisc.nativeElement.scroll({
            top: this.chartDisc.nativeElement.scrollHeight,
            left: 0,
            behavior: 'smooth'
          });
        }
      }, 200);
    }

    let update = [];
    update.push(msg);
    that.updateNonSeenMessage(update);

    this.contactList.forEach((e, index) => {
      if (this.messageList[0].recipientId == e.groupId) {
        e['nonSeenCount'] = 0;
        this.contactList = this.sortBy(this.contactList, 'lastMessageOn');
      }
    });


  }
  getNewContact(openModal?: any, template?: TemplateRef<any>) {
    let url = "message/new/contact/" + this.userId;
    this._util.startLoader('msgspinner');
    this._api.messagePageService('GET', url).subscribe(res => {
      this._util.stopLoader('msgspinner');
      if (res && res.length != 0) {
        res.forEach(element => {
          element.checked = false;
        });
        this.newContactList = res;
        this.tempNewContactList = res;
        if (openModal != undefined && openModal != null && openModal != '' && openModal && this.newContactList.length != 0) {
          this.modalRef = this.modalService.show(template, Object.assign({}, this.modalOptions));
          document.body.classList.add('body-no-scroll');
        } else {
          Swal.fire('', 'No user Connection', 'error');
        }
      } else {
        Swal.fire('', 'No user Connection', 'error');
      }
    })
  }

  @HostListener('keydown.enter', ['$event'])
  handleEnterKey(event: KeyboardEvent): void {
    // Alt+Enter → add the next line / numbered point instead of sending.
    // insertParagraph is what the native Enter does: inside an ordered list it
    // creates the next auto-numbered <li> (an empty item exits the list), and in
    // plain text it starts a new line for the next manually-typed point. Keeping
    // the list well-formed also lets Backspace remove the numbering natively.
    if (event.altKey) {
      event.preventDefault();
      document.execCommand('insertParagraph');
      return;
    }

    // Prevent default action (form submission or line break)
    event.preventDefault();

    // Check if Shift key is pressed for multiline text
    if (event.shiftKey) {
      // Allow line break when Shift+Enter is pressed
      // You'll need additional logic here to insert a line break in the editor
      return;
    }

    // If edit mode is active, call updateMsg()
    if (this.editMessage) {
      if (!this.editorDisabled && !this.sendMessageData['message'].errors?.summernoteEmpty) {
        this.updateMsg();
      }
    }
    // If image attachment edit mode is active
    else if (this.imageAttachmentEdit) {
      // Add logic for image attachment edit mode if needed
    }
    // Regular message sending mode
    else {
      // Files staged (paste / drop / paperclip) → the attachment send, which
      // carries the files AND any typed text. Plain sendMessage() ignores
      // uploadFiles, so Enter used to send the text alone and leave the staged
      // image behind (or do nothing at all when the text was empty).
      if (this.uploadFiles && this.uploadFiles.length > 0) {
        if (!this.editorDisabled) {
          this.sendMessageWithAttachment();
        }
      }
      else if (!this.editorDisabled && !this.sendMessageData['message'].errors?.summernoteEmpty) {
        this.sendMessage();
      }
    }
  }

  isButtonDisabled(messageControl: any): boolean {
    if (this.uploadFiles && this.uploadFiles.length > 0) {
      return false;
    }
    // Check the message content directly (like the bottom chat's isMessageBlank)
    // instead of trusting the validator's error object, which doesn't reliably
    // flag whitespace-only content in this layout.
    return this.editorDisabled
      || this.isBlankMessage(this.sendMessageData['message'])
      || (messageControl && messageControl.errors && messageControl.errors.summernoteEmpty);
  }


  // True when summernote content has no real text (ignores tags, <br>, &nbsp;,
  // and whitespace). Inline images count as content, so they are never blank.
  private isBlankMessage(html: any): boolean {
    if (html === undefined || html === null) { return true; }
    const raw = String(html);
    if (/<img\b/i.test(raw)) { return false; }
    const text = raw
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;|&#160;/gi, ' ')
      
      .replace(/\s+/g, '');
    return text.length === 0;
  }

  sendMessage() {

    // let type = this.aroute.snapshot.queryParamMap.get('type');
    // let groupId = this.aroute.snapshot.queryParamMap.get('groupId');
    // let refererId = this.aroute.snapshot.queryParamMap.get('refererId');


    let groupId = this.userRedirect.groupId
    let type = this.userRedirect.type
    let refererId = this.userRedirect.refererId
    this.searchData = {};
    if (this.isBlankMessage(this.sendMessageData['message'])) {
      return;
    }

    // Trim only the leading/trailing empty space (spaces, &nbsp;, blank lines) —
    // the body and its formatting (lists etc.) are kept intact.
    if (typeof this.sendMessageData['message'] === 'string') {
      this.sendMessageData['message'] = trimMessageHtml(this.sendMessageData['message']);
    }

    if (groupId == null && groupId == undefined && type == "NETWORK" || groupId == null && groupId == undefined && type == "TEAM") {
      groupId = refererId;
    }


    this.sendMessageData['authorId'] = this.userId;
    this.sendMessageData['recipientId'] = groupId;
    this.sendMessageData['userType'] = type.toUpperCase();
    this.sendMessageData['messageType'] = type.toUpperCase();
    this.sendMessageData['enableDelete'] = true;
    this.sendMessageData['refererId'] = refererId;



    const url = 'message/send';
    //this.searchAfterKeys=null
    if (!this.editorDisabled) {
      this.editorDisabled = true;
      this._util.startLoader('msgspinner');
      this._api.messagePageService('POST', url, this.sendMessageData).subscribe(res => {
        this._util.stopLoader('msgspinner');
        this.editorDisabled = false;
        if (res) {
          if (res.code === "00000") {
            this.sendMsgres.recipientId = this.sendMessageData.recipientId;
            if (this.userRedirect.groupId == null && this.userRedirect.groupId == undefined && (type == "BUSINESS" || type == "COMMUNITY" || type == "SUPPORT")) {
              this.userRedirect.groupId = res.recipientId;
              this.SelectedContact.groupId = res.recipientId;
              this.socketTopicmessageMSG1 = this._socket.onMessage('/topic/message/' + res.recipientId).subscribe(res => {


                ////this.SelectedContact.status)


                if (type == "BUSINESS" || type == "COMMUNITY") {


                  if (res.eventType == "ACTION") {

                    const timezoneDatePipe = new TimezoneDatePipe();
                    const dates = res.sentDate;
                    const latest_date = timezoneDatePipe.transform(dates, 'custom');

                    var tempMsg: any = [];
                    var secondTemMsg: any = [];
                    if (res.actionType == "ADDED_TO_NETWORK") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " joined " + " on " + latest_date;
                    }
                    else if (res.actionType == "REMOVED_FROM_NETWORK") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " removed " + secondTemMsg + " on " + latest_date;
                    } else if (res.actionType == "LEFT_NETWORK") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " left " + " on " + latest_date;
                    } else if (res.actionType == "ADDED_TO_TEAM") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " joined " + " on " + latest_date;
                    } else if (res.actionType == "REMOVED_FROM_TEAM") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " removed " + secondTemMsg + " on " + latest_date;
                    } else if (res.actionType == "LEFT_TEAM") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " left " + " on " + latest_date;
                    } else if (res.actionType == "ADDED_TO_MSG_GRP") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " added " + secondTemMsg + " on " + latest_date;
                    } else if (res.actionType == "REMOVED_FROM_MSG_GRP") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " removed " + secondTemMsg + " on " + latest_date;
                    } else if (res.actionType == "LEFT_FROM_MSG_GRP") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " left " + " on " + latest_date;
                    } else if (res.actionType == "ENABLED_RIGHTS") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " enabled chat rights for " + secondTemMsg + " on " + latest_date;
                    } else if (res.actionType == "DISABLED_RIGHTS") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " disabled  chat rights for " + secondTemMsg + " on " + latest_date;
                    }

                    else if (this.SelectedContact.groupType == "NETWORK" && res.actionType == "DEACTIVATED") {
                      res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                    }

                    else if (this.SelectedContact.groupType == "TEAM" && res.actionType == "DEACTIVATED") {
                      res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                    }

                    else if (this.SelectedContact.groupType == "BUSINESS" && res.actionType == "DEACTIVATED") {
                      res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                    }

                    else if (this.SelectedContact.groupType == "COMMUNITY" && res.actionType == "DEACTIVATED") {
                      res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                    }

                    else {
                      res.oldMessage = res.message + " on " + latest_date;

                    }
                  }

                  //" SEND MSG BUSINESS message", res)

                  this.constructMsg(this, res);
                  // }
                }
              });
            }
            // Plain-text copy of the sent HTML for the left-list preview (read before the reset below).
            const sentPreview = String(this.sendMessageData['message'] || '')
              .replace(/<[^>]*>/g, ' ')
              .replace(/&nbsp;|&#160;/gi, ' ')
              .replace(/\s+/g, ' ')
              .trim();
            this.sendMessageData = {};
            if (this.contactList != undefined && this.contactList != null) {
              this.contactList.forEach((e, index) => {
                if (this.sendMsgres.recipientId == e.groupId) {
                  if (res.sentDate == null) {
                    e['lastMessageOn'] = new Date();
                  } else {
                    e['lastMessageOn'] = res.sentDate;
                  }
                  if (sentPreview) { e['lastMessage'] = sentPreview; }
                  Object.assign(this.contactList[index], e);
                  // //this.contactList)
                  this.contactList = this.sortBy(this.contactList, 'lastMessageOn');
                }
              });
            }


            this.summernote.writeValue('');
            this.config = {
              focus: true,
              placeholder: 'Type a message...',
              toolbar: [],
              width: '100%',
              minHeight: 40,  // Use minHeight instead of height
              maxHeight: 120, // Set your maximum height directly in the config
              callbacks: {
                onInit: function () {
                  const $editor = $(this).next('.note-editor');
                  $editor.find('.note-editable').css('overflow-y', 'auto');
                },
                onKeydown: this.onComposeKeydown,
                onPaste: this.onComposePaste,
                onImageUpload: this.onComposeImageUpload
              }
            };
            this.currentIndex = 0;
            //this.messageList=[];
            this.sendMsgres.recipientId = res.recipientId;

            if (type == "USER") {
              res.enableDelete = true;
            }

            res.authorName = this.loginUserData.firstName + " " + this.loginUserData.lastName;
            res.authorPhoto = this.loginUserData.photo
            this.messageList.push(res);
            this.messageList = this.sortByMsg(this.messageList, 'lastMessageOn');


            //"messageList ", this.messageList);

            if (this.chartDisc && this.chartDisc.nativeElement) {
              this.chartDisc.nativeElement.scroll({
                top: this.chartDisc.nativeElement.scrollHeight,
                left: 0,
                behavior: 'smooth'
              });
            }

            this.intervalExistSeen = true;
            this.intervalNonSeenMessage = true;

            setTimeout(() => {
              const id = res.messageId;
              const element = document.getElementById(id);
              element.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
              if (this.scrollDir) {
                this.scrollDir.restore();
              }
            }, 400);



          }
        }
      }, err => {
        this.editorDisabled = false;
        this._util.stopLoader();
      });
    } else {
      this.summernote.writeValue('');
    }
  }


  onScrollDowngroupMemebers() {

  }


  delete(item) {
    // Confirm before deleting — same Swal style as the other delete popups on this
    // page (deleteGroup / removeGroupMember): bootstrap buttons, Yes/No, reversed.
    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: "btn btn-success",
        cancelButton: "btn btn-danger",
      },
      buttonsStyling: false,
    });
    swalWithBootstrapButtons.fire({
      title: 'Are you sure you want to delete this message?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        this.deleteMessageConfirmed(item);
      }
    });
  }

  deleteMessageConfirmed(item) {
    var url = "deleteMessages";
    var temp = [];
    temp.push(item);

    this._util.startLoader('msgspinner');
    this._api.messagePageService('POST', url, temp).subscribe(res => {
      this._util.stopLoader('msgspinner');
      this.messageList.forEach(element => {
        if (element.messageId == item.messageId) {
          element.message = "This message was deleted";
          element.deleted = true;
          element.enableDelete = false;
          element.attachments = null;
        }
      });
    }, err => {
      this._util.stopLoader();
    })
  }

  getImageSrc(item) {
    return item.url ? AppSettings.photoUrl + item.url : '';
  }

  // Exit edit mode (from the "Cancel" hint above the compose) without saving.
  cancelEdit() {
    this.editMessage = false;
    this.imageAttachmentEdit = false;
    this.editValue = null;
    this.sendMessageData['message'] = '';
  }

  edit(item) {
    this.uploadFiles = [];
    this.editMessage = true;
    this.editValue = item;
    this.editValueEditImage = item;
    this.sendMessageData['message'] = item.message;
    //item)
    if (item.attachements == true) {
      this.imageAttachmentEdit = true;
      item.attachements = false;
      this.EdituploadFiles = item.attachments;
      item.attachments.forEach(element => {
        element.edit = true;
        element.t = element.attachmentType;
        element.src = this.getImageSrc(element);
      });
      this.uploadFiles = item.attachments;
      this.editMessage = false;

    } else {
      // Activate the compose editor on Edit — the ⋮ menu click leaves focus on the
      // (closing) menu item, so without this the field shows the text but has no cursor.
      // setTimeout lets ngModel push the message into the editor first.
      setTimeout(() => this.focusComposeBox(), 50);
    }
  }

  updatemediaApi() {
    //"data1 as", this.editValue);
    //"update files", this.uploadFiles);
    //this.removeElement);

    this.editValueEditImage['enableDelete'] = true;
    this.editValueEditImage['attachements'] = true;

    this.editValueEditImage['message'] = this.sendMessageData['message'];
    this.FormData = new FormData();
    this.uploadFiles.forEach(element => {

      console.log("dataa addded", this.uploadFiles);

      if (element.file != undefined) {
        this.FormData.append("file", element.file);
      }
    });


    this.FormData.append("removedAttachments", new Blob([JSON.stringify(this.removeElement)], {
      type: "application/json"
    }));


    if (this.uploadFiles.length == 0 && this.editValue.attachments.length == 0) {
      this.editValueEditImage['attachment'] = false;
    }

    this.FormData.append('message', new Blob([JSON.stringify(this.editValueEditImage)],
      {
        type: "application/json"
      }));


    var url = "updateEditedMessage";
    this._util.startLoader('msgspinner');
    this._api.messagePageService('POST', url, this.FormData).subscribe(res => {
      this._util.stopLoader('msgspinner');
      this.editMessage = false;
      if (res.code == "00000") {
        this.uploadFiles = [];
        this.editValueEditImage = [];

        this.messageList.forEach(element => {

          if (element.messageId == this.editValue.messageId) {
            element.message = this.editValue.message;
            element.attachments = res.data.updatedMessage.attachments.filter(item => 'messageId' in item);
            this.summernote.writeValue('');
            this.editValue = "";
            element.enableDelete = true;
            this.uploadFiles = [];
            this.removeElement = [];
            this.imageAttachmentEdit = false;
            this.sendMessageData['message'] = "";
            this.attachment = false;
          }
        });
      }
    }, err => {
      this._util.stopLoader();

    })
  }

  updateMsg() {
    // Block saving an edit that is empty or whitespace-only.
    if (this.isBlankMessage(this.sendMessageData['message'])) {
      return;
    }
    var url = "updateEditedMessage";
    this._util.startLoader('msgspinner');
    this.editValue.message = this.sendMessageData['message'];
    this.FormData = new FormData();

    this.FormData.append('message', new Blob([JSON.stringify(this.editValue)],
      {
        type: "application/json"
      }));
    this._api.messagePageService('POST', url, this.FormData).subscribe(res => {
      this._util.stopLoader('msgspinner');
      this.editMessage = false;
      this.messageList.forEach(element => {
        if (element.messageId == this.editValue.messageId) {
          element.message = this.editValue.message;;
          this.summernote.writeValue('');
          this.editValue = "";
          element.enableDelete = true;
        }
      });
    }, err => {
      this._util.stopLoader();

    })
  }



  reloadcontact() {
    let userId = localStorage.getItem('userId');
    let url = "message/contact/" + userId;
    this.searchAfterKeys = null;
    this._api.messagePageService('GET', url).subscribe(res => {
      if (res) {
        // let contactList = res;
        let contactList = res.filter(element => element.groupName != undefined);
        if (contactList != undefined && contactList != null) {
          contactList.forEach(e => {
            let index = this.contactList.findIndex((item) => (item.userId === e.userId && item.groupId === e.groupId));
            if (index < 0) {
            } else {
              Object.assign(this.contactList[index], e);
            }

            if (e.lastMessageOn == null) {
              e.lastMessageOn = e.createdOn
            }
            this.contactList = this.sortBy(this.contactList, 'lastMessageOn');
          });
        }


        // let groupId = this.aroute.snapshot.queryParamMap.get('groupId');
        // let type = this.aroute.snapshot.queryParamMap.get('type');
        // let refererId = this.aroute.snapshot.queryParamMap.get('refererId');

        let groupId = this.userRedirect.groupId
        let type = this.userRedirect.type
        let refererId = this.userRedirect.refererId


        var newRefererId;
        if (refererId == null) {
          newRefererId = groupId;
        } else if (refererId != null) {
          newRefererId = refererId
        }

        if (type && type != null && type != '') {
          let contact = {
            userId: localStorage.getItem('userId'),
            groupType: type.toUpperCase(),
            groupId: groupId,
            refererId: newRefererId,
            messageType: type.toUpperCase(),
          };
          let url = "message/contact/exist";
          this.currentIndex = 0;
          this._api.messagePageService('POST', url, contact).subscribe(res => {
            if (res && res['code'] == '00000') {
              let d = res;
              this.sendMsgres.date = data;
              this.sendMessageData.message = ""
              this.editMessage = false;
              if (d['pageAdmin']) {
                this.route.navigate(['/message']);
                return;
              }
              if (d['exist'] || d['groupType'] == "SUPPORT" && res.groupId != undefined && res.groupId != null) {


                if (res.groupStatus == "INACTIVE") {
                  Swal.fire('', 'The ' + res.groupType.toLowerCase() + ' has been deleted', 'error');
                }

                if ((type === 'COMMUNITY' || type === 'BUSINESS') && (groupId == undefined || groupId == null || groupId == '')) {
                  let l = {
                    groupId: d['groupId'],
                    type: type,
                    refererId: refererId
                  };
                  this.route.navigate(['/message'], { queryParams: l });
                }
                let index = this.contactList.findIndex((item) => item.groupId === d.groupId);
                if (index < 0) {

                  this.contactList.push(d);
                  var data: any = []
                  data = this.duplicate(this.contactList);
                  this.contactList = data;
                }
                // this.messageList = (res['messageList'] == undefined || res['messageList']) == null ? [] : res['messageList'];
                this.messageList = []
                this.SelectedContact = res;
                this.syncSelectedOnlineFromList();
                if (res.status == "GROUP_INACTIVE") {
                  this.SelectedContact.status = "INACTIVE";
                  // console.log("got printed here")
                }

                this.sendMsgres.recipientId = d['groupId'];

                this.startFreshMessageLoad(d['groupId']);

              }

            }
          })

        }

      }
    })
  }

  sendMessageWithAttachment() {
    //  let groupId = this.aroute.snapshot.queryParamMap.get('groupId');
    // let type = this.aroute.snapshot.queryParamMap.get('type');
    // let refererId = this.aroute.snapshot.queryParamMap.get('refererId');
    // Belt-and-braces: the batch goes out as ONE request and AWS API Gateway
    // hard-rejects bodies over 10 MB (413) — never start a doomed upload.
    if (this.stagedTotalSize() > this.fileSize) {
      Swal.fire("", "Total attachments size should be lesser than 10 MB.", "info");
      return;
    }
    this.searchData = {};
    let groupId = this.userRedirect.groupId
    let type = this.userRedirect.type
    let refererId = this.userRedirect.refererId

    this.sendMessageData['authorId'] = this.userId;
    this.sendMessageData['recipientId'] = groupId;
    this.sendMessageData['userType'] = type.toUpperCase();
    this.sendMessageData['messageType'] = type.toUpperCase();
    this.sendMessageData['refererId'] = refererId;
    this.sendMessageData['enableDelete'] = true;
    this.FormData = new FormData();

    this.uploadFiles.forEach(element => {
      this.FormData.append('file', element.file);
    });


    this.FormData.append('message', new Blob([JSON.stringify(this.sendMessageData)],
      {
        type: "application/json"
      }));


    // Optimistic bubble with a live upload percentage (same template block the
    // old paste/drop auto-send used). One bubble stands in for the whole batch
    // and previews EVERY staged file, so none of them looks "not processing".
    const staged = this.uploadFiles || [];
    const placeholder: any = {
      messageId: 'pending-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8),
      authorId: this.userId,
      authorName: this.loginUserData ? (this.loginUserData.firstName + ' ' + this.loginUserData.lastName) : '',
      authorPhoto: this.loginUserData ? this.loginUserData.photo : null,
      recipientId: groupId,
      sentDate: new Date(),
      lastMessageOn: new Date(),
      message: '',
      uploadPending: true,
      uploadProgress: 0,
      // One entry per staged file — previews come from the staging FileReader.
      pendingItems: staged.map((f: any) => ({ kind: f.t, name: f.name, preview: f.t === 'image' ? f.src : null }))
    };
    this.messageList.push(placeholder);
    this.updateScoll();

    // The bubble carries the progress now — clear the WHOLE composer right away
    // (the payload above was already serialized). Anything typed or staged DURING
    // the upload belongs to the next message, so the success handler must never
    // touch composer state again — it used to re-clear uploadFiles/sendMessageData
    // on response, wiping files pasted mid-upload the moment the progress finished.
    const sentRecipientId = this.sendMessageData['recipientId'];
    const sentText = this.sendMessageData['message'];
    // Plain-text left-list preview (same stripping as sendMessage); attachment-only sends get a label.
    const sentPreview = String(sentText || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;|&#160;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim() || 'Sent an attachment';
    this.uploadFiles = [];
    this.attachment = false; // composer swaps back; the fresh editor starts from the cleared model
    this.sendMessageData = {};

    // On failure, put the staged files back so the user can retry (merged with
    // anything newly staged while the upload was in flight); the caption comes
    // back too unless newer text has been typed since.
    const restoreStaged = () => {
      this.removePendingMessage(placeholder);
      this.uploadFiles = this.duplicateimag(staged.concat(this.uploadFiles));
      this.attachment = this.uploadFiles.length > 0;
      if (sentText && this.isBlankMessage(this.sendMessageData['message'])) {
        this.sendMessageData['message'] = sentText;
      }
    };

    const url = 'message/send/upload';
    this.searchAfterKeys = null;
    this._api.messagePageUpload(url, this.FormData).subscribe((event: any) => {
      if (event && event.type === HttpEventType.UploadProgress && event.total) {
        // Cap at 99 — the last 1% is the server finishing; resolves on the response.
        placeholder.uploadProgress = Math.min(99, Math.round((event.loaded / event.total) * 100));
      } else if (event && event.type === HttpEventType.Response) {
        const res: any = event.body;
        if (res && res['code'] === '00000') {
          this.removePendingMessage(placeholder);
          // Composer state was already reset at send time — only the message
          // list and contact ordering are updated here.
          this.sendMsgres.recipientId = sentRecipientId;
          this.currentIndex = 0;


          if (this.contactList != undefined && this.contactList != null) {
            this.contactList.forEach((e, index) => {
              if (this.sendMsgres.recipientId == e.groupId) {
                e['lastMessageOn'] = res.sentDate;
                e['lastMessage'] = sentPreview;
                Object.assign(this.contactList[index], e);

                this.contactList = this.sortBy(this.contactList, 'lastMessageOn');
              }
            });
          }

          this.messageList.push(res);
          this.messageList = this.sortByMsg(this.messageList, 'lastMessageOn');

          setTimeout(() => {
            const id = res.messageId;
            const element = document.getElementById(id);
            element.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
            if (this.scrollDir) {
              this.scrollDir.restore();
            }
          }, 1000);
          if (type == 'BUSINESS' && (groupId == null || groupId == undefined || groupId == '')) {

            let d = {
              type: type,
              refererId: refererId,
              groupId: res['recipientId']
            };
            this.route.navigate(['/message'], { queryParams: d })
          }

          // No writeValue('') here — the editor was recreated empty at send time,
          // and clearing now would erase text typed during the upload.

          this.intervalExistSeen = true;
          this.intervalNonSeenMessage = true;
        } else {
          restoreStaged();
          Swal.fire('', 'File upload failed. Please try again.', 'error');
        }
      }
    }, () => {
      restoreStaged();
      Swal.fire('', 'File upload failed. Please try again.', 'error');
    });
  }

  // Double-click guard: a second click on the SAME row within 500ms re-runs the
  // whole open flow (socket teardown + two racing contact/exist calls whose
  // responses each wipe messageList) and leaves the conversation blank. Debounce
  // the re-click; a deliberate re-click after 500ms still reloads normally.
  private lastRowClickId: any = null;
  private lastRowClickAt = 0;

  redirectUrl(item: any) {

    console.log("item", item);

    const clickId = item && item['groupId'];
    const now = Date.now();
    if (clickId && clickId === this.lastRowClickId && now - this.lastRowClickAt < 500) {
      return;
    }
    this.lastRowClickId = clickId;
    this.lastRowClickAt = now;

    if (this.socketTopicmessageMSG1) {
      this.socketTopicmessageMSG1.unsubscribe();
    }
    if (this.socketTopicmessageMSG2) {
      this.socketTopicmessageMSG2.unsubscribe();
    }
    if (this.socketTopicmessageMSG3) {
      this.socketTopicmessageMSG3.unsubscribe();
    }
    if (this.socketTopicmessageMSG4) {
      this.socketTopicmessageMSG4.unsubscribe();
    }
    if (this.socketTopicmessageMSG5) {
      this.socketTopicmessageMSG5.unsubscribe();
    }
    if (this.socketTopicmessageMSG6) {
      this.socketTopicmessageMSG6.unsubscribe();
    }
    if (this.socketTopicmessageMSG7) {
      this.socketTopicmessageMSG7.unsubscribe();
    }

    if (this.socketTopicmessageMSG8) {
      this.socketTopicmessageMSG8.unsubscribe();

    }
    if (this.socketTopicmessageMSG9) {
      this.socketTopicmessageMSG9.unsubscribe();
    }
    if (this.socketTopicmessageMSG10) {
      this.socketTopicmessageMSG10.unsubscribe();
    }


    if (this.socketTopicmessageMSG11) {
      this.socketTopicmessageMSG11.unsubscribe();
    }

    if (this.socketTopicmessageMSG12) {
      this.socketTopicmessageMSG12.unsubscribe();
    }

    if (this.socketTopicmessageMSG12) {
      this.socketTopicmessageMSG12.unsubscribe();
    }
    if (this.socketTopicmessageMSG13) {
      this.socketTopicmessageMSG13.unsubscribe();
    }
    if (this.socketTopicmessageMSG14) {
      this.socketTopicmessageMSG14.unsubscribe();
    }

    if (this.socketTopicmessage14) {
      this.socketTopicmessage14.unsubscribe();
    }

    if (this.socketTopicmessageMSG15) {
      this.socketTopicmessageMSG15.unsubscribe();
    }


    let tempcontact: any = this.contactList;
    this.contactList = [];
    tempcontact.forEach((e, index) => {
      if (e.groupId != null) {
        Object.assign(tempcontact[index], e);
        this.contactList.push(e)
        this.contactList = this.sortBy(this.contactList, 'lastMessageOn');
      }
    });

    if (item['groupStatus'] == "INACTIVE") {
      Swal.fire('', 'The' + item['groupType'].toLowerCase() + 'as it has been deleted', 'error');
    } else if (item['groupId'] && item['groupId'] != null && item['groupId'] != "") {
      let m = {
        groupId: item['groupId'],
        type: item['groupType'].toUpperCase()
      };
      if (item['refererId'] && item['refererId'] != null && item['refererId'] != "")
        m['refererId'] = item['refererId'];
      m['dropdown'] = this.contactfil
      this.searchAfterKeys = null;
      this.userRedirect = m;
      // this.route.navigate(["/message"], { queryParams: m });
      this._util.startLoader('msgspinner');
      let urlTree;
      urlTree = this.route.createUrlTree([], {
        queryParams: m,
        queryParamsHandling: 'merge',
        preserveFragment: true
      });
      this._location.go(urlTree)
      this._util.stopLoader('msgspinner');

      setTimeout(() => {
        this.countAPI = item['nonSeenCount'];
        if (this.countAPI > 10) {
          this.showbottom = false;
        }
        item['nonSeenCount'] = "0";
        //this.queryParamBasedMethod();
        this.existsapicall(m);
      }, 300);
    }
  }


  // Generation token: only the LATEST contact/exist response may touch the
  // conversation state. A stale response (superseded by a newer row click)
  // used to clear messageList / overwrite SelectedContact for the wrong chat.
  private existsCallToken = 0;

  existsapicall(m) {
    const token = ++this.existsCallToken;
    let groupId = m.groupId;
    let type = m.type;
    let refererId = m.refererId;
    var newRefererId;
    if (refererId == null) {
      newRefererId = groupId;
    } else if (refererId != null) {
      newRefererId = refererId
    }
    this.imageAttachmentEdit = false;
    this.uploadFiles = [];
    // Close the attachment preview strip on chat switch — uploadFiles is cleared
    // above, but the strip's *ngIf runs on this flag, so it stayed open.
    this.attachment = false;
    if (type && type != null && type != '') {
      let contact = {
        userId: localStorage.getItem('userId'),
        groupType: type.toUpperCase(),
        groupId: groupId,
        refererId: newRefererId,
        messageType: type.toUpperCase(),
      };
      this._util.startLoader('msgspinner');
      let url = "message/contact/exist";
      this.currentIndex = 0;
      this._api.messagePageService('POST', url, contact).subscribe(res => {
        this._util.stopLoader('msgspinner');
        if (token !== this.existsCallToken) { return; } // superseded by a newer click
        this.Jobandcandidatedetails = res;
        this.nodatafound = false;

        this.sendMessageData.message = ""
        this.editMessage = false;
        if (res && res['code'] == '00000') {
          let d = res;

          this.sendMsgres.date = data;
          if (d['pageAdmin']) {
            this.route.navigate(['/message']);
            return;
          }
          if (d['exist'] || d['groupType'] == "SUPPORT" && res.groupId != undefined && res.groupId != null) {

            if (res.groupStatus == "INACTIVE") {
              // Swal.fire('', 'The ' +res.groupType.toLowerCase() +' has been deleted', 'error');
            }

            if ((type === 'COMMUNITY' || type === 'BUSINESS' || type === 'SUPPORT') && (groupId == undefined || groupId == null || groupId == '')) {
              let l = {
                groupId: d['groupId'],
                type: type,
                refererId: refererId
              };
              this.route.navigate(['/message'], { queryParams: l });
            }

            let index = this.contactList.findIndex((item) => item.groupId === d.groupId);
            if (index < 0) {

              this.contactList.push(d);
              var data: any = []
              data = this.duplicate(this.contactList);
              this.contactList = data;

            }
            if (this.SelectedContact?.groupId !== d.groupId) {
              // Conversation search is per-chat. closeConvoSearch() keeps the
              // keyword/results so reopening search in the SAME chat shows the
              // same search — but that means switching to a DIFFERENT chat
              // must clear it here, otherwise the previous chat's search box
              // and results keep showing over the newly opened conversation.
              this.showConvoSearch = false;
              this.convoSearchKeyword = '';
              this.convoSearchResults = [];
              ++this.convoSearchToken;
            }
            // this.messageList = (res['messageList'] == undefined || res['messageList']) == null ? [] : res['messageList'];
            this.messageList = [];
            this.SelectedContact = res;
            this.syncSelectedOnlineFromList();
            if (res.status == "GROUP_INACTIVE" && this.userRedirect.groupId === this.SelectedContact.groupId) {
              this.SelectedContact.status = "INACTIVE";
              // console.log("got printed here")
            }


            this.sendMsgres.recipientId = d['groupId'];
            if (type === "BUSINESS" || type === "COMMUNITY" || type === "SUPPORT") {
              this.SelectedContact.newstatus = this.SelectedContact.status;
              if (this.SelectedContact.status == "ACTIVE" && res.chatStatus == "ENABLED" || this.SelectedContact.status == "ACTIVE" && res.chatStatus == "NEW") {
                this.SelectedContact.status = "ACTIVE";
              } else if (this.SelectedContact.chatStatus == "DISABLED") {
                this.SelectedContact.status = "INACTIVE";
                // console.log("got printed here")
              }
            }

            this.messageStatus = MESSAGESTATUS.MESSAGE;
            this.startFreshMessageLoad(d['groupId']);

            if (type === 'USER') {

              this.socketTopicmessageMSG4 = this._socket.onMessage('/topic/message/' + groupId + '-' + localStorage.getItem('userId')).subscribe(res => {
                //////"USER", res)
                if (this.SelectedContact.groupId == res.authorId && type === 'USER') {
                  this.existsapicall(res)
                  this.constructMsg(this, res);
                }

              });

              this.socketTopicmessageMSG5 = this._socket.onMessage('/topic/message/' + localStorage.getItem('userId') + '-' + groupId).subscribe(res => {
                //////"USER2", res)
                if (this.SelectedContact.groupId == res.authorId && type === 'USER') {
                  //" existsapicall USER message1 ", res)
                  this.constructMsg(this, res);
                }
              });

              this.socketTopicmessageMSG12 = this._socket.onMessage('/topic/event/' + localStorage.getItem('userId')).subscribe(res_evn => {
                //////"res_evn " + res_evn);
                //   console.log("res_business oneevent 1" + JSON.stringify(res_evn))
                // console.log("printed 4")
                // console.log("printed 3")

                if (type === 'USER') {
                  this.SelectedContact.status = res_evn.status;
                }

                if (res_evn.status == 'GROUP_INACTIVE') {
                  this.SelectedContact.status = "INACTIVE"
                  // console.log("got printed here")
                }
                // this.SelectedContact = {};

                // this.messageStatus = MESSAGESTATUS.EMPTY;
                // const index = this.contactList.findIndex(x => x.groupId === res_evn.groupId);
                // if (index !== undefined) this.contactList.splice(index, 1);
              });

            } else if (type === 'GROUP' || type === 'NETWORK' || type === 'TEAM' || type === 'JOB' || type === 'CANDIDATE') {
              this.socketTopicmessageMSG2 = this._socket.onMessage('/topic/message/' + groupId).subscribe(res => {


                if (type === 'GROUP' || type === 'NETWORK' || type === 'TEAM' || type === 'JOB' || type === 'CANDIDATE') {

                  if (res.eventType == "ACTION") {

                    const timezoneDatePipe = new TimezoneDatePipe();
                    const dates = res.sentDate;
                    const latest_date = timezoneDatePipe.transform(dates, 'custom');

                    var tempMsg: any = [];
                    var secondTemMsg: any = [];
                    if (res.actionType == "ADDED_TO_NETWORK") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " joined " + " on " + latest_date;
                    }
                    else if (res.actionType == "REMOVED_FROM_NETWORK") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " removed " + secondTemMsg + " on " + latest_date;
                    } else if (res.actionType == "LEFT_NETWORK") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " left " + " on " + latest_date;
                    } else if (res.actionType == "ADDED_TO_TEAM") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " joined  " + " on " + latest_date;
                    } else if (res.actionType == "REMOVED_FROM_TEAM") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " removed " + secondTemMsg + " on " + latest_date;
                    } else if (res.actionType == "LEFT_TEAM") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " left " + " on " + latest_date;
                    } else if (res.actionType == "ADDED_TO_MSG_GRP") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " added " + secondTemMsg + " on " + latest_date;
                    } else if (res.actionType == "REMOVED_FROM_MSG_GRP") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " removed " + secondTemMsg + " on " + latest_date;
                    } else if (res.actionType == "LEFT_FROM_MSG_GRP") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " left " + " on " + latest_date;
                    } else if (res.actionType == "ENABLED_RIGHTS") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " enabled chat rights for " + secondTemMsg + " on " + latest_date;
                    } else if (res.actionType == "DISABLED_RIGHTS") {
                      if (res.authorId == this.userId) {
                        tempMsg = " You "
                      } else {
                        tempMsg = res.authorName;
                      }
                      if (res.recipientUserId == this.userId) {
                        secondTemMsg = " You "
                      } else {
                        secondTemMsg = res.recipientName;
                      }
                      res.oldMessage = tempMsg + " disabled  chat rights for " + secondTemMsg + " on " + latest_date;
                    }
                    else if (this.SelectedContact.groupType == "NETWORK" && res.actionType == "DEACTIVATED") {
                      res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                    }

                    else if (this.SelectedContact.groupType == "TEAM" && res.actionType == "DEACTIVATED") {
                      res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                    }

                    else if (this.SelectedContact.groupType == "BUSINESS" && res.actionType == "DEACTIVATED") {
                      res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                    }

                    else if (this.SelectedContact.groupType == "COMMUNITY" && res.actionType == "DEACTIVATED") {
                      res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                    }

                    else {
                      res.oldMessage = res.message + " on " + latest_date;

                    }
                  }


                  // this.contactList.forEach((e, index) => {

                  if (res.recipientId === this.SelectedContact.groupId) {
                    // Object.assign(this.contactList[index], e);
                    this.contactList = this.sortBy(this.contactList, 'lastMessageOn');

                    if (this.SelectedContact.status == 'ACTIVE') {
                      //" existsapicall GROUP message1 ", res)

                      this.constructMsg(this, res);


                    }

                  }
                  // });


                }
              });

              this.socketTopicmessageMSG14 = this._socket.onMessage('/topic/event/' + localStorage.getItem('userId')).subscribe(res_evn => {

                // console.log("printed 10")
                // console.log("res_business NETWORK " +  JSON.stringify(res_evn))


                if (type === 'GROUP' || type === 'NETWORK' || type === 'TEAM' || type === 'JOB' || type === 'CANDIDATE') {

                  //////"GROUP2  exists", res)
                  // this.SelectedContact.status = res_evn.status;
                  if ((res_evn.status == 'GROUP_INACTIVE' || res_evn.status === 'INACTIVE') && res_evn.groupId === this.SelectedContact.groupId) {
                    this.SelectedContact.status = "INACTIVE"
                    // console.log("got printed here")
                  }

                  this.tpoicData = res_evn;
                  //////'topic/event', res_evn);
                  // const index = this.contactList.findIndex(x => x.groupId === res_evn.groupId);
                  // if (index !== undefined) this.contactList.splice(index, 1);
                  //
                  // this.messageStatus = MESSAGESTATUS.EMPTY;
                  if (this.SelectedContact.groupId == res_evn.groupId) {
                    if (res_evn.groupStatus == 'REVOKED') {
                      // Swal.fire(res_evn.groupOwner + ' removed you from ' + res_evn.groupName);
                    } else if (res_evn.groupStatus == 'INACTIVE') {
                      Swal.fire(res_evn.groupName + ' group has been deleted');
                    }
                    // this.SelectedContact = {};
                  }

                }
              });

            } else if (type === 'BUSINESS' || type === 'COMMUNITY') {

              if (groupId != undefined && groupId != null && groupId != "") {
                this.socketTopicmessageMSG3 = this._socket.onMessage('/topic/message/' + groupId).subscribe(res => {
                  //"USER BUSINESS message", res)
                  if (type === 'BUSINESS' || type === 'COMMUNITY') {
                    console.log("Data Emitted here isss ", res, m);

                    if (res.recipientId == this.SelectedContact.groupId) {


                      if (res.eventType == "ACTION") {
                        const timezoneDatePipe = new TimezoneDatePipe();
                        const dates = res.sentDate;
                        const latest_date = timezoneDatePipe.transform(dates, 'custom');

                        var tempMsg: any = [];
                        var secondTemMsg: any = [];
                        if (res.actionType == "ADDED_TO_NETWORK") {
                          if (res.authorId == this.userId) {
                            tempMsg = " You "
                          } else {
                            tempMsg = res.authorName;
                          }
                          if (res.recipientUserId == this.userId) {
                            secondTemMsg = " You "
                          } else {
                            secondTemMsg = res.recipientName;
                          }
                          res.oldMessage = tempMsg + " joined " + " on " + latest_date;
                        }
                        else if (res.actionType == "REMOVED_FROM_NETWORK") {
                          if (res.authorId == this.userId) {
                            tempMsg = " You "
                          } else {
                            tempMsg = res.authorName;
                          }
                          if (res.recipientUserId == this.userId) {
                            secondTemMsg = " You "
                          } else {
                            secondTemMsg = res.recipientName;
                          }
                          res.oldMessage = tempMsg + " removed " + secondTemMsg + " on " + latest_date;
                        } else if (res.actionType == "LEFT_NETWORK") {
                          if (res.authorId == this.userId) {
                            tempMsg = " You "
                          } else {
                            tempMsg = res.authorName;
                          }
                          if (res.recipientUserId == this.userId) {
                            secondTemMsg = " You "
                          } else {
                            secondTemMsg = res.recipientName;
                          }
                          res.oldMessage = tempMsg + " left " + " on " + latest_date;
                        } else if (res.actionType == "ADDED_TO_TEAM") {
                          if (res.authorId == this.userId) {
                            tempMsg = " You "
                          } else {
                            tempMsg = res.authorName;
                          }
                          if (res.recipientUserId == this.userId) {
                            secondTemMsg = " You "
                          } else {
                            secondTemMsg = res.recipientName;
                          }
                          res.oldMessage = tempMsg + " joined  " + " on " + latest_date;
                        } else if (res.actionType == "REMOVED_FROM_TEAM") {
                          if (res.authorId == this.userId) {
                            tempMsg = " You "
                          } else {
                            tempMsg = res.authorName;
                          }
                          if (res.recipientUserId == this.userId) {
                            secondTemMsg = " You "
                          } else {
                            secondTemMsg = res.recipientName;
                          }
                          res.oldMessage = tempMsg + " removed " + secondTemMsg + " on " + latest_date;
                        } else if (res.actionType == "LEFT_TEAM") {
                          if (res.authorId == this.userId) {
                            tempMsg = " You "
                          } else {
                            tempMsg = res.authorName;
                          }
                          if (res.recipientUserId == this.userId) {
                            secondTemMsg = " You "
                          } else {
                            secondTemMsg = res.recipientName;
                          }
                          res.oldMessage = tempMsg + " left " + " on " + latest_date;
                        } else if (res.actionType == "ADDED_TO_MSG_GRP") {
                          if (res.authorId == this.userId) {
                            tempMsg = " You "
                          } else {
                            tempMsg = res.authorName;
                          }
                          if (res.recipientUserId == this.userId) {
                            secondTemMsg = " You "
                          } else {
                            secondTemMsg = res.recipientName;
                          }
                          res.oldMessage = tempMsg + " added " + secondTemMsg + " on " + latest_date;
                        } else if (res.actionType == "REMOVED_FROM_MSG_GRP") {
                          if (res.authorId == this.userId) {
                            tempMsg = " You "
                          } else {
                            tempMsg = res.authorName;
                          }
                          if (res.recipientUserId == this.userId) {
                            secondTemMsg = " You "
                          } else {
                            secondTemMsg = res.recipientName;
                          }
                          res.oldMessage = tempMsg + " removed " + secondTemMsg + " on " + latest_date;
                        } else if (res.actionType == "LEFT_FROM_MSG_GRP") {
                          if (res.authorId == this.userId) {
                            tempMsg = " You "
                          } else {
                            tempMsg = res.authorName;
                          }
                          if (res.recipientUserId == this.userId) {
                            secondTemMsg = " You "
                          } else {
                            secondTemMsg = res.recipientName;
                          }
                          res.oldMessage = tempMsg + " left " + " on " + latest_date;
                        } else if (res.actionType == "ENABLED_RIGHTS") {
                          if (res.authorId == this.userId) {
                            tempMsg = " You "
                          } else {
                            tempMsg = res.authorName;
                          }
                          if (res.recipientUserId == this.userId) {
                            secondTemMsg = " You "
                          } else {
                            secondTemMsg = res.recipientName;
                          }
                          res.oldMessage = tempMsg + " enabled chat rights for " + secondTemMsg + " on " + latest_date;
                        } else if (res.actionType == "DISABLED_RIGHTS") {
                          if (res.authorId == this.userId) {
                            tempMsg = " You "
                          } else {
                            tempMsg = res.authorName;
                          }
                          if (res.recipientUserId == this.userId) {
                            secondTemMsg = " You "
                          } else {
                            secondTemMsg = res.recipientName;
                          }
                          res.oldMessage = tempMsg + " disabled  chat rights for " + secondTemMsg + " on " + latest_date;
                        }

                        else if (this.SelectedContact.groupType == "NETWORK" && res.actionType == "DEACTIVATED") {
                          res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                        }

                        else if (this.SelectedContact.groupType == "TEAM" && res.actionType == "DEACTIVATED") {
                          res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                        }

                        else if (this.SelectedContact.groupType == "BUSINESS" && res.actionType == "DEACTIVATED") {
                          res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                        }

                        else if (this.SelectedContact.groupType == "COMMUNITY" && res.actionType == "DEACTIVATED") {
                          res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                        }

                        else {
                          res.oldMessage = res.message + " on " + latest_date;

                        }
                      }

                      //" existsapicall BUSINESS message1 ", res)

                      this.constructMsg(this, res);

                    }

                    const messageContacts: Array<any> = res.messageContacts;

                    if (messageContacts && messageContacts.length > 0) {

                      messageContacts.forEach(cData => {
                        const userId = localStorage.getItem("userId");
                        if (cData.groupId === groupId && userId === cData.userId) {
                          this.SelectedContact.status = cData.chatStatus === "DISABLED" ? "INACTIVE" : cData.chatStatus;
                          console.log(this.SelectedContact);
                        }

                      });

                    }


                  }
                });
              } else {

                this.socketTopicmessageMSG6 = this._socket.onMessage('/topic/message/' + refererId).subscribe(res => {
                  //"USER BUSINESS Message2", res)
                  if (type === 'BUSINESS' || type === 'COMMUNITY') {

                    if (res.eventType == "ACTION") {
                      const timezoneDatePipe = new TimezoneDatePipe();
                      const dates = res.sentDate;
                      const latest_date = timezoneDatePipe.transform(dates, 'custom');

                      var tempMsg: any = [];
                      var secondTemMsg: any = [];
                      if (res.actionType == "ADDED_TO_NETWORK") {
                        if (res.authorId == this.userId) {
                          tempMsg = " You "
                        } else {
                          tempMsg = res.authorName;
                        }
                        if (res.recipientUserId == this.userId) {
                          secondTemMsg = " You "
                        } else {
                          secondTemMsg = res.recipientName;
                        }
                        res.oldMessage = tempMsg + " joined " + " on " + latest_date;
                      }
                      else if (res.actionType == "REMOVED_FROM_NETWORK") {
                        if (res.authorId == this.userId) {
                          tempMsg = " You "
                        } else {
                          tempMsg = res.authorName;
                        }
                        if (res.recipientUserId == this.userId) {
                          secondTemMsg = " You "
                        } else {
                          secondTemMsg = res.recipientName;
                        }
                        res.oldMessage = tempMsg + " removed " + secondTemMsg + " on " + latest_date;
                      } else if (res.actionType == "LEFT_NETWORK") {
                        if (res.authorId == this.userId) {
                          tempMsg = " You "
                        } else {
                          tempMsg = res.authorName;
                        }
                        if (res.recipientUserId == this.userId) {
                          secondTemMsg = " You "
                        } else {
                          secondTemMsg = res.recipientName;
                        }
                        res.oldMessage = tempMsg + " left" + secondTemMsg + " on " + latest_date;
                      } else if (res.actionType == "ADDED_TO_TEAM") {
                        if (res.authorId == this.userId) {
                          tempMsg = " You "
                        } else {
                          tempMsg = res.authorName;
                        }
                        if (res.recipientUserId == this.userId) {
                          secondTemMsg = " You "
                        } else {
                          secondTemMsg = res.recipientName;
                        }
                        res.oldMessage = tempMsg + " joined " + " on " + latest_date;
                      } else if (res.actionType == "REMOVED_FROM_TEAM") {
                        if (res.authorId == this.userId) {
                          tempMsg = " You "
                        } else {
                          tempMsg = res.authorName;
                        }
                        if (res.recipientUserId == this.userId) {
                          secondTemMsg = " You "
                        } else {
                          secondTemMsg = res.recipientName;
                        }
                        res.oldMessage = tempMsg + " removed " + secondTemMsg + " on " + latest_date;
                      } else if (res.actionType == "LEFT_TEAM") {
                        if (res.authorId == this.userId) {
                          tempMsg = " You "
                        } else {
                          tempMsg = res.authorName;
                        }
                        if (res.recipientUserId == this.userId) {
                          secondTemMsg = " You "
                        } else {
                          secondTemMsg = res.recipientName;
                        }
                        res.oldMessage = tempMsg + " left " + " on " + latest_date;
                      } else if (res.actionType == "ADDED_TO_MSG_GRP") {
                        if (res.authorId == this.userId) {
                          tempMsg = " You "
                        } else {
                          tempMsg = res.authorName;
                        }
                        if (res.recipientUserId == this.userId) {
                          secondTemMsg = " You "
                        } else {
                          secondTemMsg = res.recipientName;
                        }
                        res.oldMessage = tempMsg + " added " + secondTemMsg + " on " + latest_date;
                      } else if (res.actionType == "REMOVED_FROM_MSG_GRP") {
                        if (res.authorId == this.userId) {
                          tempMsg = " You "
                        } else {
                          tempMsg = res.authorName;
                        }
                        if (res.recipientUserId == this.userId) {
                          secondTemMsg = " You "
                        } else {
                          secondTemMsg = res.recipientName;
                        }
                        res.oldMessage = tempMsg + " removed " + secondTemMsg + " on " + latest_date;
                      } else if (res.actionType == "LEFT_FROM_MSG_GRP") {
                        if (res.authorId == this.userId) {
                          tempMsg = " You "
                        } else {
                          tempMsg = res.authorName;
                        }
                        if (res.recipientUserId == this.userId) {
                          secondTemMsg = " You "
                        } else {
                          secondTemMsg = res.recipientName;
                        }
                        res.oldMessage = tempMsg + " left " + " on " + latest_date;
                      } else if (res.actionType == "ENABLED_RIGHTS") {
                        if (res.authorId == this.userId) {
                          tempMsg = " You "
                        } else {
                          tempMsg = res.authorName;
                        }
                        if (res.recipientUserId == this.userId) {
                          secondTemMsg = " You "
                        } else {
                          secondTemMsg = res.recipientName;
                        }
                        res.oldMessage = tempMsg + " enabled chat rights for " + secondTemMsg + " on " + latest_date;
                      } else if (res.actionType == "DISABLED_RIGHTS") {
                        if (res.authorId == this.userId) {
                          tempMsg = " You "
                        } else {
                          tempMsg = res.authorName;
                        }
                        if (res.recipientUserId == this.userId) {
                          secondTemMsg = " You "
                        } else {
                          secondTemMsg = res.recipientName;
                        }
                        res.oldMessage = tempMsg + " disabled  chat rights for " + secondTemMsg + " on " + latest_date;
                      }

                      else if (this.SelectedContact.groupType == "NETWORK" && res.actionType == "DEACTIVATED") {
                        res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                      }

                      else if (this.SelectedContact.groupType == "TEAM" && res.actionType == "DEACTIVATED") {
                        res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                      }

                      else if (this.SelectedContact.groupType == "BUSINESS" && res.actionType == "DEACTIVATED") {
                        res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                      }

                      else if (this.SelectedContact.groupType == "COMMUNITY" && res.actionType == "DEACTIVATED") {
                        res.oldMessage = res.oldMessage = res.message + " on " + latest_date + " No further communication is possible.";
                      }

                      else {
                        res.oldMessage = res.message + " on " + latest_date;

                      }
                    }

                    if (res.recipientId == this.SelectedContact.groupId) {
                      //" existsapicall BUSINESS message1 recipientId ", res)

                      this.constructMsg(this, res);
                    }
                  }
                });
              }

              this.socketTopicmessageMSG11 = this._socket.onMessage('/topic/event/' + localStorage.getItem('userId')).subscribe(res_evn => {
                //"res_business evnt " + JSON.stringify(res_evn))
                //    console.log("res_business BUSINESS " + JSON.stringify(res_evn))
                // console.log("printed 12")

                if (type === 'BUSINESS' || type === 'COMMUNITY') {
                  if (res_evn.status == "ACTIVE") {
                    if (res_evn.chatStatus == "ENABLED") {
                      this.SelectedContact.status = "ACTIVE";
                    } else if (res_evn.chatStatus == "DISABLED") {
                      this.SelectedContact.status = "INACTIVE";
                      // console.log("got printed here")
                    }
                  } else if (res_evn.status == "INACTIVE" || res_evn.status == "GROUP_INACTIVE") {
                    this.SelectedContact.status = "INACTIVE";
                    // console.log("got printed here")
                  }
                }
                // const index = this.contactList.findIndex(x => x.groupId === res_evn.groupId);
                // if (index !== undefined) this.contactList.splice(index, 1);
              });

            }

          } else {
            this.messageList = [];
            this.SelectedContact = res;
            this.syncSelectedOnlineFromList();
            this.messageStatus = MESSAGESTATUS.MESSAGE;

            if ((type === 'COMMUNITY' || type === 'BUSINESS') && (groupId == undefined || groupId == null || groupId == '')) {
              let l = {
                groupId: d['groupId'],
                type: type,
                refererId: refererId
              };
              this.route.navigate(['/message'], { queryParams: l });
            }

          }
        }





      })


    }


  }

  singleRedirectUrl(item: any) {



    let tempcontact: any = this.contactList;
    this.contactList = [];
    tempcontact.forEach((e, index) => {
      if (e.groupId != null) {
        Object.assign(tempcontact[index], e);
        this.contactList.push(e)
        this.contactList = this.sortBy(this.contactList, 'lastMessageOn');
      }
    });

    let type = "USER"
    this.contactfil = type;
    let m = {
      groupId: item['groupId'],
      type: 'USER',
      userId: localStorage.getItem('userId'),
      refererId: null,
      dropdown: this.contactfil
    };

    this.userRedirect.groupId = item['groupId'];
    this.userRedirect.type = 'USER',
      this.searchAfterKeys = null;

    let urlTree;
    urlTree = this.route.createUrlTree([], {
      queryParams: m,
      queryParamsHandling: 'merge',
      preserveFragment: true
    });
    this._location.go(urlTree)
    this._util.stopLoader('msgspinner');

    this.countAPI = item['nonSeenCount'];
    if (this.countAPI > 10) {
      this.showbottom = false;
    }


    setTimeout(() => {
      // this.messageStatus = MESSAGESTATUS.MESSAGE;
      this.existsapicall(m)
      this.eventSelection('USER');
    }, 300);
    this.modalRef.hide();
  }

  addUserToGroup(event: any, userId: any, item) {
    if (event.target.checked) {
      item.checked = true
      this.groupData.contacts.push(item.groupId);
      this.groupData.name.push(item.groupName);

    } else {
      item.checked = false
      for (var i = 0; i < this.groupData.contacts.length; i++) {
        if (!item.checked && this.groupData.contacts[i] == item.groupId) {
          this.groupData.contacts.splice(i, 1);
          this.groupData.name.splice(i, 1);
          if (this.addusers.length != 0) {
            this.addusers.splice(i, 1);

          }
        }
      }
    }
  }

  // ── Group-chat creation wizard navigation ──
  nextGroupStep() {
    if (this.groupStep < 3) {
      this.groupStep++;
    }
  }

  prevGroupStep() {
    if (this.groupStep > 1) {
      this.groupStep--;
    }
  }

  // Remove a selected member (chip) — keeps groupData.contacts/name and the
  // list item's checked state in sync so re-opening step 1 stays consistent.
  removeGroupMemberChip(index: number) {
    const removedId = this.groupData.contacts[index];
    this.groupData.contacts.splice(index, 1);
    this.groupData.name.splice(index, 1);
    const item = (this.newContactList || []).find(c => c.groupId === removedId);
    if (item) {
      item.checked = false;
    }
  }

  // Close a slide-in panel with the slide-out animation, then run the real
  // close/reset logic once the animation has finished.
  closePanelWithAnim() {
    this.panelClosing = true;
    setTimeout(() => {
      this.panelClosing = false;
      this.closeNewContactModal();
    }, 400);
  }

  // Play the slide-out on the topmost slide panel, then run the real close.
  // Used by the legacy Add/Edit member-list panels (they lack the .msg-panel
  // wrapper the Private/Group panels animate through).
  private animateSlideClose(then: () => void) {
    const dialogs = document.querySelectorAll('.modal-dialog.msg-slide-panel');
    const dialog = dialogs[dialogs.length - 1] as HTMLElement;
    if (dialog) {
      dialog.classList.add('msg-slide-closing');
      setTimeout(then, 400);
    } else {
      then();
    }
  }

  // Animated close for the Edit member-list panels.
  closeSliderNC() {
    this.animateSlideClose(() => this.closeNewContactModal());
  }

  // Close any open per-message option dropdown when the message list scrolls.
  closeMessageDropdowns() {
    if (!this.messageDropdowns) {
      return;
    }
    this.messageDropdowns.forEach(d => {
      if (d.isOpen) {
        d.hide();
      }
    });
  }

  // ── Emoji picker (compose bar) ──
  toggleEmojiPicker() {
    this.showEmojiPicker = !this.showEmojiPicker;
  }

  // The picker runs in native mode (OS emoji font). Its data covers Unicode 14/15,
  // but older Windows builds have no glyph for the newest emoji (🫱 🫰 🫶 🫀 …) and
  // draw an empty box. Hide exactly those: render the character on a canvas and
  // compare it with a guaranteed-missing codepoint's tofu box — identical pixels
  // means this browser can't draw it. Per-user, so newer systems keep the full set.
  // Bound as [emojisToShowFilter]; receives the emoji's unified codepoints ("1FAF1").
  emojisToShowFilter = (unified: string): boolean => this.isEmojiRenderable(unified);

  private emojiSupportCache = new Map<string, boolean>();
  private emojiTofuFingerprint: string | null = null;
  private emojiProbeCanvas: HTMLCanvasElement | null = null;

  private isEmojiRenderable(unified: string): boolean {
    if (!unified || typeof unified !== 'string') { return true; }
    const cached = this.emojiSupportCache.get(unified);
    if (cached !== undefined) { return cached; }

    let supported = true;
    try {
      const char = unified.split('-')
        .map(part => String.fromCodePoint(parseInt(part, 16)))
        .join('');
      if (this.emojiTofuFingerprint === null) {
        // U+10FFFF is a permanent noncharacter — whatever it paints IS the tofu box.
        this.emojiTofuFingerprint = this.paintEmojiProbe(String.fromCodePoint(0x10FFFF));
      }
      const painted = this.paintEmojiProbe(char);
      supported = !!painted && painted !== this.emojiTofuFingerprint;
    } catch {
      supported = true; // canvas blocked / bad codepoint — don't hide anything
    }

    this.emojiSupportCache.set(unified, supported);
    return supported;
  }

  private paintEmojiProbe(char: string): string {
    if (!this.emojiProbeCanvas) {
      this.emojiProbeCanvas = document.createElement('canvas');
      this.emojiProbeCanvas.width = 24;
      this.emojiProbeCanvas.height = 24;
    }
    const ctx = this.emojiProbeCanvas.getContext('2d');
    if (!ctx) { return ''; }
    ctx.clearRect(0, 0, 24, 24);
    ctx.textBaseline = 'top';
    ctx.font = '20px "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif';
    ctx.fillText(char, 0, 0);
    return this.emojiProbeCanvas.toDataURL();
  }

  // Stop the picker from stealing focus (which would blur the editor and reset the
  // caret to the start) — EXCEPT the search box, which needs focus to type in.
  // With focus kept in the editor, its caret stays put and advances with each
  // insert, so consecutive picks land in order.
  onPickerMousedown(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target && !target.closest('.emoji-mart-search')) {
      event.preventDefault();
    }
  }

  // Expand the header search icon into the input and focus it.
  openConvoSearch() {
    this.showConvoSearch = true;
    setTimeout(() => {
      const el = document.querySelector('.chat-search-107 input') as HTMLElement;
      el?.focus();
    }, 0);
  }

  // Collapsing the box (blur / explicit close) only hides it — the keyword
  // and results are kept so reopening the search shows the same search
  // instead of forcing the user to retype it.
  closeConvoSearch() {
    this.showConvoSearch = false;
  }

  onConvoSearchInput() {
    this.convoSearchSubject.next(this.convoSearchKeyword);
  }

  private runConvoSearch() {
    this.convoSearchPage = 0;
    this.convoSearchHasMore = true;
    if (!this.convoSearchKeyword || this.convoSearchKeyword.trim() === '') {
      // Invalidate any in-flight search request so its late response can't
      // land after this clear and silently repopulate stale results — same
      // race condition already fixed once for getChatsByTab.
      ++this.convoSearchToken;
      this.convoSearchResults = [];
      return;
    }
    this.fetchConvoSearch(false);
  }

  onConvoSearchScroll() {
    if (this.convoSearchHasMore && !this.convoSearchLoadingMore
      && this.convoSearchKeyword && this.convoSearchKeyword.trim() !== '') {
      this.fetchConvoSearch(true);
    }
  }

  private fetchConvoSearch(loadMore: boolean) {
    if (!this.SelectedContact || !this.SelectedContact.groupId) { return; }
    // Same USER-type recipientId fallback used elsewhere in this file
    // (e.g. line ~1675): USER conversations address by refererId.
    const recipientId = this.SelectedContact.groupType === 'USER'
      ? (this.SelectedContact.refererId || this.SelectedContact.groupId)
      : this.SelectedContact.groupId;
    const requestPage = loadMore ? this.convoSearchPage + 1 : 0;
    const payload = {
      recipientId,
      messageType: this.SelectedContact.groupType,
      keyword: this.convoSearchKeyword,
      page: requestPage,
      pageSize: 20,
      userId: localStorage.getItem('userId')
    };

    const token = ++this.convoSearchToken;
    if (loadMore) {
      this.convoSearchLoadingMore = true;
    } else {
      this.convoSearchLoading = true;
    }

    this._api.create('message/searchInConversation', payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        if (token !== this.convoSearchToken) { return; }
        this.convoSearchLoading = false;
        this.convoSearchLoadingMore = false;

        if (res.code !== GigsumoConstants.SUCESSCODE) { return; }

        // Response array key unconfirmed against a real payload yet — same
        // defensive lookup used for getChatsByTab (message/getChatsByTab
        // returned a different key per tab: chats/contacts/groups).
        const metaKeys = new Set(['total', 'searchAfterKey', 'page', 'pageSize']);
        let results: any[] = [];
        if (res.data) {
          for (const key of Object.keys(res.data)) {
            if (metaKeys.has(key)) { continue; }
            const value = res.data[key];
            if (Array.isArray(value) && value.length > 0) {
              results = value;
              break;
            }
          }
        }

        this.convoSearchResults = loadMore ? [...this.convoSearchResults, ...results] : results;
        this.convoSearchPage = requestPage;
        this.convoSearchHasMore = results.length === payload.pageSize;
      }, () => {
        if (token !== this.convoSearchToken) { return; }
        this.convoSearchLoading = false;
        this.convoSearchLoadingMore = false;
      });
  }

  // Empty-state "Send First Message" CTA → focus the compose editor.
  focusComposeBox() {
    try {
      ($('#mainComposeEditor') as any).summernote('editor.focus');
    } catch (e) { }
  }

  insertEmoji(emoji: string) {
    try {
      // The editor keeps focus (picker mousedown is prevent-defaulted), so insert
      // INLINE at the live caret with execCommand — it stays on the same line and
      // advances the caret, so consecutive picks stay in order. (summernote's
      // insertText was pushing later emojis onto a new line.)
      const ok = document.execCommand('insertText', false, emoji);
      if (!ok) {
        ($('#mainComposeEditor') as any).summernote('editor.insertText', emoji);
      }
    } catch (e) {
      // Fallback: append to the model if the editor isn't ready.
      this.sendMessageData['message'] = (this.sendMessageData['message'] || '') + emoji;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClickForEmoji(event: MouseEvent) {
    if (!this.showEmojiPicker) {
      return;
    }
    const target = event.target as HTMLElement;
    // Keep open for clicks inside the emoji tool/popover; close otherwise.
    if (target && !target.closest('.msg-emoji-wrap')) {
      this.showEmojiPicker = false;
    }
  }

  // Which create-picker button was clicked — decides the minimum connections
  // required (group chat needs 2 members picked, so 2 connections) and which
  // "not enough connections" popup addcontact() shows.
  private pickerMode: 'private' | 'group' = 'private';

  openNewContactModal(template?: TemplateRef<any>) {
    this.SelectedContact.groupId = null;
    this.pickerMode = 'private';
    this.addcontact(true, template);
  }

  private searchSubjectBusiness = new Subject<string>();
  onscrollbusinessadminlist() {
    if (!this.stopScroll1) {
      this.contact.searchAfterKey = this.previousSearchAfterKey === null ? null
        : [this.previousSearchAfterKey];
      this.businessadminlist();
    }
  }
  onsearchbusinessadminlist() {
    if (this.skipNextSearch) {
      return
    }
    if (this.searchContent.trim() !== '') {
      this.contact.searchAfterKey = null;
      this.previousSearchAfterKey = null;
      this.businessadminlist();
    } else {

      this.groupBusinessListMembers = [];
      this.tempBusinessList = [];
      this.contact.searchAfterKey = null;
      this.previousSearchAfterKey = null;
      this.businessadminlist();
    }
    // this.stopScroll = false;
    this.stopScroll1 = false;
  }

  businessadminlist() {
    let url = "message/group/getGroupMembers";
    Object.assign(this.contact, {
      createdBy: this.SelectedContact.createdBy,
      groupType: this.SelectedContact.groupType,
      groupId: this.SelectedContact.groupId,
      limit: 10,
      searchContent: this.searchContent === '' ? null : this.searchContent,
    });

    // Don't reset the contacts array here
    // this.groupDataBusiness.contacts = [] <- Remove this line

    this._util.startLoader('msgspinner');
    this._api.messagePageService('POST', url, this.contact).subscribe(res => {
      this._util.stopLoader('msgspinner');
      if (res) {
        res.data.groupMembers.forEach(element => {
          element.userName = element.firstName + " " + element.lastName;

          // Check if user is already in the contacts array
          const isSelected = this.groupDataBusiness.contacts.includes(element.userId);

          if (element.profileStatus == "ENABLED" || element.profileStatus == "NEW") {
            // Set checked based on whether the user is in contacts array
            element.checked = isSelected || element.superAdmin === true;

            // Only add to contacts if not already there
            if (element.checked && !this.groupDataBusiness.contacts.includes(element.userId)) {
              this.groupDataBusiness.contacts.push(element.userId);
            }

            if (element.superAdmin == true) {
              element.disable = true;
            } else if (element.superAdmin == false) {
              element.disable = false;
            }
            if (this.SelectedContact.adminList[0] != localStorage.getItem('userId') && element.superAdmin == false) {
              element.disable = true;
            }
          }
          else if (element.profileStatus == "DISABLED") {
            element.checked = false;
            element.disable = true;
            if (this.SelectedContact.adminList[0] == localStorage.getItem('userId')) {
              element.disable = false;
            }
          }
        });

        // Rest of your existing code
        const key = res.data.searchAfterKey && res.data.searchAfterKey[0];
        if (this.previousSearchAfterKey === key || (res.data.groupMembers && res.data.groupMembers.length == 0)) {
          this.stopScroll1 = true
        }
        if (this.previousSearchAfterKey !== key) {
          if (this.searchContent != '') {
            this.groupBusinessListMembers = res.data.groupMembers;
            this.tempBusinessList = res.data.groupMembers;
          } else {
            this.groupBusinessListMembers = this.groupBusinessListMembers.concat(res.data.groupMembers);
            this.tempBusinessList = this.tempBusinessList.concat(res.data.groupMembers);
          }
        }
        this.previousSearchAfterKey = key;
      }
    }, err => {
      this._util.stopLoader();
    });
  }

  // Class property for flag
  private skipNextSearch = false;
  closeNewContactModal() {
    this.searchContent = '';
    this.skipNextSearch = true

    const groupType = this.aroute.snapshot.queryParamMap.get('type') || this.SelectedContact.groupType;
    // Emit empty values to subjects without triggering search
    Object.assign(this.contact, {
      groupId: this.SelectedContact.groupId,
      createdBy: this.SelectedContact.createdBy,
      groupType: groupType,
      limit: 10,
      searchContent: null
    });
    this.searchSubject.next('');
    this.searchSubjectBusiness.next('');
    setTimeout(() => {
      this.skipNextSearch = false
    }, 2000);
    // this.stopScroll = false;
    this.stopScroll1 = false;
    this.contact.searchAfterKey = null;
    this.previousSearchAfterKey = null;
    this.groupUserList = [];
    document.body.classList.remove('body-no-scroll');
    this.page = 1;

    this.modalService.hide(1);
    this.groupData = {
      groupName: null,
      contacts: [],
      name: []
    };
    this.groupStep = 1;
    this.paginationFlag = true;

  }

  openGroupContactModal(template?: TemplateRef<any>) {
    this.SelectedContact.groupId = null;
    this.groupStep = 1;
    this.pickerMode = 'group';
    this.addcontact(true, template);
  }

  createGroup() {
    var name = this.groupData.groupName;
    if (name != null) {
      var name = this.groupData.groupName.trim();
    }
    if (name == null || name == '') {
      var str = this.groupData.name.toString();
      this.groupData.groupName = str;
      this.groupData.isAutoGeneratedGroupName = true;

    } else {
      this.groupData.isAutoGeneratedGroupName = false;
    }


    if (this.groupData.contacts == null || this.groupData.contacts.length == 0
      || this.groupData.contacts.length == 1) {
      this.groupData.groupName = ""
      Swal.fire('', 'Please add one more user into the group', 'error');
      return;
    }
    let userId = localStorage.getItem("userId");
    this.groupData['createdBy'] = userId;
    this.groupData['groupType'] = 'GROUP';
    this.groupData.contacts.push(userId);
    const url = 'message/group';
    this._util.startLoader('msgspinner');
    this._api.messagePageService('POST', url, this.groupData).subscribe(res => {
      this._util.stopLoader('msgspinner');
      if (res && res != null && res != '' && res.code == "00000") {
        this.selectedMenu = "GROUP"
        this.nodatafound = false;

        let type = this.contactfil
        if (type == "" && type == undefined && type == null) {
          type = "All"
        }
        this.contactfil = type;
        let d = {
          groupId: res['groupId'],
          type: 'GROUP',
          dropdown: this.contactfil
        };
        this.route.navigate(['/message'], { queryParams: d });
        this.groupData.groupName = ""
        this.modalRef.hide();
        // this.stopScroll = false
        this.stopScroll1 = false
        this.groupData = {
          groupName: null,
          contacts: [],
          name: []
        };
        this.redirectUrl(res);
        this.eventSelection('GROUP');
      } else if (res.code == "99998") {
        Swal.fire({
          position: "center",
          icon: "error",
          title: "Group already exists!, Please try different name",
          showConfirmButton: false,
          timer: 2500,
        });
        this.groupData.groupName = ""
        this.groupData.contacts.pop();
      }
    }, err => {
      this._util.stopLoader();
    });
  }

  sortBy(list: Array<any>, prop: string) {
    if (list != null && list.length > 0) {
      return list.sort((a, b) => {
        if (a.lastMessageOn == "null") {
          a[prop] = new Date();
          return <any>new Date(b[prop]) - <any>new Date(a[prop]);
        } else {
          return <any>new Date(b[prop]) - <any>new Date(a[prop]);
        }
      });
    }
  }

  sortByMsg(list: Array<any>, prop: string) {
    if (list != null && list.length > 0) {
      return list.sort((a, b) => {
        return <any>new Date(a[prop]) - <any>new Date(b[prop]);
      });
    }
  }


  @ViewChild('scrollContainer', { static: true }) scrollContainer: ElementRef;

  onScroll() {
    console.log("Scrolled");
  }

  scrollReachedEnd() {
    console.log("SCrolled to end");
  }

  onScrollUp() {

    this.currentIndex = this.currentIndex + 1;
    //////this.currentIndex);
    this.messageListscroll(null, this.currentIndex, this.pagination);
    //   el.scrollIntoView(true);
    this.showbottom = true;

  }





  showImageAndVideoPreview(item: any, i: any) {
    document.getElementById('myModal').style.display = 'block';
    this.fancyList = item['attachments'];
    setTimeout(() => {
      this.showSlides(i + 1);
    }, 100);
  }

  // Pasted/inline images live in the message HTML ([innerHTML]), so they carry no
  // Angular click binding. Delegate from the bubble: if an <img> was clicked, open
  // the same preview modal with its full src (fullUrl bypasses the imgUrl+url join
  // the attachment slides use).
  onBubbleImageClick(event: MouseEvent) {
    const el = event.target as HTMLElement;
    if (el && el.tagName === 'IMG') {
      const src = (el as HTMLImageElement).src;
      if (!src) { return; }
      this.fancyList = [{ attachmentType: 'image', fullUrl: src }];
      document.getElementById('myModal').style.display = 'block';
      setTimeout(() => {
        this.showSlides(1);
      }, 100);
    }
  }
  closeFancyModal() {
    let slides = document.getElementsByClassName("mySlidess");
    for (let i = 0; i < slides.length; i++) {
      slides[i]['style'].display = "none";
    }
    document.getElementById("myModal").style.display = "none";
    this.slideIndex = 1;
  }

  plusSlides(n) {
    this.showSlides(this.slideIndex += n);
  }

  currentSlide(n) {
    this.showSlides(this.slideIndex = n);
  }
  showSlides(n) {
    this.slideIndex = n;
    let slides = document.getElementsByClassName("mySlidess");
    if (n > slides.length) { this.slideIndex = 1; }
    if (n < 1) { this.slideIndex = slides.length; }
    for (let i = 0; i < slides.length; i++) {
      slides[i]['style'].display = "none";
    }
    slides[this.slideIndex - 1]['style'].display = "block";
  }

  getSearchMessageDetails(item: any) {
    this.searchData = {};
    this.searchTerm = "";
    setTimeout(() => {
      const id = item['messageId'];
      const element = document.getElementById(id);
      element.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
      if (this.scrollDir) {
        this.scrollDir.restore();
      }
      element.style.backgroundColor = "var(--gs-purple-bg)";
      setTimeout(function () {
        element.style.backgroundColor = ""
      }, 2000);
    }, 100);
  }

  onSearchChange(evnet: any) { }

  closeOpenedMsg() {
    this.route.navigate(['/message']);
    this.SelectedContact = {};
    this.Jobandcandidatedetails = []
    this.nodatafound = true;
    if (this.messageDetailComponent) {
      this.messageDetailComponent.loadApicall = false;
      this.messageDetailComponent.resetConnectionList();
    }

  }


  fileUpload(event: any, template?: any, option?: any) {
    const pickedFiles: any[] = Array.from(event.target.files || []);
    if (!pickedFiles.length) { return; }

    // Validate EVERY picked file (the old check read files[0] only, so a
    // multi-select could smuggle an oversized file past the client) and keep a
    // running TOTAL including what's already staged — the whole batch goes out
    // as ONE multipart request, and AWS API Gateway hard-rejects request bodies
    // over 10 MB with a 413 before the backend ever sees them.
    let totalSize = this.stagedTotalSize();
    let rejectedType = false;
    const accepted: any[] = [];
    for (const file of pickedFiles) {
      // The `accept` attribute is only a dialog filter — "All Files" defeats it,
      // so the media-only rule has to be enforced here as well as on drop/paste.
      if (!this.isAllowedAttachment(file)) {
        rejectedType = true;
        continue;
      }
      if (file.size > this.fileSize) {
        Swal.fire("", "Please upload file lesser than 10 MB.", "info");
        continue;
      }
      if (totalSize + file.size > this.fileSize) {
        Swal.fire("", "Total attachments size should be lesser than 10 MB.", "info");
        break;
      }
      totalSize += file.size;
      accepted.push(file);
    }
    // Clear the input so re-picking the SAME rejected file still fires `change`
    // — without this the second attempt looks like a dead paperclip.
    event.target.value = '';
    if (rejectedType) {
      Swal.fire('', 'Only image, audio and video files can be attached.', 'info');
    }
    if (!accepted.length) { return; }

    if (this.uploadFiles.length == 0) {
      this.editValueEditImage['attachment'] = false;
    }


    if (option && option === 'update') {


    } else {
      this.FormData = new FormData();
    }
    accepted.forEach((file: any) => {
      const reader = new FileReader();
      reader.onload = (event1: any) => {
        const type = file.type;
        let type1 = 'image';
        if (type.includes('image')) {
          type1 = 'image';
        } else if (type.includes('video')) {
          type1 = 'video';
        } else if (type.includes('application')) {
          type1 = 'application';
        } else if (type.includes('audio')) {
          type1 = 'audio';
        } else {
          type1 = 'others';
        }
        const data = {
          t: type1,
          name: file.name,
          ft: type,
          src: event1.target.result,
          file: file
        };
        this.uploadFiles.push(data);

        if (this.editMessage) {
          this.imageAttachmentEdit = true;
          this.editMessage = false;
          this.editValueEditImage['attachment'] = true;
        }
        let tempdata = this.duplicateimag(this.uploadFiles);
        this.uploadFiles = tempdata;

      };
      reader.readAsDataURL(file);


      if (option == "update") {

      } else if (option == "fileupload") {

      }

      if (!this.attachment) {
        if (!this.imageAttachmentEdit) {
          this.attachment = true;


        }

      }
    });
  }

  duplicateimag(data) {
    var temp = [];
    var arr = data.filter(function (e) {
      // If it is not a duplicate, return true
      if (temp.indexOf(e.name) == -1) {
        temp.push(e.name);
        return true;
      }
      return false;
    });
    return arr;
  }
  toggleVideo(event?: any) {
    if (this.videoplayer && this.videoplayer.nativeElement) {
      this.videoplayer.nativeElement.play();
    }
  }

  removeAttachMent(item: any, i: any) {
    this.uploadFiles.splice(i, 1);
    // Only an edited message carries attachments here — editValueEditImage is []
    // otherwise, and .attachments.splice on undefined threw, leaving the empty
    // preview strip stuck open (attachment never reset below).
    if (Array.isArray(this.editValueEditImage?.attachments)) {
      this.editValueEditImage.attachments.splice(i, 1);
    }
    if (this.uploadFiles.length === 0) {
      this.attachment = false;
    }
  }

  removeAttachMentedit(item: any, i: any) {
    this.uploadFiles.splice(i, 1);
    if (Array.isArray(this.editValueEditImage?.attachments)) {
      this.editValueEditImage.attachments.splice(i, 1);
    }
    if (this.uploadFiles.length === 0) {
      this.attachment = false;
    }

    if (this.editValue.attachments != null && this.editValue.attachments.length != 0) {
      this.editValue.attachments.forEach(ele => {
        if (ele.status == "ACTIVE" && ele.attachmentId == item.attachmentId) {
          ele.deleted = true;
          this.removeElement.push(ele);
        }
      });
    }

  }


  onScrollUpGroupmembers() {

  }

  @ViewChild('scrollCont', { static: true }) scrolContent: any

  isBottomReached(el) {
    return (el.scrollHeight - Math.floor(el.scrollTop + 1)) === el.clientHeight;
  }
  onScrollDownNetworkMembers() {

    if (!this.stopScroll1) {
      this.contact.searchAfterKey = this.previousSearchAfterKey === null ? null
        : [this.previousSearchAfterKey];
      this.LoadmoreGroupMembers();
    }
  }



  pagecount(event) {
    this.configlist.currentPage = event;
    this.page = event;
    this.newpage = event;
  }



  listofUsers(templates?: TemplateRef<any>) {
    this.previousSearchAfterKey = null;
    this.groupUserList = [];
    this.SelectedContact.members;

    if (this.modalRef) {
      this.modalRef.hide();
    }

    if (this.modalRefAddgroup) {
      this.modalRefAddgroup.hide();
    }

    if (!this.modalRef) {
      this.groupUserList = this.SelectedContact.members;
      this.modalRef = this.modalService.show(templates, Object.assign({}, this.modalOptions));
      document.body.classList.add('body-no-scroll');
    }
  }

  dateDiff(date): boolean {
    date = new Date(date);
    let currD = new Date();
    currD.setHours(0, 0, 0, 0);

    return date > currD;
  }

}





